import { flushSync } from "react-dom";
import {
  clampPolylineGroupToStageBounds,
  clampRectangleTopLeftToStageBounds,
} from "./drawingHtmlUtils";
import {
  applyOffCanvasMainColumnAvoidanceX,
  clampOffCanvasTopLeftXAwayFromMainColumn,
  getOffCanvasMainColumnBounds,
  snapOffCanvasPointXOutOfMainColumn,
} from "./boundaryUtils";
import { generateMarkerId } from "./lineUtils";
import { storeMarkerInIndexedDB } from "./markerStorageUtils";
import { normalizeShapeType } from "../../../util/shapeTypeMapper";
import { processCompressedImage } from "./imageCompressionUtils";
import {
  getCanvasStageHeightPx,
  getMainCanvasMaxRadiusPx,
  getMainCanvasWidthPx,
} from "./mainCanvasLayout";
import { clearCrossCanvasCloneServerIdentity } from "./canvasCrossCanvasIdentityUtils";

const DISPLAY_MAX_DIM = 1024;
const DRAW_END_TRANSFORMER_BUFFER_PX = 10;
// Hard ceiling for all circle draw/transform flows.

// Global counter to ensure sequential execution of history saves
let historySaveCounter = 0;

/**
 * Convert HTMLImageElement to data URL so history/redo can load the image
 * when the original src was a blob URL (which may be revoked after undo).
 * @param {HTMLImageElement} imageElement
 * @param {number} maxDim - Max width/height to avoid huge data URLs (default 4096)
 * @returns {string|null} data URL or null
 */
function imageElementToDataUrl(imageElement, maxDim = 4096) {
  if (!imageElement || typeof imageElement.naturalWidth === "undefined") return null;
  try {
    const w = imageElement.naturalWidth;
    const h = imageElement.naturalHeight;
    if (!w || !h) return null;
    let cw = w;
    let ch = h;
    if (maxDim && (w > maxDim || h > maxDim)) {
      const r = Math.min(maxDim / w, maxDim / h);
      cw = Math.round(w * r);
      ch = Math.round(h * r);
    }
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imageElement, 0, 0, w, h, 0, 0, cw, ch);
    return canvas.toDataURL("image/png");
  } catch (e) {
    return null;
  }
}

/**
 * For large images, returns a downscaled display image + data URL so canvas state
 * never holds full-res (avoids 30MB+ images slowing every redraw).
 */
async function getDisplayImageForState(loadedImage, imageSrc) {
  if (!loadedImage || typeof loadedImage.naturalWidth !== "number") {
    return { image: loadedImage, src: imageSrc };
  }
  const w = loadedImage.naturalWidth || loadedImage.width;
  const h = loadedImage.naturalHeight || loadedImage.height;
  if (w <= DISPLAY_MAX_DIM && h <= DISPLAY_MAX_DIM) {
    return { image: loadedImage, src: imageSrc };
  }
  try {
    const processed = await processCompressedImage(loadedImage, imageSrc);
    return {
      image: processed.compressedImage,
      src: processed.compressedImage?.src ?? imageSrc,
    };
  } catch (e) {
    return { image: loadedImage, src: imageSrc };
  }
}

// Global Promise to ensure sequential execution of history saves
let lastHistorySavePromise = Promise.resolve();
let lastCloneImagePointerUpRef = {
  ts: 0,
  x: null,
  y: null,
  imageId: null,
};
let lastImagePointerUpRef = {
  ts: 0,
  x: null,
  y: null,
  imageId: null,
};
let lastCloneShapePointerUpRef = {
  ts: 0,
  x: null,
  y: null,
  action: null,
  stage: null,
  shapeId: null,
};
// Keep clone-draw shapes pinned to the canvas they started on.
let cloneDrawCanvasByShapeId = new Map();

const getDefaultStylePreset = (key) => {
  try {
    const sanitizePresetForKey = (preset, presetKey) => {
      if (!preset || typeof preset !== "object") return null;
      // Match UnifiedLineArrowEditor: runtime-only flags must never be restored
      // from defaults, otherwise creation can pick stale/transient styling data.
      if (presetKey === "unifiedLineArrowDefaultStylePreset") {
        const transientUnifiedKeys = new Set([
          "_isDragging",
          "_isInOffCanvasDuringDrag",
          "_previewTargetStage",
          "_useNodePositionForGroup",
          "_isCrossCanvasPreview",
          "isPreview",
          "hideBlueCircles",
          "forceShowBlueCircles",
          "forceHideBlueCircles",
          "selected",
          "isSelected",
          "isDraggable",
          "_deferInitialRender",
          "_presetBasePoints",
          "isNew",
          "isFirstTime",
        ]);
        return Object.entries(preset).reduce((acc, [k, v]) => {
          if (!transientUnifiedKeys.has(k)) {
            acc[k] = v;
          }
          return acc;
        }, {});
      }
      return preset;
    };
    const resolvePresetByStoredId = (presetKey) => {
      const idKey = `${presetKey}Id`;
      const storedId = localStorage.getItem(idKey);
      if (!storedId) return null;
      const sharedPresetsRaw = localStorage.getItem("sharedPresets");
      if (!sharedPresetsRaw) return null;
      const sharedPresets = JSON.parse(sharedPresetsRaw);
      if (!Array.isArray(sharedPresets)) return null;
      const matched = sharedPresets.find(
        (p) => p && String(p.id) === String(storedId),
      );
      return sanitizePresetForKey(matched, presetKey);
    };
    const oneShotKey = `${key}OneShot`;
    const oneShotRaw = localStorage.getItem(oneShotKey);
    if (oneShotRaw) {
      const parsedOneShot = JSON.parse(oneShotRaw);
      if (parsedOneShot && typeof parsedOneShot === "object") {
        return sanitizePresetForKey(parsedOneShot, key);
      }
    }
    // For unified line/arrow defaults, source of truth is the default preset id.
    // Resolve from sharedPresets so edits to the preset are reflected immediately.
    if (key === "unifiedLineArrowDefaultStylePreset") {
      const resolvedById = resolvePresetByStoredId(key);
      if (resolvedById) {
        return resolvedById;
      }
    }
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? sanitizePresetForKey(parsed, key)
      : null;
  } catch (_error) {
    return null;
  }
};

const consumeDefaultStylePreset = (key) => {
  try {
    localStorage.removeItem(`${key}OneShot`);
  } catch (_error) {
    // ignore storage errors
  }
};

const pickStyleFields = (source, fields) => {
  if (!source || typeof source !== "object") return {};
  const result = {};
  fields.forEach((field) => {
    if (source[field] !== undefined) {
      result[field] = source[field];
    }
  });
  return result;
};

const normalizeRelativePolylinePoints = (
  rawPoints,
  anchorX = undefined,
  anchorY = undefined,
) => {
  if (!Array.isArray(rawPoints) || rawPoints.length < 4) {
    return null;
  }
  const numericPoints = rawPoints.map((coord) => Number(coord) || 0);
  const firstX = numericPoints[0] || 0;
  const firstY = numericPoints[1] || 0;
  const hasAnchor =
    Number.isFinite(Number(anchorX)) && Number.isFinite(Number(anchorY));
  const resolvedAnchorX = hasAnchor ? Number(anchorX) : 0;
  const resolvedAnchorY = hasAnchor ? Number(anchorY) : 0;

  // Clone templates can carry either:
  // 1) absolute stage points, OR
  // 2) already-relative points (including negative offsets for bent lines).
  // Re-basing to first point breaks (2) and makes bent arrows jump far away.
  const firstMatchesAnchor =
    hasAnchor &&
    Math.abs(firstX - resolvedAnchorX) <= 2 &&
    Math.abs(firstY - resolvedAnchorY) <= 2;
  const pointMagnitude = Math.max(
    ...numericPoints.map((n) => Math.abs(Number(n) || 0)),
  );
  const looksAbsolute = firstMatchesAnchor || (hasAnchor && pointMagnitude > 400);

  if (looksAbsolute) {
    return numericPoints.map((coord, idx) =>
      idx % 2 === 0 ? coord - resolvedAnchorX : coord - resolvedAnchorY,
    );
  }
  return numericPoints;
};

const getElementAnchor = (element) => {
  if (!element || typeof element !== "object") {
    return { x: undefined, y: undefined };
  }
  const rawX =
    element.originalX ??
    element.x ??
    element.xPos ??
    element.xpos ??
    element.posX ??
    element.left;
  const rawY =
    element.originalY ??
    element.y ??
    element.yPos ??
    element.ypos ??
    element.posY ??
    element.top;
  const xNum = Number(rawX);
  const yNum = Number(rawY);
  return {
    x: Number.isFinite(xNum) ? xNum : undefined,
    y: Number.isFinite(yNum) ? yNum : undefined,
  };
};

/** Display type for selectedObject when the element lives in `lines` (unified renderer). */
const getUnifiedLineArrowSelectionType = (el) => {
  if (!el || typeof el !== "object") return "Line";
  const hs =
    el.hasStartArrow === true || el.pointerAtBeginning === true;
  const he =
    el.hasEndArrow === true ||
    el.pointerAtEnd === true ||
    el.pointerAtEnding === true;
  if (hs && he) return "DoubleSidedArrow";
  if (hs || he) return "Arrow";
  return "Line";
};

/** Preset sizes of 1–2px are valid for `applyDrawingRestrictions` but unusable for click placement. */
const RECT_CLICK_MIN_W = 20;
const RECT_CLICK_MIN_H = 20;
const RECT_CLICK_MAX_H = 600;

/** Stage pixels: movement below this counts as a click (avoids micro-drag turning into a 3×3 rect). */
const POINTER_CLICK_MOVE_THRESHOLD_PX = 12;

/** Coerce to click-to-create size when max(width,height) is at most this (noise / sub-pixel drag). */
const RECT_MICRO_DRAG_COERCE_MAX_SIDE_PX = 10;

/** Width/height for click-to-create rectangles; clamps to usable min/max (preset can store tiny values). */
const presetRectangleWidthHeight = (
  preset,
  fallbackWidth,
  fallbackHeight,
  maxWidthPx,
) => {
  const capW = Math.max(RECT_CLICK_MIN_W, maxWidthPx ?? getMainCanvasWidthPx());
  const wh = pickStyleFields(preset, ["width", "height"]);
  const w = Number(wh.width);
  const h = Number(wh.height);
  const fw = Number.isFinite(w) && w > 0 ? w : fallbackWidth;
  const fh = Number.isFinite(h) && h > 0 ? h : fallbackHeight;
  return {
    width: Math.max(RECT_CLICK_MIN_W, Math.min(capW, fw)),
    height: Math.max(RECT_CLICK_MIN_H, Math.min(RECT_CLICK_MAX_H, fh)),
  };
};

/**
 * Radius (and optional ellipse axes) for click-to-create circles from default preset.
 * Radii clamped to match drawing restrictions (5–250).
 */
const circleClickDimensionsFromPreset = (preset, fallbackRadius, maxRadiusPx) => {
  const capR =
    Number.isFinite(maxRadiusPx) && maxRadiusPx > 0
      ? maxRadiusPx
      : getMainCanvasMaxRadiusPx(undefined, getCanvasStageHeightPx());
  const dims = pickStyleFields(preset, ["radius", "radiusX", "radiusY"]);
  const r = Number(dims.radius);
  const rx = Number(dims.radiusX);
  const ry = Number(dims.radiusY);
  let defaultRadius = fallbackRadius;
  if (Number.isFinite(r) && r > 0) {
    defaultRadius = r;
  } else {
    const maxXY = Math.max(
      Number.isFinite(rx) && rx > 0 ? rx : 0,
      Number.isFinite(ry) && ry > 0 ? ry : 0,
    );
    if (maxXY > 0) defaultRadius = maxXY;
  }

  const clampR = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.max(5, Math.min(capR, n));
  };
  const extras = {};
  const cx = clampR(rx);
  const cy = clampR(ry);
  if (cx != null) extras.radiusX = cx;
  if (cy != null) extras.radiusY = cy;
  return { defaultRadius, extras };
};

/**
 * Handles pointer down events for drawing shapes on canvas
 * @param {Object} params - Parameters object
 * @param {string} params.action - Current action (SELECT, RECTANGLE, etc.)
 * @param {Object} params.stageRef - Ref to the Konva stage
 * @param {Function} params.generateMarkerId - Function to generate marker IDs
 * @param {Object} params.markerCount - Ref for marker count
 * @param {Object} params.currentShapeId - Ref for current shape ID
 * @param {Object} params.isPaining - Ref for painting state
 * @param {Function} params.setLocationMarkers - Setter for location markers
 * @param {Function} params.setShowTextBox - Setter for show text box flag
 * @param {string} params.pageId - Current page ID
 * @param {Function} params.setIsDrawingRect - Setter for drawing rectangle state
 * @param {Function} params.setNewRectStart - Setter for new rectangle start point
 * @param {Object} params.currentRectId - Ref for current rectangle ID
 * @param {Function} params.setRectangles - Setter for rectangles array
 * @param {Function} params.setIsDrawingCircle - Setter for drawing circle state
 * @param {Function} params.setNewCircleStart - Setter for new circle start point
 * @param {Object} params.currentCircleId - Ref for current circle ID
 * @param {Function} params.setCircles - Setter for circles array
 * @param {Function} params.setIsDrawingArrow - Setter for drawing arrow state
 * @param {Function} params.setArrowStartPoint - Setter for arrow start point
 * @param {Object} params.currentArrowId - Ref for current arrow ID
 * @param {Function} params.setArrows - Setter for arrows array
 * @param {Function} params.setIsDrawingDoubleArrow - Setter for drawing double arrow state
 * @param {Function} params.setDoubleArrowStartPoint - Setter for double arrow start point
 * @param {Object} params.currentDoubleArrowId - Ref for current double arrow ID
 * @param {Function} params.setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {boolean} params.isDrawingLine - Whether currently drawing a line
 * @param {Function} params.setIsDrawingLine - Setter for drawing line state
 * @param {Function} params.setLineStartPoint - Setter for line start point
 * @param {Object} params.currentLineId - Ref for current line ID
 * @param {Function} params.setLines - Setter for lines array
 * @param {string} params.fillColor - Fill color for shapes
 * @param {Function} params.applyDrawingRestrictions - Function to apply drawing restrictions
 * @param {Function} params.saveToHistory - Function to save to history
 * @param {string} params.ACTIONS_SELECT - ACTIONS.SELECT constant
 * @param {string} params.ACTIONS_RECTANGLE - ACTIONS.RECTANGLE constant
 * @param {string} params.ACTIONS_CIRCLE - ACTIONS.CIRCLE constant
 * @param {string} params.ACTIONS_ARROW - ACTIONS.ARROW constant
 * @param {string} params.ACTIONS_DOUBLE_SIDED_ARROW - ACTIONS.DOUBLE_SIDED_ARROW constant
 * @param {string} params.ACTIONS_LINE - ACTIONS.LINE constant
 */
export const createOnPointerDown = ({
  action,
  stageRef,
  markerCount,
  currentShapeId,
  isPaining,
  setLocationMarkers,
  setShowTextBox,
  // Optional: clear selection when starting a new draw
  // (prevents selected-layer render rules from hiding the freshly created element)
  setSelectedObject = null,
  setSelectedObjects = null,
  pageId,
  setIsDrawingRect,
  setNewRectStart,
  currentRectId,
  setRectangles,
  setIsDrawingCircle,
  setNewCircleStart,
  currentCircleId,
  setCircles,
  setIsDrawingArrow,
  setArrowStartPoint,
  currentArrowId,
  setArrows,
  setIsDrawingDoubleArrow,
  setDoubleArrowStartPoint,
  currentDoubleArrowId,
  setDoubleSidedArrows,
  isDrawingLine,
  setIsDrawingLine,
  setLineStartPoint,
  currentLineId,
  setLines,
  lines, // Current lines array (needed for name generation)
  arrows = null, // Current arrows array (needed for name generation for legacy arrows)
  doubleSidedArrows = null, // Current doubleSidedArrows array (needed for name generation)
  setIsDrawingText,
  setNewTextStart,
  currentTextId,
  setTexts,
  texts,
  fillColor,
  applyDrawingRestrictions,
  saveToHistory,
  ACTIONS_SELECT,
  ACTIONS_RECTANGLE,
  ACTIONS_CIRCLE,
  ACTIONS_ARROW,
  ACTIONS_DOUBLE_SIDED_ARROW,
  ACTIONS_LINE,
  ACTIONS_TEXT,
  ACTIONS_IMAGE,
  initialPointerPosition, // Ref to track initial pointer position for click detection
  lockedAction, // Track which action is locked (needed to skip temporary rectangle creation)
  cloneTemplate, // Template object for cloning shapes with exact properties
  cloneTemplates = [], // Array of template objects for cloning multiple elements
  isCloneMode = false, // Track if clone mode is active
  pendingImageData, // Pending image data for placement
  setIsDrawingImage, // Setter for image drawing state
  setImageStartPoint, // Setter for image start point
  setImages, // Setter for images array
  currentImageId, // Ref to track preview image ID
  imageConstraintRef, // Ref to track last used constraint for smooth transitions
  previewImageDataRef = null, // Ref to store preview image data for perfect alignment
  generateMarkerId, // Function to generate unique IDs
  // Off-canvas state setters (optional)
  offCanvasStageRef = null, // Ref to off-canvas stage to detect which canvas is active
  setOffCanvasRectangles = null,
  setOffCanvasCircles = null,
  setOffCanvasArrows = null,
  setOffCanvasDoubleSidedArrows = null,
  setOffCanvasLines = null,
  offCanvasLines = null, // Current off-canvas lines array (needed for name generation)
  setOffCanvasTexts = null,
  setOffCanvasImages = null,
  deferDrawingClampUntilPointerUp = false,
}) => {
  // Helper function to generate unique name by checking existing names
  // When cloning, we ALWAYS generate a new name (never reuse the original)
  const generateUniqueNameForClone = (baseName, existingNames) => {
    // Extract base name and number pattern (e.g., "Rectangle 2" -> "Rectangle", 2)
    const nameMatch = baseName.match(/^(.+?)\s+(\d+)$/);
    const base = nameMatch ? nameMatch[1] : baseName;

    // When cloning, we ALWAYS generate a new name by finding the highest existing number
    // Start from 0 to ensure we find the true maximum across all existing names
    let maxNumber = 0;
    existingNames.forEach((existingName) => {
      const existingMatch = existingName.match(new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+)$`));
      if (existingMatch) {
        const num = parseInt(existingMatch[1]);
        if (num > maxNumber) {
          maxNumber = num;
        }
      }
    });

    // Use the next number after the highest existing number
    // This ensures we never reuse an existing name, even if the base name doesn't exist in the set
    const nextNumber = maxNumber + 1;
    return `${base} ${nextNumber}`;
  };

  return function onPointerDown(e) {
    // Allow pointerDown to run for clone mode even when action is SELECT
    if (action === ACTIONS_SELECT && !isCloneMode) return;

    const stage = stageRef.current;
    const stagePixelW = stage?.width?.() ?? getMainCanvasWidthPx();
    const stagePixelH = stage?.height?.() ?? getCanvasStageHeightPx();
    const maxClickRadius = getMainCanvasMaxRadiusPx(undefined, stagePixelH);

    // Determine which canvas we're drawing on by comparing stage refs
    const isOffCanvas =
      offCanvasStageRef && stage === offCanvasStageRef.current;


    // Choose the appropriate state setters based on which canvas is active
    const activeSetRectangles =
      isOffCanvas && setOffCanvasRectangles
        ? setOffCanvasRectangles
        : setRectangles;
    const activeSetCircles =
      isOffCanvas && setOffCanvasCircles ? setOffCanvasCircles : setCircles;
    const activeSetArrows =
      isOffCanvas && setOffCanvasArrows ? setOffCanvasArrows : setArrows;
    const activeSetDoubleSidedArrows =
      isOffCanvas && setOffCanvasDoubleSidedArrows
        ? setOffCanvasDoubleSidedArrows
        : setDoubleSidedArrows;
    const activeSetLines =
      isOffCanvas && setOffCanvasLines ? setOffCanvasLines : setLines;
    const activeSetTexts =
      isOffCanvas && setOffCanvasTexts ? setOffCanvasTexts : setTexts;
    const activeSetImages =
      isOffCanvas && setOffCanvasImages ? setOffCanvasImages : setImages;

    // CRITICAL: Set pointer positions before getting them to ensure accurate coordinates
    if (e?.evt && stage) {
      stage.setPointersPositions(e.evt);
    }

    const pointerPos = stage.getPointerPosition();
    if (!pointerPos) {
      console.warn("🟣 [POINTER-DOWN] No pointer position available");
      return;
    }

    const { x, y } = pointerPos;

    const stageWidth = stage?.width?.() ?? 0;
    const stageHeight = stage?.height?.() ?? 0;
    const stageClampBufferPx =
      action === ACTIONS_SELECT ||
      action === ACTIONS_RECTANGLE ||
      action === ACTIONS_CIRCLE ||
      action === ACTIONS_ARROW ||
      action === ACTIONS_DOUBLE_SIDED_ARROW ||
      action === ACTIONS_LINE ||
      action === ACTIONS_TEXT ||
      action === ACTIONS_IMAGE ||
      lockedAction === ACTIONS_RECTANGLE ||
      lockedAction === ACTIONS_CIRCLE ||
      lockedAction === ACTIONS_ARROW ||
      lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
      lockedAction === ACTIONS_LINE ||
      lockedAction === ACTIONS_TEXT ||
      lockedAction === ACTIONS_IMAGE
        ? DRAW_END_TRANSFORMER_BUFFER_PX
        : 0;

    // Off-canvas: never start draw in the horizontal band under the main stage overlay
    let drawX = x;
    const drawY = y;
    if (!deferDrawingClampUntilPointerUp && isOffCanvas && stageWidth > 0) {
      drawX = snapOffCanvasPointXOutOfMainColumn(drawX, stageWidth);
    }
    const applyInitialDrawingRestrictions = (shapeType, props) =>
      deferDrawingClampUntilPointerUp
        ? { ...props }
        : applyDrawingRestrictions(shapeType, props);

    const rectangleDefaultPreset = getDefaultStylePreset(
      "rectangleDefaultStylePreset",
    );
    const circleDefaultPreset = getDefaultStylePreset(
      "circleDefaultStylePreset",
    );
    const unifiedLineArrowDefaultPreset = getDefaultStylePreset(
      "unifiedLineArrowDefaultStylePreset",
    );
    const debugUnifiedPresetCreate =
      typeof window !== "undefined" &&
      window.localStorage?.getItem("debugUnifiedPresetCreate") === "1";
    const clampRectTopLeftInsideStage = (x, y, width, height, strokeWidth = 2) => {
      const strokeHalf = Math.max(0, Number(strokeWidth) || 0) / 2;
      const safeWidth = Math.max(0, Number(width) || 0);
      const safeHeight = Math.max(0, Number(height) || 0);
      const maxInsetX = Math.max(0, (stageWidth - safeWidth - strokeHalf * 2) / 2);
      const maxInsetY = Math.max(0, (stageHeight - safeHeight - strokeHalf * 2) / 2);
      const edgeInsetX = Math.min(stageClampBufferPx, maxInsetX);
      const edgeInsetY = Math.min(stageClampBufferPx, maxInsetY);
      const minX = strokeHalf + edgeInsetX;
      const minY = strokeHalf + edgeInsetY;
      const maxX = Math.max(minX, stageWidth - safeWidth - strokeHalf - edgeInsetX);
      const maxY = Math.max(minY, stageHeight - safeHeight - strokeHalf - edgeInsetY);
      return {
        x: Math.max(minX, Math.min(Number(x) || 0, maxX)),
        y: Math.max(minY, Math.min(Number(y) || 0, maxY)),
      };
    };
    const clampCircleCenterInsideStage = (x, y, radius, strokeWidth = 2) => {
      const safeRadius = Math.max(0, Number(radius) || 0);
      const strokeHalf = Math.max(0, Number(strokeWidth) || 0) / 2;
      const maxInsetX = Math.max(0, (stageWidth - safeRadius * 2 - strokeHalf * 2) / 2);
      const maxInsetY = Math.max(0, (stageHeight - safeRadius * 2 - strokeHalf * 2) / 2);
      const edgeInsetX = Math.min(stageClampBufferPx, maxInsetX);
      const edgeInsetY = Math.min(stageClampBufferPx, maxInsetY);
      const minX = safeRadius + strokeHalf + edgeInsetX;
      const minY = safeRadius + strokeHalf + edgeInsetY;
      const maxX = Math.max(minX, stageWidth - safeRadius - strokeHalf - edgeInsetX);
      const maxY = Math.max(minY, stageHeight - safeRadius - strokeHalf - edgeInsetY);
      return {
        x: Math.max(minX, Math.min(Number(x) || 0, maxX)),
        y: Math.max(minY, Math.min(Number(y) || 0, maxY)),
      };
    };
    const clampImageCenterInsideStage = (x, y, width, height, border = 0) => {
      const halfW = Math.max(0, Number(width) || 0) / 2 + border;
      const halfH = Math.max(0, Number(height) || 0) / 2 + border;
      const maxInsetX = Math.max(0, (stageWidth - halfW * 2) / 2);
      const maxInsetY = Math.max(0, (stageHeight - halfH * 2) / 2);
      const edgeInsetX = Math.min(stageClampBufferPx, maxInsetX);
      const edgeInsetY = Math.min(stageClampBufferPx, maxInsetY);
      return {
        x: Math.max(
          halfW + edgeInsetX,
          Math.min(Number(x) || 0, stageWidth - halfW - edgeInsetX),
        ),
        y: Math.max(
          halfH + edgeInsetY,
          Math.min(Number(y) || 0, stageHeight - halfH - edgeInsetY),
        ),
      };
    };

    // Store initial position for click detection (important for multi-clone mode)
    if (initialPointerPosition) {
      initialPointerPosition.current = { x: drawX, y: drawY };
    }

    markerCount.current += 1;
    const id = generateMarkerId();
    if (isCloneMode && cloneTemplate && lockedAction) {
      cloneDrawCanvasByShapeId.set(id, isOffCanvas ? "off-canvas" : "main");
    }

    currentShapeId.current = id;
    isPaining.current = true;

    const marker = { id, x: drawX, y: drawY };
    setLocationMarkers((prev) => [...prev, marker]);
    storeMarkerInIndexedDB({ markerData: marker, pageId });
    setShowTextBox(false);

    // If user is starting a new draw, clear current selection first.
    // This avoids cases where "selected elements render on Layer 5" + "hide selected from Layer 1"
    // temporarily makes the newly created element appear to vanish while selected.
    const isStartingDrawAction =
      action === ACTIONS_RECTANGLE ||
      action === ACTIONS_CIRCLE ||
      action === ACTIONS_ARROW ||
      action === ACTIONS_DOUBLE_SIDED_ARROW ||
      action === ACTIONS_LINE ||
      action === ACTIONS_TEXT ||
      action === ACTIONS_IMAGE;
    if (!isCloneMode && isStartingDrawAction) {
      if (typeof setSelectedObject === "function") setSelectedObject(null);
      if (typeof setSelectedObjects === "function") setSelectedObjects([]);
    }

    const activeDrawingAction =
      action === ACTIONS_SELECT &&
      isCloneMode &&
      cloneTemplate &&
      (lockedAction === ACTIONS_ARROW ||
        lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
        lockedAction === ACTIONS_LINE)
        ? ACTIONS_LINE
        : action;

    if (
      isCloneMode &&
      cloneTemplate &&
      (lockedAction === ACTIONS_LINE ||
        lockedAction === ACTIONS_ARROW ||
        lockedAction === ACTIONS_DOUBLE_SIDED_ARROW)
    ) {
      console.log("[CLONE-DRAW][DOWN]", {
        action,
        lockedAction,
        activeDrawingAction,
        hasCloneTemplate: !!cloneTemplate,
        isOffCanvas:
          offCanvasStageRef && stageRef?.current === offCanvasStageRef.current,
      });
    }

    switch (activeDrawingAction) {
      case ACTIONS_RECTANGLE: {
        // CRITICAL: In clone mode, don't create rectangle here - it will be created in onPointerMove when dragging starts
        // This prevents creating a rectangle on click when user intends to drag-to-draw
        if (
          isCloneMode &&
          cloneTemplate &&
          lockedAction === ACTIONS_RECTANGLE
        ) {
          setIsDrawingRect(true);
          setNewRectStart({ x: drawX, y: drawY });
          currentRectId.current = id; // Store rectangle ID separately
          break; // Exit early, don't create rectangle
        }

        // Always create temporary rectangle for drawing (both locked and non-locked tools)
        // For locked tools: if it's a click, we'll clean it up in onPointerUp before creating the proper one
        // For locked tools: if it's a drag, the temporary rectangle will be used for drawing
        // CRITICAL: If we have cloneTemplate, we're in clone mode - don't create temporary rectangle
        // The clone will be created in onPointerMove (for drag) or onPointerUp (for click)
        if (cloneTemplate) {
          setIsDrawingRect(true);
          setNewRectStart({ x: drawX, y: drawY });
          currentRectId.current = id;
          break; // Exit early, don't create temporary rectangle
        }


        setIsDrawingRect(true);
        setNewRectStart({ x: drawX, y: drawY });
        currentRectId.current = id; // Store rectangle ID separately

        // Apply restrictions to initial rectangle position
        // Note: width: 0, height: 0 will be clamped to MIN_WIDTH: 10, MIN_HEIGHT: 10 by applyDrawingRestrictions
        // But we'll filter this out when saving to history for locked clicks
        const restrictedProps = applyInitialDrawingRestrictions("rectangle", {
          x: drawX,
          y: drawY,
          width: 0,
          height: 0,
        });

        activeSetRectangles((prev) => {
          // Generate unique name by checking existing names
          const existingNames = new Set(prev.map((r) => r.name));
          const baseName = cloneTemplate?.name || "Rectangle 1";
          const newName = generateUniqueNameForClone(baseName, existingNames);

          // Create a clean template without id and name to prevent conflicts
          const { id: templateId, name: templateName, ...cleanCloneTemplate } = cloneTemplate || {};

          // Use clone template properties if available, otherwise use defaults
          const baseProps = cloneTemplate
            ? {
              ...clearCrossCanvasCloneServerIdentity(cleanCloneTemplate),
              // Override position with click position
              x: restrictedProps.x,
              y: restrictedProps.y,
              // Reset size for drawing
              width: 0,
              height: 0,
            }
            : {
              fill: "#FFFFFF",
              stroke: "#FF0000",
              fillColor: "#FFFFFF",
              strokeColor: "#FF0000",
              maintainAspectRatio: false,
              strokeWidth: 2,
              ...pickStyleFields(rectangleDefaultPreset, [
                "fill",
                "fillColor",
                "fillOpacity",
                "stroke",
                "strokeColor",
                "strokeOpacity",
                "strokeWidth",
                "fontSize",
                "textColor",
                "fontStyle",
                "textDecoration",
                "align",
                "verticalAlign",
                "text",
                "maintainAspectRatio",
              ]),
            };

          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((rect) => rect.id === id);
          if (idExists) {
            console.error(
              "❌ [POINTER-DOWN] Duplicate rectangle ID detected, skipping",
              {
                duplicateId: id,
                existingIds: prev.map((r) => r.id).slice(-5),
              }
            );
            return prev; // Don't add if duplicate detected
          }


          // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
          // Also explicitly set id after spreading clean template to ensure it's always unique
          return [
            ...prev,
            {
              ...restrictedProps,
              ...baseProps,
              name: newName, // Set name AFTER spreading to prevent template name from overwriting
              id, // Always set a fresh unique ID
              // Ensure full opacity while drawing
              fillOpacity:
                cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
              strokeOpacity:
                cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
              opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
              isNew: true,
              isHighlighted: false,
            },
          ];
        });
        break;
      }

      // Handle clone mode when action is SELECT but lockedAction is the shape type
      case ACTIONS_SELECT: {
        // In clone mode, action is SELECT but lockedAction indicates the shape to draw
        if (
          isCloneMode &&
          lockedAction === ACTIONS_RECTANGLE &&
          cloneTemplate
        ) {
          // CRITICAL: Don't create rectangle on click - only store start position
          // We'll create it in onPointerMove when dragging actually starts
          // This prevents creating a clone on click when user intends to drag-to-draw
          setIsDrawingRect(true);
          setNewRectStart({ x: drawX, y: drawY });
          currentRectId.current = id; // Store rectangle ID separately
        } else if (
          isCloneMode &&
          lockedAction === ACTIONS_CIRCLE &&
          cloneTemplate
        ) {
          // Same for circles
          setIsDrawingCircle(true);
          setNewCircleStart({ x: drawX, y: drawY });
          currentCircleId.current = id;
        } else if (
          isCloneMode &&
          lockedAction === ACTIONS_IMAGE &&
          cloneTemplate
        ) {
          // Images in clone mode - set up for drag-to-create
          setIsDrawingImage(true);
          setImageStartPoint({ x: drawX, y: drawY });

          // Reset constraint tracking for new image
          if (imageConstraintRef) imageConstraintRef.current = null;
          if (previewImageDataRef) previewImageDataRef.current = null;
          // Create preview image from clone template
          const previewImageId = id;
          currentImageId.current = previewImageId;

          // Get image data from clone template
          const templateImage =
            cloneTemplate.image || cloneTemplate.src || null;
          const templateSrc =
            cloneTemplate.src || cloneTemplate.image?.src || null;
          // For rotated images, use originalWidth/originalHeight to preserve correct dimensions
          const templateWidth = cloneTemplate.originalWidth || cloneTemplate.width || 100;
          const templateHeight = cloneTemplate.originalHeight || cloneTemplate.height || 100;

          // DEBUG: Log cloning dimension calculation to track incorrect dimensions
          console.log("DEBUG: Image cloning dimension calculation", {
            cloneTemplate: cloneTemplate,
            originalWidth: cloneTemplate.originalWidth,
            originalHeight: cloneTemplate.originalHeight,
            templateWidth: cloneTemplate.width,
            templateHeight: cloneTemplate.height,
            finalTemplateWidth: templateWidth,
            finalTemplateHeight: templateHeight,
            rotation: cloneTemplate.rotation,
            maintainAspectRatio: cloneTemplate.maintainAspectRatio,
            scaleX: cloneTemplate.scaleX,
            scaleY: cloneTemplate.scaleY
          });

          // Create a clean template without id to prevent any ID conflicts
          const { id: templateId, ...cleanCloneTemplate } = cloneTemplate || {};

          // Calculate correct position based on anchor type
          // For center anchor (useTopLeftAnchor: false), drawX/drawY represent center point
          // For top-left anchor (useTopLeftAnchor: true), drawX/drawY represent top-left corner
          let cloneX, cloneY;
          if (cloneTemplate.useTopLeftAnchor === true) {
            // Top-left anchor: use drawX/drawY directly as top-left corner
            cloneX = drawX;
            cloneY = drawY;
          } else {
            // Center anchor: drawX/drawY represent center point, so position is already correct
            cloneX = drawX;
            cloneY = drawY;
          }

          // DEBUG: Log clone positioning to track where clones are getting cut
          console.log("DEBUG: Image clone positioning", {
            drawX: drawX,
            drawY: drawY,
            cloneX: cloneX,
            cloneY: cloneY,
            templateWidth: templateWidth,
            templateHeight: templateHeight,
            cloneTemplateX: cloneTemplate.x,
            cloneTemplateY: cloneTemplate.y,
            useTopLeftAnchor: cloneTemplate.useTopLeftAnchor,
            rotation: cloneTemplate.rotation,
            stageWidth: stage?.width?.(),
            stageHeight: stage?.height?.(),
            isOffCanvas: isOffCanvas
          });

          activeSetImages((prev) => {
            const imageNumber = prev.length + 1;
            // IMPORTANT: Explicitly set id after spreading clean template to ensure it's always unique
            return [
              ...prev,
              {
                image: templateImage, // Use image from clone template
                src: templateSrc,
                x: cloneX,
                y: cloneY,
                width: templateWidth,
                height: templateHeight,
                maintainAspectRatio:
                  cloneTemplate.maintainAspectRatio !== false,
                compress: cloneTemplate.compress !== false, // Default to true unless explicitly false
                imageNumber,
                name: `Picture ${imageNumber}`,
                isNew: true,
                opacity: 0.7, // Semi-transparent preview
                ...cleanCloneTemplate, // Include all other properties from template (without id)
                id: previewImageId, // Always set a fresh unique ID
              },
            ];
          });
        } else if (
          isCloneMode &&
          cloneTemplate &&
          (lockedAction === ACTIONS_LINE ||
            lockedAction === ACTIONS_ARROW ||
            lockedAction === ACTIONS_DOUBLE_SIDED_ARROW)
        ) {
          // Duplicate-to-selected-location uses ACTIONS_SELECT; for unified
          // line/arrow we still need to initialize draw state here so drag-to-create works.
          setIsDrawingLine(true);
          setLineStartPoint({ x: drawX, y: drawY });
          currentLineId.current = id;
        }
        break;
      }

      case ACTIONS_CIRCLE: {
        // Click-to-place clone mode (Duplicate to Selected Location):
        // cloneElementsAtPosition handles creation on click. If we create a temp
        // circle here, one click produces an extra duplicate.
        if (isCloneMode && cloneTemplate && lockedAction === ACTIONS_SELECT) {
          break;
        }

        // CRITICAL: In clone mode, don't create circle here - it will be created in onPointerMove when dragging starts
        // This prevents creating a circle on click when user intends to drag-to-draw
        // Also prevents duplicate circles: onPointerDown creates one, then onPointerMove creates another
        if (isCloneMode && cloneTemplate && lockedAction === ACTIONS_CIRCLE) {
          setIsDrawingCircle(true);
          setNewCircleStart({ x: drawX, y: drawY });
          currentCircleId.current = id; // Store circle ID separately
          break; // Exit early, don't create circle
        }

        setIsDrawingCircle(true);
        setNewCircleStart({ x: drawX, y: drawY });
        currentCircleId.current = id; // Store circle ID separately

        // Apply restrictions to initial circle position
        const restrictedProps = applyInitialDrawingRestrictions("circle", {
          x: drawX,
          y: drawY,
          radius: 0,
        });

        activeSetCircles((prev) => {
          // Generate unique name by checking existing names
          const existingNames = new Set(prev.map((c) => c.name));
          const baseName = cloneTemplate?.name || "Circle 1";
          const newName = generateUniqueNameForClone(baseName, existingNames);

          // Create a clean template without id to prevent any ID conflicts
          const { id: templateId, name: templateName, ...cleanCloneTemplate } = cloneTemplate || {};

          // Use clone template properties if available, otherwise use defaults
          const baseProps = cloneTemplate
            ? {
              ...clearCrossCanvasCloneServerIdentity(cleanCloneTemplate),
              // Override position with click position
              x: restrictedProps.x,
              y: restrictedProps.y,
              // Reset radius for drawing
              radius: 0,
            }
            : {
              fill: "#FFFFFF",
              stroke: "#FF0000",
              fillColor: "#FFFFFF",
              maintainAspectRatio: true,
              strokeColor: "#FF0000",
              strokeWidth: 2,
              ...pickStyleFields(circleDefaultPreset, [
                "fill",
                "fillColor",
                "fillOpacity",
                "stroke",
                "strokeColor",
                "strokeOpacity",
                "strokeWidth",
                "fontSize",
                "textColor",
                "fontStyle",
                "textDecoration",
                "align",
                "verticalAlign",
                "text",
                "maintainAspectRatio",
              ]),
            };

          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((circle) => circle.id === id);
          if (idExists) {
            console.error(
              "❌ [POINTER-DOWN] Duplicate circle ID detected, skipping",
              {
                duplicateId: id,
                existingIds: prev.map((c) => c.id).slice(-5),
              }
            );
            return prev; // Don't add if duplicate detected
          }

          // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
          // Also explicitly set id after spreading clean template to ensure it's always unique
          return [
            ...prev,
            {
              ...restrictedProps,
              ...baseProps,
              name: newName, // Set name AFTER spreading to prevent template name from overwriting
              id, // Always set a fresh unique ID
              // Ensure full opacity while drawing
              fillOpacity:
                cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
              strokeOpacity:
                cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
              opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
              isNew: true,
              isHighlighted: false,
            },
          ];
        });
        break;
      }

      case ACTIONS_ARROW: {
        // CRITICAL: Click-to-place clone mode (Duplicate to Selected Location) - do NOT create temporary line here.
        // Background handles clone via cloneElementsAtPosition; creating a line here causes an extra default-styled line.
        if (isCloneMode && cloneTemplate && lockedAction === ACTIONS_SELECT) {
          break;
        }
        // CRITICAL: Check where the original arrow is stored
        // If it's in arrows array (legacy), clone to arrows array
        // If it's in lines array (unified), clone to lines array
        const originalInArrows = cloneTemplate?.id && arrows
          ? arrows.some((a) => a.id === cloneTemplate.id)
          : false;

        // Check if it's a unified element (stored in lines array)
        // Check multiple property name variations for unified elements
        const hasStartArrow =
          cloneTemplate?.hasStartArrow === true ||
          cloneTemplate?.pointerAtBeginning === true ||
          Boolean(cloneTemplate?.hasStartArrow);
        const hasEndArrow =
          cloneTemplate?.hasEndArrow === true ||
          cloneTemplate?.pointerAtEnd === true ||
          cloneTemplate?.pointerAtEnding === true ||
          Boolean(cloneTemplate?.hasEndArrow);

        // Only treat as unified if it has arrow properties AND is NOT in arrows array
        const isForcedUnifiedCloneArrow =
          isCloneMode &&
          cloneTemplate &&
          (lockedAction === ACTIONS_ARROW ||
            lockedAction === ACTIONS_DOUBLE_SIDED_ARROW);
        const isUnifiedElement =
          (isCloneMode && cloneTemplate && (hasStartArrow || hasEndArrow) && !originalInArrows) ||
          isForcedUnifiedCloneArrow;


        // For unified elements in clone mode, create temporary element in lines array instead of arrows array
        // BUT: If original is in arrows array, use arrows array instead
        if (isUnifiedElement) {
          const newLineId = generateMarkerId();
          currentShapeId.current = newLineId;
          currentLineId.current = newLineId; // Use line ID for unified elements
          setIsDrawingLine(true); // Use line drawing state for unified elements
          setLineStartPoint({ x: drawX, y: drawY });

          // Apply restrictions to initial line position
          const restrictedProps = applyInitialDrawingRestrictions("line", {
            x: drawX,
            y: drawY,
            width: 0,
            height: 0,
          });

          // Read arrow properties from template (check both new and legacy)
          // CRITICAL: Exclude legacy pointer properties - only use hasStartArrow/hasEndArrow
          const tempHasStartArrow =
            cloneTemplate?.hasStartArrow === true ||
            cloneTemplate?.pointerAtBeginning === true;
          const tempHasEndArrow =
            cloneTemplate?.hasEndArrow === true ||
            cloneTemplate?.pointerAtEnd === true ||
            cloneTemplate?.pointerAtEnding === true;
          const templateAnchor = getElementAnchor(cloneTemplate);
          const normalizedTemplatePoints = normalizeRelativePolylinePoints(
            cloneTemplate?.points,
            templateAnchor.x,
            templateAnchor.y,
          );

          // Create initial unified element in lines array with same start and end point
          // Use activeSetLines (already defined at top of function) to handle off-canvas

          activeSetLines((prev) => {
            // Generate unique name by checking existing names
            // CRITICAL: For arrows, we need to check BOTH:
            // 1. lines array (unified elements with hasStartArrow/hasEndArrow)
            // 2. arrows array (legacy arrows)
            // 3. doubleSidedArrows array (legacy double-sided arrows)
            const isArrow = cloneTemplate && (
              (cloneTemplate.hasStartArrow === true || cloneTemplate.hasEndArrow === true) ||
              (cloneTemplate.pointerAtBeginning === true || cloneTemplate.pointerAtEnd === true || cloneTemplate.pointerAtEnding === true)
            );

            // Collect names from all sources
            const allArrowNames = [];

            // From lines array (prev) - unified arrows
            if (isArrow) {
              const unifiedArrows = prev.filter((l) =>
                (l.hasStartArrow === true || l.hasEndArrow === true) ||
                (l.pointerAtBeginning === true || l.pointerAtEnd === true || l.pointerAtEnding === true)
              );
              allArrowNames.push(...unifiedArrows.map((l) => l.name));
            } else {
              // For non-arrows (lines), just use prev
              allArrowNames.push(...prev.map((l) => l.name));
            }

            // From arrows array (legacy)
            if (isArrow && arrows) {
              allArrowNames.push(...arrows.map((a) => a.name).filter(Boolean));
            }

            // From doubleSidedArrows array (legacy)
            if (isArrow && doubleSidedArrows) {
              allArrowNames.push(...doubleSidedArrows.map((a) => a.name).filter(Boolean));
            }

            const existingNames = new Set(allArrowNames.filter(Boolean));
            const baseName = cloneTemplate?.name || "Stroke 1";


            const newName = generateUniqueNameForClone(baseName, existingNames);
            return [
              ...prev,
              {
                id: newLineId,
                name: newName,
                ...restrictedProps,
                points:
                  normalizedTemplatePoints && normalizedTemplatePoints.length >= 4
                    ? normalizedTemplatePoints
                    : [0, 0, 0, 0], // Use template geometry immediately when available
                fillColor:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                fill:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                strokeColor:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                strokeWidth: cloneTemplate?.strokeWidth || 2,
                stroke:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                // Preserve unified element properties from template
                // CRITICAL: Only set hasStartArrow and hasEndArrow
                // UnifiedLineArrowElement derives pointerAtBeginning/pointerAtEnding from these
                // Setting both causes duplicate arrow heads
                hasStartArrow: tempHasStartArrow,
                hasEndArrow: tempHasEndArrow,
                _presetBasePoints: normalizedTemplatePoints,
                // Prevent one-frame incorrect geometry flash before first pointer-move update.
                _deferInitialRender: true,
                isNew: true,
                createdOnCanvas: true, // Mark as user-created element
              },
            ];
          });
          break;
        }

        // Start arrow creation on mouse down (for regular arrows or non-clone mode)
        const newArrowId = generateMarkerId();
        currentShapeId.current = newArrowId;
        currentArrowId.current = newArrowId; // Store arrow ID separately
        setIsDrawingArrow(true);
        setArrowStartPoint({ x: drawX, y: drawY });

        // Apply restrictions to initial arrow position
        const restrictedProps = applyInitialDrawingRestrictions("arrow", {
          x: drawX,
          y: drawY,
          width: 0,
          height: 0,
        });

        // Create initial arrow with same start and end point
        activeSetArrows((prev) => {
          const arrowNumber = prev.length + 1;
          if (debugUnifiedPresetCreate) {
            console.log("[UnifiedPresetDebug][pointerDown][legacy-arrow-temp]", {
              unifiedLineArrowDefaultPreset,
              action: ACTIONS_ARROW,
              isCloneMode,
              hasCloneTemplate: Boolean(cloneTemplate),
              createdFromFillColor: fillColor || "black",
            });
          }
          return [
            ...prev,
            {
              id: newArrowId,
              name: `Arrow ${arrowNumber}`,
              ...restrictedProps,
              points: [0, 0, 0, 0], // Use relative coordinates (relative to arrow.x and arrow.y)
              fillColor,
              fill: fillColor,
              strokeColor: fillColor || "black",
              strokeWidth: 2,
              isNew: true,
              stroke: fillColor || "black",
            },
          ];
        });
        break;
      }

      case ACTIONS_DOUBLE_SIDED_ARROW: {
        // CRITICAL: Click-to-place clone mode (Duplicate to Selected Location) - do NOT create temporary line here.
        if (isCloneMode && cloneTemplate && lockedAction === ACTIONS_SELECT) {
          break;
        }
        // Check if this is a unified element (stored in lines array)
        // If clone mode is active and cloneTemplate is a unified element, skip creating temporary arrow
        // Check multiple property name variations for unified elements
        const hasStartArrow =
          cloneTemplate?.hasStartArrow === true ||
          cloneTemplate?.pointerAtBeginning === true ||
          Boolean(cloneTemplate?.hasStartArrow);
        const hasEndArrow =
          cloneTemplate?.hasEndArrow === true ||
          cloneTemplate?.pointerAtEnd === true ||
          cloneTemplate?.pointerAtEnding === true ||
          Boolean(cloneTemplate?.hasEndArrow);
        const isForcedUnifiedCloneDoubleArrow =
          isCloneMode &&
          cloneTemplate &&
          lockedAction === ACTIONS_DOUBLE_SIDED_ARROW;
        const isUnifiedElement =
          (isCloneMode && cloneTemplate && (hasStartArrow || hasEndArrow)) ||
          isForcedUnifiedCloneDoubleArrow;

        // For unified elements in clone mode, create temporary element in lines array instead of doubleSidedArrows array
        if (isUnifiedElement) {
          const newLineId = generateMarkerId();
          currentShapeId.current = newLineId;
          currentLineId.current = newLineId; // Use line ID for unified elements
          setIsDrawingLine(true); // Use line drawing state for unified elements
          setLineStartPoint({ x: drawX, y: drawY });

          // Apply restrictions to initial line position
          const restrictedProps = applyInitialDrawingRestrictions("line", {
            x: drawX,
            y: drawY,
            width: 0,
            height: 0,
          });

          // Create initial unified element in lines array with same start and end point
          // IMPORTANT: use activeSetLines so off-canvas clone-draw writes to off-canvas lines.
          activeSetLines((prev) => {
            // Generate unique name by checking existing names
            // CRITICAL: Use the actual lines array (passed to function) to ensure we see all existing names
            const currentLinesArray = lines || [];
            const existingNames = new Set([
              ...currentLinesArray.map((l) => l.name),
              ...prev.map((l) => l.name), // Also include prev in case of pending updates
            ]);
            const baseName = cloneTemplate?.name || "Stroke 1";
            const newName = cloneTemplate ? generateUniqueNameForClone(baseName, existingNames) : `Stroke ${prev.length + 1}`;
            const unifiedDefaults = pickStyleFields(unifiedLineArrowDefaultPreset, [
              "stroke",
              "strokeColor",
              "fill",
              "fillColor",
              "strokeWidth",
              "isDashed",
              "dash",
              "opacity",
              "hasStartArrow",
              "hasEndArrow",
            ]);
            const resolvedHasStartArrow =
              cloneTemplate?.hasStartArrow === true ||
              cloneTemplate?.pointerAtBeginning === true ||
              (unifiedDefaults.hasStartArrow !== undefined
                ? Boolean(unifiedDefaults.hasStartArrow)
                : true);
            const resolvedHasEndArrow =
              cloneTemplate?.hasEndArrow === true ||
              cloneTemplate?.pointerAtEnd === true ||
              cloneTemplate?.pointerAtEnding === true ||
              (unifiedDefaults.hasEndArrow !== undefined
                ? Boolean(unifiedDefaults.hasEndArrow)
                : true);
            const templateAnchor = getElementAnchor(cloneTemplate);
            const normalizedTemplatePoints = normalizeRelativePolylinePoints(
              cloneTemplate?.points,
              templateAnchor.x,
              templateAnchor.y,
            );
            return [
              ...prev,
              {
                id: newLineId,
                name: newName,
                ...restrictedProps,
                points:
                  normalizedTemplatePoints && normalizedTemplatePoints.length >= 4
                    ? normalizedTemplatePoints
                    : [0, 0, 0, 0], // Use template geometry immediately when available
                fillColor:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                fill:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                strokeColor:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                strokeWidth: cloneTemplate?.strokeWidth || 2,
                stroke:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  fillColor ||
                  "black",
                // Preserve unified element properties from template
                // CRITICAL: Only set hasStartArrow and hasEndArrow
                // UnifiedLineArrowElement derives pointerAtBeginning/pointerAtEnding from these
                // Setting both causes duplicate arrow heads
                hasStartArrow: cloneTemplate?.hasStartArrow || false,
                hasEndArrow: cloneTemplate?.hasEndArrow || false,
                _presetBasePoints: normalizedTemplatePoints,
                // Prevent one-frame incorrect geometry flash before first pointer-move update.
                _deferInitialRender: true,
                isNew: true,
                createdOnCanvas: true, // Mark as user-created element
              },
            ];
          });
          break;
        }

        // Start double-sided arrow creation on mouse down (for regular arrows or non-clone mode)
        const newArrowId = generateMarkerId();
        currentShapeId.current = newArrowId;
        currentDoubleArrowId.current = newArrowId; // Store double arrow ID separately
        setIsDrawingDoubleArrow(true);
        setDoubleArrowStartPoint({ x: drawX, y: drawY });

        // Apply restrictions to initial double-sided arrow position
        const restrictedProps = applyInitialDrawingRestrictions("arrow", {
          x: drawX,
          y: drawY,
          width: 0,
          height: 0,
        });

        // Create initial double-sided arrow with same start and end point
        setDoubleSidedArrows((prev) => {
          const doubleSidedArrowNumber = prev.length + 1;
          if (debugUnifiedPresetCreate) {
            console.log("[UnifiedPresetDebug][pointerDown][legacy-double-arrow-temp]", {
              unifiedLineArrowDefaultPreset,
              action: ACTIONS_DOUBLE_SIDED_ARROW,
              isCloneMode,
              hasCloneTemplate: Boolean(cloneTemplate),
              createdFromFillColor: fillColor || "black",
            });
          }
          return [
            ...prev,
            {
              id: newArrowId,
              name: `Double Sided Arrow ${doubleSidedArrowNumber}`,
              ...restrictedProps,
              points: [0, 0, 0, 0], // Use relative coordinates (relative to arrow.x and arrow.y)
              fillColor,
              fill: fillColor,
              strokeColor: fillColor || "black",
              strokeWidth: 2,
              isNew: true,
              stroke: fillColor || "black",
            },
          ];
        });
        break;
      }

      case ACTIONS_LINE: {
        // CRITICAL: Click-to-place clone mode (Duplicate to Selected Location) - do NOT create temporary line here.
        if (isCloneMode && cloneTemplate && lockedAction === ACTIONS_SELECT) {
          break;
        }
        // Single-element line clone (toolbar locks ACTIONS.LINE): same pattern as rectangle/circle —
        // no temp node on pointerDown; first pointerMove creates the line from cloneTemplate.
        if (isCloneMode && cloneTemplate && lockedAction === ACTIONS_LINE) {
          currentLineId.current = id;
          setIsDrawingLine(true);
          setLineStartPoint({ x: drawX, y: drawY });
          break;
        }
        if (!isDrawingLine) {
          const newLineId = generateMarkerId();
          currentShapeId.current = newLineId;
          currentLineId.current = newLineId; // Store line ID separately
          setIsDrawingLine(true);
          setLineStartPoint({ x: drawX, y: drawY });

          // Apply restrictions to initial line position
          const restrictedProps = applyInitialDrawingRestrictions("line", {
            x: drawX,
            y: drawY,
            width: 0,
            height: 0,
          });

          // Use activeSetLines (already defined at top of function) to handle off-canvas
          activeSetLines((prev) => {
            // Generate unique name by checking existing names
            // CRITICAL: Use the actual lines array (passed to function) to ensure we see all existing names
            const currentLinesArray = lines || [];
            const existingNames = new Set([
              ...currentLinesArray.map((l) => l.name),
              ...prev.map((l) => l.name), // Also include prev in case of pending updates
            ]);
            const baseName = cloneTemplate?.name || "Stroke 1";
            const newName = cloneTemplate ? generateUniqueNameForClone(baseName, existingNames) : `Stroke ${prev.length + 1}`;
            const unifiedDefaults = pickStyleFields(
              unifiedLineArrowDefaultPreset,
              [
                "stroke",
                "strokeColor",
                "fill",
                "fillColor",
                "strokeWidth",
                "isDashed",
                "dash",
                "opacity",
                "hasStartArrow",
                "hasEndArrow",
                "points",
              ],
            );
            const { id: templateId, name: templateName, ...cleanCloneTemplate } =
              cloneTemplate || {};
            const templateStyleFields = pickStyleFields(cleanCloneTemplate, [
              "stroke",
              "strokeColor",
              "fill",
              "fillColor",
              "strokeWidth",
              "isDashed",
              "dash",
              "opacity",
              "hasStartArrow",
              "hasEndArrow",
              "pointerAtBeginning",
              "pointerAtEnd",
              "pointerAtEnding",
              "startDot",
              "endDot",
            ]);
            // Use selected template style immediately on pointer-down (when present)
            // so draw-to-create does not flash the default preset first.
            const resolvedStyleDefaults = {
              ...unifiedDefaults,
              ...templateStyleFields,
            };
            const presetPointsRaw = Array.isArray(cloneTemplate?.points)
              ? cloneTemplate.points
              : Array.isArray(unifiedDefaults.points)
                ? unifiedDefaults.points
                : null;
            const normalizedPresetPoints =
              normalizeRelativePolylinePoints(
                presetPointsRaw,
                getElementAnchor(cloneTemplate).x,
                getElementAnchor(cloneTemplate).y,
              );
            const resolvedHasStartArrow =
              resolvedStyleDefaults.hasStartArrow !== undefined
                ? Boolean(resolvedStyleDefaults.hasStartArrow)
                : false;
            const resolvedHasEndArrow =
              resolvedStyleDefaults.hasEndArrow !== undefined
                ? Boolean(resolvedStyleDefaults.hasEndArrow)
                : true;
            return [
              ...prev,
              {
                id: newLineId,
                name: newName,
                ...restrictedProps,
                points: [0, 0, 0, 0], // Use relative coordinates (relative to line.x and line.y)
                fillColor,
                fill: fillColor,
                strokeColor: fillColor || "black",
                strokeWidth: 2,
                isNew: true,
                stroke: fillColor || "black",
                // Unified format defaults to single-ended arrow unless default preset overrides it.
                ...resolvedStyleDefaults,
                // Render preset geometry immediately to avoid one-frame straight-line fallback.
                points:
                  normalizedPresetPoints && normalizedPresetPoints.length >= 4
                    ? normalizedPresetPoints
                    : [0, 0, 0, 0],
                // Internal helper used only while drawing to preserve preset geometry on drag.
                _presetBasePoints: normalizedPresetPoints,
                hasStartArrow: resolvedHasStartArrow,
                hasEndArrow: resolvedHasEndArrow,
                // Keep legacy properties in sync for backward compatibility.
                pointerAtBeginning: resolvedHasStartArrow,
                pointerAtEnd: resolvedHasEndArrow,
                // Hide the just-created temp line until first drag move computes
                // real geometry, preventing a one-frame fallback flash.
                _deferInitialRender: true,
              },
            ];
          });
        }
        break;
      }

      case ACTIONS_TEXT: {
        // Start text drawing - user can drag to set width
        setIsDrawingText(true);

        // DEBUG: Log original click position

        // CRITICAL: Adjust position to account for text element padding
        // Text elements have 10px left and top padding (contentLeftOffset and currentYOffset)
        // The Group is positioned at (x, y) but visible content starts at (x + 10, y + 10)
        // So we subtract the padding to make the visible content appear at the click position
        // Note: The content div uses left: 10, top: yContentBody (which is currentYOffset = 10)
        // The first visible text line may have a slight visual offset due to font baseline/line-height
        // However, since the content div's top is exactly at yContentBody (10), we use the exact offset
        const TEXT_PADDING_OFFSET_X = 10; // contentLeftOffset from TextElement.jsx line 1694
        const TEXT_PADDING_OFFSET_Y = 10; // currentYOffset from TextElement.jsx line 733
        const adjustedX = drawX - TEXT_PADDING_OFFSET_X;
        const adjustedY = drawY - TEXT_PADDING_OFFSET_Y;

        // DEBUG: Log adjusted position

        setNewTextStart({ x: adjustedX, y: adjustedY });
        currentTextId.current = id; // Store text ID separately

        // Default text size for click-to-create.
        // If no drag move is emitted before pointer-up, this guarantees the
        // text is still created with usable width (instead of collapsing to 10).
        const defaultTextHeight = 100;
        const defaultTextWidth = 175; // Drag updates this live in onPointerMove

        // Apply restrictions to initial text position (using adjusted coordinates)
        const restrictedProps = applyInitialDrawingRestrictions("text", {
          x: adjustedX,
          y: adjustedY,
          width: defaultTextWidth,
          height: defaultTextHeight,
        });

        // DEBUG: Log final position after restrictions

        const finalTextX = restrictedProps.x;
        const finalTextY = restrictedProps.y;

        // DEBUG: Log final text creation

        activeSetTexts((prev) => {
          const textNumber = prev.length + 1;
          return [
            ...prev,
            {
              id,
              name: `Text Box TB${textNumber}`,
              x: finalTextX,
              y: finalTextY,
              part: "",
              task: "",
              note: "",
              unit: "",
              qty: 0,
              textStyles: {},
              selectedContent: null,
              showBackground: true,
              width: defaultTextWidth,
              height: defaultTextHeight,
              showContentInput: false,
              content: null,
              fontSize: 14,
              strokeWidth: 1, // Initial stroke width for preview visibility
              isNew: true,
            },
          ];
        });
        break;
      }

      case ACTIONS_IMAGE: {
        // CRITICAL: In clone mode, handle image cloning similar to rectangles
        if (isCloneMode && lockedAction === ACTIONS_IMAGE && cloneTemplate) {
          setIsDrawingImage(true);
          setImageStartPoint({ x: drawX, y: drawY });

          // Reset constraint tracking for new image
          if (imageConstraintRef) imageConstraintRef.current = null;
          if (previewImageDataRef) previewImageDataRef.current = null;
          // Create preview image from clone template
          const previewImageId = id;
          currentImageId.current = previewImageId;

          // Get image data from clone template
          const templateImage = cloneTemplate.image; // HTMLImageElement if available
          const templateSrc =
            cloneTemplate.src ||
            cloneTemplate.imageDataUrl ||
            cloneTemplate.image?.src ||
            null;
          const templateWidth = cloneTemplate.width || 100;
          const templateHeight = cloneTemplate.height || 100;

          // If we have an HTMLImageElement, use it directly
          // Otherwise, we'll need to load it from src
          if (templateImage && templateImage instanceof window.Image) {
            // Image is already loaded, use it directly
            activeSetImages((prev) => {
              const imageNumber = prev.length + 1;
              return [
                ...prev,
                {
                  id: previewImageId,
                  image: templateImage, // Use image from clone template
                  src: templateSrc,
                  x: drawX,
                  y: drawY,
                  width: templateWidth,
                  height: templateHeight,
                  maintainAspectRatio:
                    cloneTemplate.maintainAspectRatio !== false,
                  compress: cloneTemplate.compress !== false, // Default to true unless explicitly false
                  imageNumber,
                  name: `Picture ${imageNumber}`,
                  isNew: true,
                  opacity: 0.7, // Semi-transparent preview
                  // Include all other properties from template
                  originalImage: cloneTemplate.originalImage || templateImage,
                  originalImageDataUrl:
                    cloneTemplate.originalImageDataUrl || templateSrc,
                  imageDataUrl: cloneTemplate.imageDataUrl || templateSrc,
                  compressedImage: cloneTemplate.compressedImage,
                  uncompressedCroppedImage:
                    cloneTemplate.uncompressedCroppedImage,
                  cropData: cloneTemplate.cropData,
                  originalCropRotation: cloneTemplate.originalCropRotation,
                  originalWidth: cloneTemplate.originalWidth || templateWidth,
                  originalHeight:
                    cloneTemplate.originalHeight || templateHeight,
                  originalAspectRatio:
                    cloneTemplate.originalAspectRatio ||
                    templateWidth / templateHeight,
                  flipHorizontal: cloneTemplate.flipHorizontal || false,
                  flipVertical: cloneTemplate.flipVertical || false,
                  hasBakedTransforms: cloneTemplate.hasBakedTransforms || false,
                  scaleX: cloneTemplate.scaleX || 1,
                  scaleY: cloneTemplate.scaleY || 1,
                  rotation: cloneTemplate.rotation || 0,
                  isOnTopLayer: cloneTemplate.isOnTopLayer !== false, // Default to true unless explicitly false
                  referenceUrl: cloneTemplate.referenceUrl || "",
                },
              ];
            });
          } else if (templateSrc) {
            // Need to load image from src
            const previewImg = new window.Image();
            previewImg.crossOrigin = "anonymous";
            previewImg.onload = () => {
              // Update the preview image with the loaded image
              activeSetImages((prev) =>
                prev.map((img) =>
                  img.id === previewImageId
                    ? {
                      ...img,
                      image: previewImg,
                    }
                    : img
                )
              );
            };
            previewImg.onerror = () => {
              console.error("Failed to load preview image from clone template");
            };
            previewImg.src = templateSrc;

            activeSetImages((prev) => {
              const imageNumber = prev.length + 1;
              return [
                ...prev,
                {
                  id: previewImageId,
                  image: previewImg, // Will be updated when loaded
                  src: templateSrc,
                  x: drawX,
                  y: drawY,
                  width: templateWidth,
                  height: templateHeight,
                  maintainAspectRatio:
                    cloneTemplate.maintainAspectRatio !== false,
                  compress: cloneTemplate.compress !== false, // Default to true unless explicitly false
                  imageNumber,
                  name: `Picture ${imageNumber}`,
                  isNew: true,
                  opacity: 0.7, // Semi-transparent preview
                  // Include all other properties from template
                  originalImage: cloneTemplate.originalImage,
                  originalImageDataUrl:
                    cloneTemplate.originalImageDataUrl || templateSrc,
                  imageDataUrl: cloneTemplate.imageDataUrl || templateSrc,
                  compressedImage: cloneTemplate.compressedImage,
                  uncompressedCroppedImage:
                    cloneTemplate.uncompressedCroppedImage,
                  cropData: cloneTemplate.cropData,
                  originalCropRotation: cloneTemplate.originalCropRotation,
                  originalWidth: cloneTemplate.originalWidth || templateWidth,
                  originalHeight:
                    cloneTemplate.originalHeight || templateHeight,
                  originalAspectRatio:
                    cloneTemplate.originalAspectRatio ||
                    templateWidth / templateHeight,
                  flipHorizontal: cloneTemplate.flipHorizontal || false,
                  flipVertical: cloneTemplate.flipVertical || false,
                  hasBakedTransforms: cloneTemplate.hasBakedTransforms || false,
                  scaleX: cloneTemplate.scaleX || 1,
                  scaleY: cloneTemplate.scaleY || 1,
                  rotation: cloneTemplate.rotation || 0,
                  isOnTopLayer: cloneTemplate.isOnTopLayer !== false, // Default to true unless explicitly false
                  referenceUrl: cloneTemplate.referenceUrl || "",
                },
              ];
            });
          } else {
            console.error(
              "🟣 [POINTER-DOWN] No image source available in clone template",
              {
                cloneTemplateKeys: Object.keys(cloneTemplate),
              }
            );
          }

          break; // Exit early, don't process regular image upload
        }

        if (!pendingImageData) return;

        // Start drawing image - user can either click or drag
        setIsDrawingImage(true);
        setImageStartPoint({ x: drawX, y: drawY });

        // Reset constraint tracking for new image
        if (imageConstraintRef) imageConstraintRef.current = null;

        // Create preview image
        const previewImageId = generateMarkerId();
        currentImageId.current = previewImageId;

        // Use the already-loaded image from pendingImageData for immediate preview
        const previewImage = pendingImageData.loadedImage || null;

        // If image isn't loaded yet, load it
        if (!previewImage && pendingImageData.imageSrc) {
          const previewImg = new Image();
          previewImg.crossOrigin = "anonymous";
          previewImg.onload = () => {
            // Update the preview image with the loaded image
            activeSetImages((prev) =>
              prev.map((img) =>
                img.id === previewImageId
                  ? {
                    ...img,
                    image: previewImg,
                  }
                  : img
              )
            );
          };
          previewImg.onerror = () => {
            console.error("Failed to load preview image");
          };
          previewImg.src = pendingImageData.imageSrc;
        }

        // Start with 0x0 dimensions - will be updated when user drags
        // This ensures no preview is shown when holding at one static point
        const previewWidth = 0;
        const previewHeight = 0;

        activeSetImages((prev) => {
          const imageNumber = prev.length + 1;
          return [
            ...prev,
            {
              id: previewImageId,
              image: previewImage, // Use already-loaded image if available
              src: pendingImageData.imageSrc,
              x: drawX,
              y: drawY,
              width: previewWidth,
              height: previewHeight,
              maintainAspectRatio: true,
              compress: true, // Default to true for new images
              imageNumber,
              name: `Picture ${imageNumber}`,
              isNew: true,
              opacity: 0.7, // Semi-transparent preview
              useTopLeftAnchor: false, // Center anchor so rotation stays in place (same as preset images)
              isOnTopLayer: false, // New images should start on base layer
            },
          ];
        });
        break;
      }
    }

    // Only save to history for actions that aren't shape creation
    // Shape creation (RECTANGLE, CIRCLE, ARROW, DOUBLE_SIDED_ARROW, LINE, TEXT, IMAGE)
    // should only save when completed in onPointerUp, not when started in onPointerDown
    // This prevents duplicate/blank history entries
    if (
      action !== ACTIONS_RECTANGLE &&
      action !== ACTIONS_CIRCLE &&
      action !== ACTIONS_ARROW &&
      action !== ACTIONS_DOUBLE_SIDED_ARROW &&
      action !== ACTIONS_LINE &&
      action !== ACTIONS_TEXT &&
      action !== ACTIONS_IMAGE &&
      !isDrawingLine
    ) {
      saveToHistory();
    }
  };
};

/**
 * Handles pointer move events for drawing shapes on canvas
 * @param {Object} params - Parameters object
 */
export const createOnPointerMove = ({
  action,
  stageRef,
  isPaining,
  setHoverPoint,
  isDrawingRect,
  newRectStart,
  applyDrawingRestrictions,
  setRectangles,
  isDrawingCircle,
  newCircleStart,
  setCircles,
  isDrawingArrow,
  arrowStartPoint,
  currentShapeId,
  setArrows,
  isDrawingDoubleArrow,
  doubleArrowStartPoint,
  setDoubleSidedArrows,
  setScribbles,
  isDrawingLine,
  lineStartPoint,
  setLines,
  lines, // Current lines array (needed for name generation)
  arrows = null, // Current arrows array (needed for name generation for legacy arrows)
  doubleSidedArrows = null, // Current doubleSidedArrows array (needed for name generation)
  isDrawingText,
  newTextStart,
  currentTextId,
  setTexts,
  isDrawingImage,
  imageStartPoint,
  pendingImageData,
  setImages,
  currentImageId,
  imageConstraintRef, // Ref to track last used constraint (width/height) for smooth transitions
  previewImageDataRef = null, // Ref to store preview image data for perfect alignment
  ACTIONS_SELECT,
  ACTIONS_RECTANGLE,
  ACTIONS_CIRCLE,
  ACTIONS_ARROW,
  ACTIONS_DOUBLE_SIDED_ARROW,
  ACTIONS_SCRIBBLE,
  ACTIONS_LINE,
  ACTIONS_TEXT,
  ACTIONS_IMAGE,
  isCloneMode = false, // Track if clone mode is active
  cloneTemplate, // Template object for cloning shapes with exact properties
  currentLineId, // Ref for current line ID (needed for unified elements)
  lockedAction = null, // Track which action is locked (needed for clone mode)
  currentRectId = null, // Ref to current rectangle ID (needed for clone mode)
  currentCircleId = null, // Ref to current circle ID (needed for clone mode)
  isDrawingRectRef = null, // Synced in MyCanvas so pointer handlers see rect draw state before React re-renders
  newRectStartRef = null,
  // Off-canvas state setters (optional)
  offCanvasStageRef = null, // Ref to off-canvas stage to detect which canvas is active
  setOffCanvasRectangles = null,
  setOffCanvasCircles = null,
  setOffCanvasArrows = null,
  setOffCanvasDoubleSidedArrows = null,
  setOffCanvasLines = null,
  offCanvasLines = null, // Current off-canvas lines array (needed for name generation)
  setOffCanvasTexts = null,
  setOffCanvasImages = null,
  deferDrawingClampUntilPointerUp = false,
}) => {
  // Helper function to generate unique name by checking existing names
  // When cloning, we ALWAYS generate a new name (never reuse the original)
  const generateUniqueNameForClone = (baseName, existingNames) => {
    // Extract base name and number pattern (e.g., "Rectangle 2" -> "Rectangle", 2)
    const nameMatch = baseName.match(/^(.+?)\s+(\d+)$/);
    const base = nameMatch ? nameMatch[1] : baseName;

    // When cloning, we ALWAYS generate a new name by finding the highest existing number
    // Start from 0 to ensure we find the true maximum across all existing names
    let maxNumber = 0;
    existingNames.forEach((existingName) => {
      const existingMatch = existingName.match(new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+)$`));
      if (existingMatch) {
        const num = parseInt(existingMatch[1]);
        if (num > maxNumber) {
          maxNumber = num;
        }
      }
    });

    // Use the next number after the highest existing number
    // This ensures we never reuse an existing name, even if the base name doesn't exist in the set
    const nextNumber = maxNumber + 1;
    return `${base} ${nextNumber}`;
  };

  return function onPointerMove(e) {
    // Allow pointerMove to run for clone mode even when action is SELECT
    if (action === ACTIONS_SELECT && !isCloneMode) return;

    const stage = stageRef.current;
    if (!stage) return;

    // Debug: Log when onPointerMove is called for unified arrows/lines
    if (
      isCloneMode &&
      (lockedAction === ACTIONS_ARROW ||
        lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
        lockedAction === ACTIONS_LINE) &&
      isDrawingLine
    ) {
    }

    // Determine which canvas we're drawing on by comparing stage refs
    const isOffCanvas =
      offCanvasStageRef && stage === offCanvasStageRef.current;

    // Choose the appropriate state setters based on which canvas is active
    const activeSetRectangles =
      isOffCanvas && setOffCanvasRectangles
        ? setOffCanvasRectangles
        : setRectangles;
    const activeSetCircles =
      isOffCanvas && setOffCanvasCircles ? setOffCanvasCircles : setCircles;
    const activeSetArrows =
      isOffCanvas && setOffCanvasArrows ? setOffCanvasArrows : setArrows;
    const activeSetDoubleSidedArrows =
      isOffCanvas && setOffCanvasDoubleSidedArrows
        ? setOffCanvasDoubleSidedArrows
        : setDoubleSidedArrows;
    const activeSetLines =
      isOffCanvas && setOffCanvasLines ? setOffCanvasLines : setLines;
    const activeSetTexts =
      isOffCanvas && setOffCanvasTexts ? setOffCanvasTexts : setTexts;
    const activeSetImages =
      isOffCanvas && setOffCanvasImages ? setOffCanvasImages : setImages;

    const rectDrawable = isDrawingRectRef?.current ?? isDrawingRect;
    const rectStartLive = newRectStartRef?.current ?? newRectStart;

    // CRITICAL: Set pointer position from event if provided, otherwise use current position
    // This ensures smooth tracking during drag operations
    if (e && e.evt) {
      stage.setPointersPositions(e.evt);
    }

    const pointerPos = stage.getPointerPosition();
    if (!pointerPos) return;

    const { x, y } = pointerPos;
    const stageWidth = stage.width();
    const stageHeight = stage.height();
    const stageClampBufferPx =
      action === ACTIONS_SELECT ||
      action === ACTIONS_RECTANGLE ||
      action === ACTIONS_CIRCLE ||
      action === ACTIONS_ARROW ||
      action === ACTIONS_DOUBLE_SIDED_ARROW ||
      action === ACTIONS_LINE ||
      action === ACTIONS_TEXT ||
      action === ACTIONS_IMAGE ||
      lockedAction === ACTIONS_RECTANGLE ||
      lockedAction === ACTIONS_CIRCLE ||
      lockedAction === ACTIONS_ARROW ||
      lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
      lockedAction === ACTIONS_LINE ||
      lockedAction === ACTIONS_TEXT ||
      lockedAction === ACTIONS_IMAGE
        ? DRAW_END_TRANSFORMER_BUFFER_PX
        : 0;
    const rectangleDefaultPreset = getDefaultStylePreset(
      "rectangleDefaultStylePreset",
    );
    const activeStrokeWidth = Number(cloneTemplate?.strokeWidth ?? 2);
    const strokeHalf = Math.max(0, activeStrokeWidth / 2);

    const clampedX = deferDrawingClampUntilPointerUp
      ? x
      : Math.max(strokeHalf, Math.min(x, stageWidth - strokeHalf));
    const clampedY = deferDrawingClampUntilPointerUp
      ? y
      : Math.max(strokeHalf, Math.min(y, stageHeight - strokeHalf));
    const applyLiveDrawingRestrictions = (shapeType, props) =>
      deferDrawingClampUntilPointerUp
        ? { ...props }
        : applyDrawingRestrictions(shapeType, props);
    const fitImageSizeToOffCanvasStrip = (width, height) => {
      const safeWidth = Math.max(10, Number(width) || 0);
      const safeHeight = Math.max(10, Number(height) || 0);
      if (!isOffCanvas || stageWidth <= 0) {
        return { width: safeWidth, height: safeHeight };
      }
      const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
      const leftStripWidth = Math.max(0, mainZoneStart - strokeHalf * 2);
      const rightStripWidth = Math.max(0, stageWidth - mainZoneEnd - strokeHalf * 2);
      const maxStripWidth = Math.max(leftStripWidth, rightStripWidth);
      if (!Number.isFinite(maxStripWidth) || maxStripWidth <= 0) {
        return { width: safeWidth, height: safeHeight };
      }
      if (safeWidth <= maxStripWidth) {
        return { width: safeWidth, height: safeHeight };
      }
      const scale = maxStripWidth / safeWidth;
      return {
        width: Math.max(10, maxStripWidth),
        height: Math.max(10, safeHeight * scale),
      };
    };
    const clampCenterWithSize = (cx, cy, width, height, border = 0) => {
      if (deferDrawingClampUntilPointerUp) {
        return {
          x: Number(cx) || 0,
          y: Number(cy) || 0,
          width: Math.max(10, Number(width) || 0),
          height: Math.max(10, Number(height) || 0),
        };
      }
      const fitted = fitImageSizeToOffCanvasStrip(width, height);
      const halfW = Math.max(0, Number(fitted.width) || 0) / 2 + border;
      const halfH = Math.max(0, Number(fitted.height) || 0) / 2 + border;
      const targetTopLeftX =
        (Number(cx) || 0) - Math.max(0, Number(fitted.width) || 0) / 2;
      const clampedTopLeftX = isOffCanvas
        ? clampOffCanvasTopLeftXAwayFromMainColumn(
            targetTopLeftX,
            Number(fitted.width) || 0,
            stageWidth,
            0,
          )
        : Math.max(0, Math.min(targetTopLeftX, stageWidth - (Number(fitted.width) || 0)));
      return {
        x: clampedTopLeftX + (Number(fitted.width) || 0) / 2,
        y: Math.max(halfH, Math.min(Number(cy) || 0, stageHeight - halfH)),
        width: Number(fitted.width) || 0,
        height: Number(fitted.height) || 0,
      };
    };
    const clampLineGeometryInStage = (groupX, groupY, relPoints, lineStrokeWidth = 2) => {
      if (deferDrawingClampUntilPointerUp) {
        return { x: groupX, y: groupY, points: relPoints };
      }
      if (!Array.isArray(relPoints) || relPoints.length < 4) {
        return { x: groupX, y: groupY, points: relPoints };
      }
      const strokePad = Math.max(0, Number(lineStrokeWidth) || 0) / 2;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < relPoints.length; i += 2) {
        const absX = (Number(groupX) || 0) + (Number(relPoints[i]) || 0);
        const absY = (Number(groupY) || 0) + (Number(relPoints[i + 1]) || 0);
        minX = Math.min(minX, absX);
        maxX = Math.max(maxX, absX);
        minY = Math.min(minY, absY);
        maxY = Math.max(maxY, absY);
      }
      let dx = 0;
      let dy = 0;
      if (minX - strokePad < 0) dx = -(minX - strokePad);
      else if (maxX + strokePad > stageWidth) dx = stageWidth - (maxX + strokePad);
      if (minY - strokePad < 0) dy = -(minY - strokePad);
      else if (maxY + strokePad > stageHeight) dy = stageHeight - (maxY + strokePad);
      return {
        x: (Number(groupX) || 0) + dx,
        y: (Number(groupY) || 0) + dy,
        points: relPoints,
      };
    };

    // Update hover point for drawing previews
    setHoverPoint({ x: clampedX, y: clampedY });

    if (!isPaining.current) return;

    // CRITICAL: In clone mode, create rectangle when dragging starts (first onPointerMove)
    // In clone mode, onPointerDown stores start position but doesn't create rectangle
    // So we need to create it here when dragging actually starts
    // BUT: Only create if currentRectId is not null (it might be null if onPointerUp already cleared it)
    if (
      isCloneMode &&
      cloneTemplate &&
      lockedAction === ACTIONS_RECTANGLE &&
      rectDrawable &&
      rectStartLive &&
      currentRectId.current
    ) {
      const rectLockedCanvas = cloneDrawCanvasByShapeId.get(
        currentRectId.current,
      );
      const activeSetRectanglesForClone =
        rectLockedCanvas === "off-canvas" && setOffCanvasRectangles
          ? setOffCanvasRectangles
          : rectLockedCanvas === "main"
            ? setRectangles
            : activeSetRectangles;
      // CRITICAL: Use a functional update with a check to prevent race conditions
      // Multiple onPointerMove events can fire before state updates, so we need to check atomically
      activeSetRectanglesForClone((prev) => {
        // CRITICAL: Double-check currentRectId is still set (might have been cleared by onPointerUp)
        if (!currentRectId.current) {
          return prev;
        }

        const targetId = currentRectId.current;
        const rectangleExists = prev.some((rect) => rect.id === targetId);

        // Reduced logging - only log when creating or if there's an issue

        if (rectangleExists) {
          // Rectangle already exists, continue with normal drawing logic below
          // Reduced logging
          return prev;
        }

        // Rectangle doesn't exist - create it now (first movement detected = drag started)
        // CRITICAL: Double-check it still doesn't exist (race condition protection)
        // Another onPointerMove might have created it between the check above and here
        const stillDoesNotExist = !prev.some((rect) => rect.id === targetId);
        if (!stillDoesNotExist) {
          return prev;
        }


        const id = targetId;
        // Generate unique name by checking existing names
        const existingNames = new Set(prev.map((r) => r.name));
        const baseName = cloneTemplate?.name || "Rectangle 1";
        const newName = generateUniqueNameForClone(baseName, existingNames);

        const restrictedProps = applyDrawingRestrictions("rectangle", {
          x: rectStartLive.x,
          y: rectStartLive.y,
          width: 0,
          height: 0,
        });

        // Create a clean template without id and name to prevent conflicts
        const { id: templateId, name: templateName, ...cleanCloneTemplate } = cloneTemplate || {};

        const baseProps = cloneTemplate
          ? {
            ...cleanCloneTemplate,
            x: restrictedProps.x,
            y: restrictedProps.y,
            width: 0,
            height: 0,
          }
          : {
            fill: "#FFFFFF",
            stroke: "#FF0000",
            fillColor: "#FFFFFF",
            strokeColor: "#FF0000",
            maintainAspectRatio: false,
            strokeWidth: 2,
          };

        // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
        // Also explicitly set id after spreading clean template to ensure it's always unique
        const newRect = {
          ...restrictedProps,
          ...baseProps,
          name: newName, // Set name AFTER spreading to prevent template name from overwriting
          id, // Always set a fresh unique ID
          fillOpacity:
            cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
          strokeOpacity:
            cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
          opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
          isNew: true,
          isHighlighted: false,
          createdOnCanvas: true, // Mark as user-created element
        };

        console.log('[PointerHandlers] Created new rectangle with createdOnCanvas:', {
          id: newRect.id,
          name: newRect.name,
          createdOnCanvas: newRect.createdOnCanvas,
          isNew: newRect.isNew
        });


        return [...prev, newRect];
      });
    }

    // CRITICAL: In clone mode, create circle when dragging starts (first onPointerMove)
    // In clone mode, onPointerDown stores start position but doesn't create circle
    // So we need to create it here when dragging actually starts
    // BUT: Only create if currentCircleId is not null (it might be null if onPointerUp already cleared it)
    if (
      isCloneMode &&
      cloneTemplate &&
      lockedAction === ACTIONS_CIRCLE &&
      isDrawingCircle &&
      newCircleStart &&
      currentCircleId.current
    ) {
      const circleLockedCanvas = cloneDrawCanvasByShapeId.get(
        currentCircleId.current,
      );
      const activeSetCirclesForClone =
        circleLockedCanvas === "off-canvas" && setOffCanvasCircles
          ? setOffCanvasCircles
          : circleLockedCanvas === "main"
            ? setCircles
            : activeSetCircles;
      // CRITICAL: Use a functional update with a check to prevent race conditions
      // Multiple onPointerMove events can fire before state updates, so we need to check atomically
      activeSetCirclesForClone((prev) => {
        // CRITICAL: Double-check currentCircleId is still set (might have been cleared by onPointerUp)
        if (!currentCircleId.current) {
          return prev;
        }

        const targetId = currentCircleId.current;

        // CRITICAL: Check if circle with this ID already exists - use findIndex for better performance
        const existingIndex = prev.findIndex(
          (circle) => circle.id === targetId
        );

        if (existingIndex !== -1) {
          // Circle already exists, continue with normal drawing logic below
          // Don't create duplicate
          return prev;
        }

        // CRITICAL: Double-check one more time to prevent race conditions
        // In case another onPointerMove event added it between the check above and here
        const stillDoesNotExist =
          prev.findIndex((circle) => circle.id === targetId) === -1;
        if (!stillDoesNotExist) {
          return prev;
        }


        // Use the targetId that was set in onPointerDown
        const id = targetId;

        // Generate unique name by checking existing names
        const existingNames = new Set(prev.map((c) => c.name));
        const baseName = cloneTemplate?.name || "Circle 1";
        const newName = generateUniqueNameForClone(baseName, existingNames);

        const restrictedProps = applyDrawingRestrictions("circle", {
          x: newCircleStart.x,
          y: newCircleStart.y,
          radius: 0,
        });

        // Create a clean template without id to prevent any ID conflicts
        const { id: templateId, name: templateName, ...cleanCloneTemplate } = cloneTemplate || {};

        const baseProps = cloneTemplate
          ? {
            ...cleanCloneTemplate,
            x: restrictedProps.x,
            y: restrictedProps.y,
            radius: 0,
          }
          : {
            fill: "#FFFFFF",
            stroke: "#FF0000",
            fillColor: "#FFFFFF",
            maintainAspectRatio: true,
            strokeColor: "#FF0000",
            strokeWidth: 2,
          };

        // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
        // Also explicitly set id after spreading clean template to ensure it's always unique
        const newCircle = {
          ...restrictedProps,
          ...baseProps,
          name: newName, // Set name AFTER spreading to prevent template name from overwriting
          id, // Always set a fresh unique ID
          fillOpacity:
            cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
          strokeOpacity:
            cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
          opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
          isNew: true, // CRITICAL: Set isNew to true so it shows with full opacity while drawing
          isHighlighted: false,
          createdOnCanvas: true, // Mark as user-created element
        };

        console.log('[PointerHandlers] Created new circle with createdOnCanvas:', {
          id: newCircle.id,
          name: newCircle.name,
          createdOnCanvas: newCircle.createdOnCanvas,
          isNew: newCircle.isNew
        });

        // CRITICAL: Final check before adding - ensure no duplicate IDs in the new array
        const finalCheck = prev.every((circle) => circle.id !== id);
        if (!finalCheck) {
          console.error(
            "🟢 [POINTER-MOVE] ❌ DUPLICATE ID DETECTED - skipping circle creation",
            {
              id,
              existingIds: prev.map((c) => c.id),
            }
          );
          return prev; // Don't add if duplicate detected
        }

        return [...prev, newCircle];
      });
    }

    // Handle rectangle drawing
    if (rectDrawable && rectStartLive) {
      // CRITICAL FIX: Calculate dimensions first, then determine position based on drag direction
      // This prevents position jumping when shrinking and expanding in different directions
      const width = Math.abs(clampedX - rectStartLive.x);
      const height = Math.abs(clampedY - rectStartLive.y);

      // Determine position based on which direction we're dragging
      // If dragging right/down: position stays at start point
      // If dragging left/up: position moves to maintain top-left corner
      const x = clampedX < rectStartLive.x ? clampedX : rectStartLive.x;
      const y = clampedY < rectStartLive.y ? clampedY : rectStartLive.y;

      // Apply restrictions to rectangle drawing
      const restrictedProps = applyLiveDrawingRestrictions("rectangle", {
        x,
        y,
        width,
        height,
      });

      const strokeForRectClamp =
        isCloneMode && cloneTemplate
          ? Number(cloneTemplate.strokeWidth ?? 2)
          : Number(rectangleDefaultPreset?.strokeWidth ?? 2);
      let clampedRectGeom = restrictedProps;
      if (!deferDrawingClampUntilPointerUp && stageWidth > 0 && stageHeight > 0) {
        if (isOffCanvas) {
          const sh = Math.max(0, Number(strokeForRectClamp) || 0) / 2;
          const edgePad = sh + stageClampBufferPx;
          const cx = clampOffCanvasTopLeftXAwayFromMainColumn(
            restrictedProps.x,
            restrictedProps.width,
            stageWidth,
            edgePad,
          );
          clampedRectGeom = {
            ...restrictedProps,
            x: cx,
            y: Math.max(
              edgePad,
              Math.min(
                stageHeight - restrictedProps.height - edgePad,
                restrictedProps.y,
              ),
            ),
          };
        } else {
          clampedRectGeom = clampRectangleTopLeftToStageBounds(
            restrictedProps,
            stageWidth,
            stageHeight,
            strokeForRectClamp,
            stageClampBufferPx,
          );
        }
      }

      const drawRectId = currentRectId.current;
      activeSetRectangles((prev) =>
        prev.map((rect) =>
          rect.isNew && drawRectId && rect.id === drawRectId
            ? {
              ...rect,
              ...clampedRectGeom,
              isFirstTime: true,
              // Preserve configured opacity during drawing updates.
              fillOpacity:
                rect.fillOpacity !== undefined ? rect.fillOpacity : 1,
              strokeOpacity:
                rect.strokeOpacity !== undefined ? rect.strokeOpacity : 1,
              opacity: rect.opacity !== undefined ? rect.opacity : 1,
            }
            : rect
        )
      );

      return;
    }

    // Handle circle drawing
    if (isDrawingCircle && newCircleStart) {
      const boundedCenterX = newCircleStart.x;
      const boundedCenterY = newCircleStart.y;
      // Use raw pointer coordinates for draw radius so circles can extend beyond stage
      // while drawing (same UX expectation as rectangle drawing/transform flows).
      let radius = Math.hypot(x - newCircleStart.x, y - newCircleStart.y);
      // Match transform UX: do not cap radius while drawing.

      // Live circle draw should not be capped by shared MAX_RADIUS restrictions.
      // Keep center at draw start and allow radius to grow with pointer distance,
      // then finalize/clamp later in pointer-up/transform flows.
      const restrictedProps = {
        x: boundedCenterX,
        y: boundedCenterY,
        radius: Math.max(0, Number(radius) || 0),
      };

      const drawCircleId = currentCircleId.current;
      activeSetCircles((prev) =>
        prev.map((circle) =>
          circle.isNew && drawCircleId && circle.id === drawCircleId
            ? {
              ...circle,
              ...restrictedProps,
              isFirstTime: true,
              // Keep isNew: true while drawing, but preserve configured opacity.
              isNew: true,
              fillOpacity:
                circle.fillOpacity !== undefined ? circle.fillOpacity : 1,
              strokeOpacity:
                circle.strokeOpacity !== undefined ? circle.strokeOpacity : 1,
              opacity: circle.opacity !== undefined ? circle.opacity : 1,
            }
            : circle
        )
      );

      return;
    }

    // Handle text drawing - user can only change width, height stays fixed
    if (isDrawingText && newTextStart) {
      const startX = newTextStart.x;
      const startY = newTextStart.y;
      const width = Math.abs(clampedX - startX);
      // Height is fixed at default (100), user can't change it during drawing
      const defaultTextHeight = 100;

      // Calculate position - adjust if dragging left
      let finalX = startX;
      let finalY = startY;

      if (clampedX < startX) {
        // Dragging left - adjust X position
        finalX = clampedX;
      }

      // Apply restrictions to text drawing (only width can change)
      const restrictedProps = applyLiveDrawingRestrictions("text", {
        x: finalX,
        y: finalY,
        width: Math.max(10, width), // Minimum width of 10
        height: defaultTextHeight,
      });
      const drawTextProps = deferDrawingClampUntilPointerUp
        ? {
            x: finalX,
            y: finalY,
            width: Math.max(10, width),
            height: defaultTextHeight,
          }
        : restrictedProps;

      activeSetTexts((prev) =>
        prev.map((text) =>
          text.isNew && text.id === currentTextId.current
            ? {
              ...text,
              x: drawTextProps.x,
              y: drawTextProps.y,
              width: drawTextProps.width,
              height: drawTextProps.height,
            }
            : text
        )
      );

      return;
    }

    // Handle image drawing preview - similar to rectangle drawing
    // Support both normal image upload (pendingImageData) and clone mode (cloneTemplate)
    if (
      isDrawingImage &&
      imageStartPoint &&
      (pendingImageData ||
        (isCloneMode && cloneTemplate && lockedAction === ACTIONS_IMAGE))
    ) {
      const startX = imageStartPoint.x;
      const startY = imageStartPoint.y;

      // Calculate raw dimensions from drag
      const rawWidth = clampedX - startX;
      const rawHeight = clampedY - startY;
      const absWidth = Math.abs(rawWidth);
      const absHeight = Math.abs(rawHeight);

      // Maintain aspect ratio based on natural dimensions
      // In clone mode, use template dimensions; otherwise use pendingImageData
      const naturalWidth =
        isCloneMode && cloneTemplate
          ? cloneTemplate.width || 100
          : pendingImageData?.naturalWidth || 100;
      const naturalHeight =
        isCloneMode && cloneTemplate
          ? cloneTemplate.height || 100
          : pendingImageData?.naturalHeight || 100;
      const aspectRatio = naturalWidth / naturalHeight;

      // Improved calculation for smoother behavior
      let finalWidth, finalHeight;

      // Handle edge cases first
      if (absWidth < 1 && absHeight < 1) {
        finalWidth = 10;
        finalHeight = 10 / aspectRatio;
      } else if (absWidth < 1) {
        finalHeight = Math.max(10, absHeight);
        finalWidth = finalHeight * aspectRatio;
      } else if (absHeight < 1) {
        finalWidth = Math.max(10, absWidth);
        finalHeight = finalWidth / aspectRatio;
      } else {
        // Both dimensions are valid - use improved hysteresis for smoother transitions
        const lastConstraint = imageConstraintRef?.current || null;
        const widthRatio = absWidth / absHeight;
        const aspectRatioThreshold = aspectRatio;

        // Improved hysteresis with wider threshold to prevent jitter
        let useWidthConstraint = false;

        if (lastConstraint === "width") {
          // If we were using width, only switch to height if significantly more appropriate
          // Wider threshold (30% instead of 20%) for smoother behavior
          useWidthConstraint = widthRatio >= aspectRatioThreshold * 0.7;
        } else if (lastConstraint === "height") {
          // If we were using height, only switch to width if significantly more appropriate
          useWidthConstraint = widthRatio >= aspectRatioThreshold * 1.3;
        } else {
          // First time - use whichever is more appropriate
          useWidthConstraint = widthRatio >= aspectRatioThreshold;
        }

        if (useWidthConstraint) {
          // Use width as primary
          finalWidth = absWidth;
          finalHeight = finalWidth / aspectRatio;
          if (imageConstraintRef) imageConstraintRef.current = "width";
        } else {
          // Use height as primary
          finalHeight = absHeight;
          finalWidth = finalHeight * aspectRatio;
          if (imageConstraintRef) imageConstraintRef.current = "height";
        }

        // Ensure minimum size
        if (finalWidth < 10) {
          finalWidth = 10;
          finalHeight = 10 / aspectRatio;
        }
        if (finalHeight < 10) {
          finalHeight = 10;
          finalWidth = 10 * aspectRatio;
        }
      }

      // Calculate top-left of drawn rect (for center conversion)
      // If dragging right/down: position stays at start point (top-left)
      // If dragging left/up: position moves to maintain top-left corner
      const previewX = clampedX < startX ? clampedX : startX;
      const previewY = clampedY < startY ? clampedY : startY;
      // Use center anchor so rotation stays in place (same as preset images)
      const centerX = previewX + finalWidth / 2;
      const centerY = previewY + finalHeight / 2;
      const boundedCenter = clampCenterWithSize(
        centerX,
        centerY,
        finalWidth,
        finalHeight,
        0
      );

      // CRITICAL: Always store preview image data in ref FIRST (before state update)
      // Store CENTER position so onPointerUp creates final image with center anchor
      if (previewImageDataRef && currentImageId.current) {
        previewImageDataRef.current = {
          x: boundedCenter.x,
          y: boundedCenter.y,
          width: boundedCenter.width,
          height: boundedCenter.height,
        };
      }

      // Update preview image smoothly - use functional update to prevent stale closures
      activeSetImages((prev) => {
        const previewImage = prev.find(
          (img) => img.isNew && img.id === currentImageId.current
        );
        if (!previewImage) return prev;

        // Only update if values actually changed to prevent unnecessary re-renders
        if (
          previewImage.x === boundedCenter.x &&
          previewImage.y === boundedCenter.y &&
          previewImage.width === boundedCenter.width &&
          previewImage.height === boundedCenter.height
        ) {
          // Even if state doesn't change, the ref is already updated above
          return prev;
        }

        return prev.map((img) =>
          img.isNew && img.id === currentImageId.current
            ? {
              ...img,
              x: boundedCenter.x,
              y: boundedCenter.y,
              width: boundedCenter.width,
              height: boundedCenter.height,
            }
            : img
        );
      });

      return;
    }

    const activeDrawingAction =
      action === ACTIONS_SELECT &&
      isCloneMode &&
      cloneTemplate &&
      (lockedAction === ACTIONS_LINE ||
        lockedAction === ACTIONS_ARROW ||
        lockedAction === ACTIONS_DOUBLE_SIDED_ARROW)
        ? lockedAction
        : action;

    if (
      isCloneMode &&
      cloneTemplate &&
      (lockedAction === ACTIONS_ARROW ||
        lockedAction === ACTIONS_DOUBLE_SIDED_ARROW) &&
      (isDrawingLine || currentLineId?.current)
    ) {
      console.log("[CLONE-DRAW][MOVE]", {
        action,
        activeDrawingAction,
        lockedAction,
        isDrawingLine,
        hasLineStartPoint: !!lineStartPoint,
        currentLineId: currentLineId?.current || null,
        isOffCanvas,
      });
    }

    switch (activeDrawingAction) {
      case ACTIONS_RECTANGLE:
        // This will not be reached when isDrawingRect is true
        break;

      case ACTIONS_CIRCLE:
        // This will not be reached when isDrawingCircle is true
        break;

      case ACTIONS_ARROW:
        // Check if this is a unified element being drawn in lines array
        const isUnifiedArrowDrawing =
          isCloneMode &&
          cloneTemplate &&
          isDrawingLine &&
          lineStartPoint &&
          currentLineId.current;
        if (isUnifiedArrowDrawing) {
          // Handle unified arrow drawing in lines array
          let deltaX = clampedX - lineStartPoint.x;
          let deltaY = clampedY - lineStartPoint.y;

          // Apply size restrictions only (not position restrictions)
          const restrictedProps = applyLiveDrawingRestrictions("line", {
            width: Math.abs(deltaX),
            height: Math.abs(deltaY),
          });

          // Recalculate deltas based on restrictions
          const restrictedWidth = restrictedProps.width;
          const restrictedHeight = restrictedProps.height;

          // Maintain aspect ratio if width was restricted
          if (Math.abs(deltaX) > 0) {
            const scale = restrictedWidth / Math.abs(deltaX);
            deltaX = deltaX * scale;
            deltaY = deltaY * scale;
          }

          // Use activeSetLines (already defined at top of function) to handle off-canvas
          activeSetLines((lines) => {
            const updated = lines.map((line) =>
              line.id === currentLineId.current
                ? (() => {
                  const targetDx = clampedX - lineStartPoint.x;
                  const targetDy = clampedY - lineStartPoint.y;
                  const basePoints = Array.isArray(line._presetBasePoints)
                    ? line._presetBasePoints
                    : null;
                  const canUsePresetGeometry =
                    basePoints && basePoints.length >= 4;
                  let nextPoints = [0, 0, targetDx, targetDy];
                  if (canUsePresetGeometry) {
                    const baseStartX = Number(basePoints[0]) || 0;
                    const baseStartY = Number(basePoints[1]) || 0;
                    const baseEndX =
                      Number(basePoints[basePoints.length - 2]) || 0;
                    const baseEndY =
                      Number(basePoints[basePoints.length - 1]) || 0;
                    const baseDx = baseEndX - baseStartX;
                    const baseDy = baseEndY - baseStartY;
                    const baseLen = Math.hypot(baseDx, baseDy);
                    const targetLen = Math.hypot(targetDx, targetDy);

                    if (baseLen > 0.001 && targetLen > 0.001) {
                      const baseAngle = Math.atan2(baseDy, baseDx);
                      const targetAngle = Math.atan2(targetDy, targetDx);
                      const rotateBy = targetAngle - baseAngle;
                      const scale = targetLen / baseLen;
                      const cosR = Math.cos(rotateBy);
                      const sinR = Math.sin(rotateBy);

                      nextPoints = basePoints.map((coord, idx) => {
                        if (idx % 2 === 0) {
                          const relX = (Number(coord) || 0) - baseStartX;
                          const relY =
                            (Number(basePoints[idx + 1]) || 0) - baseStartY;
                          return (relX * cosR - relY * sinR) * scale;
                        }
                        const relX =
                          (Number(basePoints[idx - 1]) || 0) - baseStartX;
                        const relY = (Number(coord) || 0) - baseStartY;
                        return (relX * sinR + relY * cosR) * scale;
                      });
                    } else if (baseLen > 0.001) {
                      // Keep preset geometry for tiny first-drag movement so we
                      // don't flash a straight fallback line for one frame.
                      nextPoints = basePoints.map((coord, idx) =>
                        idx % 2 === 0
                          ? (Number(coord) || 0) - baseStartX
                          : (Number(coord) || 0) - baseStartY
                      );
                    }
                  }
                  const bounded = clampLineGeometryInStage(
                    lineStartPoint.x,
                    lineStartPoint.y,
                    nextPoints,
                    line.strokeWidth || 2
                  );
                  return {
                    ...line,
                    x: bounded.x,
                    y: bounded.y,
                    points: bounded.points,
                    width: Math.abs(nextPoints[2] - nextPoints[0]),
                    height: Math.abs(nextPoints[3] - nextPoints[1]),
                    _deferInitialRender: false,
                  };
                })()
                : line
            );
            return updated;
          });
        } else if (
          isDrawingArrow &&
          arrowStartPoint &&
          currentShapeId.current
        ) {
          // For arrows, we want to preserve the original direction
          // Calculate points relative to the start point
          let deltaX = clampedX - arrowStartPoint.x;
          let deltaY = clampedY - arrowStartPoint.y;

          // Apply restrictions to arrow drawing
          const restrictedProps = applyLiveDrawingRestrictions("arrow", {
            x: arrowStartPoint.x,
            y: arrowStartPoint.y,
            width: Math.abs(deltaX),
            height: Math.abs(deltaY),
          });

          // Recalculate deltas based on restrictions
          const restrictedWidth = restrictedProps.width;
          const restrictedHeight = restrictedProps.height;

          // Maintain aspect ratio if width was restricted
          if (Math.abs(deltaX) > 0) {
            const scale = restrictedWidth / Math.abs(deltaX);
            deltaX = deltaX * scale;
            deltaY = deltaY * scale;
          }

          setArrows((arrows) => {
            const updated = arrows.map((arrow) =>
              arrow.id === currentShapeId.current
                ? {
                  ...arrow,
                  x: restrictedProps.x,
                  y: restrictedProps.y,
                  points: [
                    0,
                    0,
                    clampedX - restrictedProps.x,
                    clampedY - restrictedProps.y,
                  ],
                  width: Math.abs(clampedX - restrictedProps.x),
                  height: Math.abs(clampedY - restrictedProps.y),
                }
                : arrow
            );
            return updated;
          });
        }
        break;

      case ACTIONS_DOUBLE_SIDED_ARROW:
        // Check if this is a unified element being drawn in lines array
        const isUnifiedDoubleArrowDrawing =
          isCloneMode &&
          cloneTemplate &&
          isDrawingLine &&
          lineStartPoint &&
          currentLineId.current;
        if (isUnifiedDoubleArrowDrawing) {
          // Handle unified double-sided arrow drawing in lines array
          let deltaX = clampedX - lineStartPoint.x;
          let deltaY = clampedY - lineStartPoint.y;

          // Apply size restrictions only (not position restrictions)
          const restrictedProps = applyLiveDrawingRestrictions("line", {
            width: Math.abs(deltaX),
            height: Math.abs(deltaY),
          });

          // Recalculate deltas based on restrictions
          const restrictedWidth = restrictedProps.width;
          const restrictedHeight = restrictedProps.height;

          // Maintain aspect ratio if width was restricted
          if (Math.abs(deltaX) > 0) {
            const scale = restrictedWidth / Math.abs(deltaX);
            deltaX = deltaX * scale;
            deltaY = deltaY * scale;
          }

          // Use activeSetLines (already defined at top of function) to handle off-canvas
          activeSetLines((lines) => {
            const updated = lines.map((line) =>
              line.id === currentLineId.current
                ? (() => {
                  const targetDx = clampedX - lineStartPoint.x;
                  const targetDy = clampedY - lineStartPoint.y;
                  const basePoints = Array.isArray(line._presetBasePoints)
                    ? line._presetBasePoints
                    : null;
                  const canUsePresetGeometry =
                    basePoints && basePoints.length >= 4;
                  let nextPoints = [0, 0, targetDx, targetDy];
                  if (canUsePresetGeometry) {
                    const baseStartX = Number(basePoints[0]) || 0;
                    const baseStartY = Number(basePoints[1]) || 0;
                    const baseEndX =
                      Number(basePoints[basePoints.length - 2]) || 0;
                    const baseEndY =
                      Number(basePoints[basePoints.length - 1]) || 0;
                    const baseDx = baseEndX - baseStartX;
                    const baseDy = baseEndY - baseStartY;
                    const baseLen = Math.hypot(baseDx, baseDy);
                    const targetLen = Math.hypot(targetDx, targetDy);

                    if (baseLen > 0.001 && targetLen > 0.001) {
                      const baseAngle = Math.atan2(baseDy, baseDx);
                      const targetAngle = Math.atan2(targetDy, targetDx);
                      const rotateBy = targetAngle - baseAngle;
                      const scale = targetLen / baseLen;
                      const cosR = Math.cos(rotateBy);
                      const sinR = Math.sin(rotateBy);

                      nextPoints = basePoints.map((coord, idx) => {
                        if (idx % 2 === 0) {
                          const relX = (Number(coord) || 0) - baseStartX;
                          const relY =
                            (Number(basePoints[idx + 1]) || 0) - baseStartY;
                          return (relX * cosR - relY * sinR) * scale;
                        }
                        const relX =
                          (Number(basePoints[idx - 1]) || 0) - baseStartX;
                        const relY = (Number(coord) || 0) - baseStartY;
                        return (relX * sinR + relY * cosR) * scale;
                      });
                    } else if (baseLen > 0.001) {
                      // Keep preset geometry for tiny first-drag movement so we
                      // don't flash a straight fallback line for one frame.
                      nextPoints = basePoints.map((coord, idx) =>
                        idx % 2 === 0
                          ? (Number(coord) || 0) - baseStartX
                          : (Number(coord) || 0) - baseStartY
                      );
                    }
                  }
                  const bounded = clampLineGeometryInStage(
                    lineStartPoint.x,
                    lineStartPoint.y,
                    nextPoints,
                    line.strokeWidth || 2
                  );
                  return {
                    ...line,
                    x: bounded.x,
                    y: bounded.y,
                    points: bounded.points,
                    width: Math.abs(nextPoints[2] - nextPoints[0]),
                    height: Math.abs(nextPoints[3] - nextPoints[1]),
                    _deferInitialRender: false,
                  };
                })()
                : line
            );
            return updated;
          });
        } else if (
          isDrawingDoubleArrow &&
          doubleArrowStartPoint &&
          currentShapeId.current
        ) {
          // For double-sided arrows, we want to preserve the original direction
          // Calculate points relative to the start point
          let deltaX = clampedX - doubleArrowStartPoint.x;
          let deltaY = clampedY - doubleArrowStartPoint.y;

          // Apply restrictions to double-sided arrow drawing
          const restrictedProps = applyLiveDrawingRestrictions("arrow", {
            x: doubleArrowStartPoint.x,
            y: doubleArrowStartPoint.y,
            width: Math.abs(deltaX),
            height: Math.abs(deltaY),
          });

          // Recalculate deltas based on restrictions
          const restrictedWidth = restrictedProps.width;

          // Maintain aspect ratio if width was restricted
          if (Math.abs(deltaX) > 0) {
            const scale = restrictedWidth / Math.abs(deltaX);
            deltaX = deltaX * scale;
            deltaY = deltaY * scale;
          }

          setDoubleSidedArrows((arrows) => {
            const updated = arrows.map((arrow) =>
              arrow.id === currentShapeId.current
                ? {
                  ...arrow,
                  x: restrictedProps.x,
                  y: restrictedProps.y,
                  points: [
                    0,
                    0,
                    clampedX - restrictedProps.x,
                    clampedY - restrictedProps.y,
                  ],
                  width: Math.abs(clampedX - restrictedProps.x),
                  height: Math.abs(clampedY - restrictedProps.y),
                }
                : arrow
            );
            return updated;
          });
        }
        break;

      case ACTIONS_SCRIBBLE:
        setScribbles((scribbles) => {
          const updated = scribbles.map((scribble) =>
            scribble.id === currentShapeId.current
              ? {
                ...scribble,
                points: [...scribble.points, clampedX, clampedY],
              }
              : scribble
          );
          return updated;
        });
        break;

      case ACTIONS_LINE:
        // CRITICAL: In clone mode, create unified arrow when dragging starts (first onPointerMove)
        // In clone mode, onPointerDown stores start position but doesn't create the element for drag
        // So we need to create it here when dragging actually starts
        // BUT: Only create if currentLineId is not null (it might be null if onPointerUp already cleared it)
        if (
          isCloneMode &&
          cloneTemplate &&
          (lockedAction === ACTIONS_ARROW ||
            lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
            lockedAction === ACTIONS_LINE) &&
          isDrawingLine &&
          lineStartPoint &&
          currentLineId.current
        ) {
          // Check if this is a unified element
          const hasStartArrow =
            cloneTemplate?.hasStartArrow === true ||
            cloneTemplate?.pointerAtBeginning === true;
          const hasEndArrow =
            cloneTemplate?.hasEndArrow === true ||
            cloneTemplate?.pointerAtEnd === true ||
            cloneTemplate?.pointerAtEnding === true;
          const isUnifiedElement =
            cloneTemplate && (hasStartArrow || hasEndArrow);

          if (
            isUnifiedElement ||
            lockedAction === ACTIONS_LINE ||
            lockedAction === ACTIONS_ARROW ||
            lockedAction === ACTIONS_DOUBLE_SIDED_ARROW
          ) {
            // CRITICAL: Use a functional update with a check to prevent race conditions
            // Multiple onPointerMove events can fire before state updates, so we need to check atomically
            activeSetLines((prev) => {
              // CRITICAL: Double-check currentLineId is still set (might have been cleared by onPointerUp)
              if (!currentLineId.current) {
                return prev;
              }

              const targetId = currentLineId.current;
              const lineExists = prev.some((line) => line.id === targetId);

              if (lineExists) {
                // Line already exists, continue with normal drawing logic below
                return prev;
              }

              // Line doesn't exist - create it now (first movement detected = drag started)
              // CRITICAL: Double-check it still doesn't exist (race condition protection)
              const stillDoesNotExist = !prev.some(
                (line) => line.id === targetId
              );
              if (!stillDoesNotExist) {
                return prev;
              }


              const id = targetId;
              // Generate unique name by checking existing names
              // CRITICAL: For arrows, we need to check BOTH:
              // 1. lines array (unified elements with hasStartArrow/hasEndArrow)
              // 2. arrows array (legacy arrows)
              // 3. doubleSidedArrows array (legacy double-sided arrows)
              const isArrow = cloneTemplate && (
                (cloneTemplate.hasStartArrow === true || cloneTemplate.hasEndArrow === true) ||
                (cloneTemplate.pointerAtBeginning === true || cloneTemplate.pointerAtEnd === true || cloneTemplate.pointerAtEnding === true)
              );

              // Collect names from all sources
              const allArrowNames = [];

              // From lines array (prev) - unified arrows
              if (isArrow) {
                const unifiedArrows = prev.filter((l) =>
                  (l.hasStartArrow === true || l.hasEndArrow === true) ||
                  (l.pointerAtBeginning === true || l.pointerAtEnd === true || l.pointerAtEnding === true)
                );
                allArrowNames.push(...unifiedArrows.map((l) => l.name));
              } else {
                // For non-arrows (lines), just use prev
                allArrowNames.push(...prev.map((l) => l.name));
              }

              // From arrows array (legacy)
              if (isArrow && arrows) {
                allArrowNames.push(...arrows.map((a) => a.name).filter(Boolean));
              }

              // From doubleSidedArrows array (legacy)
              if (isArrow && doubleSidedArrows) {
                allArrowNames.push(...doubleSidedArrows.map((a) => a.name).filter(Boolean));
              }

              const existingNames = new Set(allArrowNames.filter(Boolean));
              const baseName = cloneTemplate?.name || "Stroke 1";


              const newName = generateUniqueNameForClone(baseName, existingNames);

              const restrictedProps = applyLiveDrawingRestrictions("line", {
                x: lineStartPoint.x,
                y: lineStartPoint.y,
                width: 0,
                height: 0,
              });

              // Read arrow properties from template (check both new and legacy)
              const tempHasStartArrow =
                cloneTemplate?.hasStartArrow === true ||
                cloneTemplate?.pointerAtBeginning === true;
              const tempHasEndArrow =
                cloneTemplate?.hasEndArrow === true ||
                cloneTemplate?.pointerAtEnd === true ||
                cloneTemplate?.pointerAtEnding === true;
              const resolvedHasStartArrow =
                tempHasStartArrow ||
                (lockedAction === ACTIONS_DOUBLE_SIDED_ARROW);
              const resolvedHasEndArrow =
                tempHasEndArrow ||
                (lockedAction === ACTIONS_ARROW ||
                  lockedAction === ACTIONS_DOUBLE_SIDED_ARROW);

              // Exclude legacy pointer properties, id, and name from cloneTemplate
              const {
                id: templateId,
                name: templateName,
                pointerAtBeginning,
                pointerAtEnd,
                pointerAtEnding,
                ...cleanTemplate
              } = cloneTemplate;

              const baseProps = {
                ...cleanTemplate,
                x: restrictedProps.x,
                y: restrictedProps.y,
                points: [0, 0, 0, 0], // Use relative coordinates
                fillColor:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  "black",
                fill:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  "black",
                strokeColor:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  "black",
                strokeWidth: cloneTemplate?.strokeWidth || 2,
                stroke:
                  cloneTemplate?.stroke ||
                  cloneTemplate?.strokeColor ||
                  "black",
                hasStartArrow: resolvedHasStartArrow,
                hasEndArrow: resolvedHasEndArrow,
                isNew: true,
              };

              // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
              // Also set id explicitly after spread to ensure it overrides any id from cloneTemplate
              const newLine = {
                id, // Set ID first
                ...restrictedProps,
                ...baseProps,
                name: newName, // Set name AFTER spreading to prevent template name from overwriting
                id, // Explicitly set again after spread to ensure override
                createdOnCanvas: true, // Mark as user-created element
              };

              return [...prev, newLine];
            });
          }
        }

        // Update unified arrows/lines when drawing
        // This handles both ACTIONS_LINE and unified arrows (ACTIONS_ARROW/ACTIONS_DOUBLE_SIDED_ARROW stored in lines array)
        if (
          isDrawingLine &&
          lineStartPoint &&
          currentShapeId.current &&
          (action === ACTIONS_LINE ||
            (isCloneMode &&
              (lockedAction === ACTIONS_ARROW ||
                lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
                lockedAction === ACTIONS_LINE)))
        ) {

          // For lines, we want to preserve the original direction
          // Calculate points relative to the start point
          let deltaX = clampedX - lineStartPoint.x;
          let deltaY = clampedY - lineStartPoint.y;

          // Apply size restrictions only (not position restrictions)
          const restrictedProps = applyLiveDrawingRestrictions("line", {
            width: Math.abs(deltaX),
            height: Math.abs(deltaY),
          });

          // Recalculate deltas based on restrictions
          const restrictedWidth = restrictedProps.width;
          const restrictedHeight = restrictedProps.height;

          // Maintain aspect ratio if width was restricted
          if (Math.abs(deltaX) > 0) {
            const scale = restrictedWidth / Math.abs(deltaX);
            deltaX = deltaX * scale;
            deltaY = deltaY * scale;
          }

          // Use activeSetLines (already defined at top of function) to handle off-canvas
          activeSetLines((lines) => {
            const targetId = currentShapeId.current;
            const lineToUpdate = lines.find((line) => line.id === targetId);
            const updated = lines.map((line) =>
              line.id === targetId
                ? (() => {
                    const targetDx = clampedX - lineStartPoint.x;
                    const targetDy = clampedY - lineStartPoint.y;
                    const basePoints = Array.isArray(line._presetBasePoints)
                      ? line._presetBasePoints
                      : null;
                    const canUsePresetGeometry =
                      basePoints && basePoints.length >= 4;

                    let nextPoints = [0, 0, targetDx, targetDy];
                    if (canUsePresetGeometry) {
                      const baseStartX = Number(basePoints[0]) || 0;
                      const baseStartY = Number(basePoints[1]) || 0;
                      const baseEndX =
                        Number(basePoints[basePoints.length - 2]) || 0;
                      const baseEndY =
                        Number(basePoints[basePoints.length - 1]) || 0;
                      const baseDx = baseEndX - baseStartX;
                      const baseDy = baseEndY - baseStartY;
                      const baseLen = Math.hypot(baseDx, baseDy);
                      const targetLen = Math.hypot(targetDx, targetDy);

                      if (baseLen > 0.001 && targetLen > 0.001) {
                        const baseAngle = Math.atan2(baseDy, baseDx);
                        const targetAngle = Math.atan2(targetDy, targetDx);
                        const rotateBy = targetAngle - baseAngle;
                        const scale = targetLen / baseLen;
                        const cosR = Math.cos(rotateBy);
                        const sinR = Math.sin(rotateBy);

                        nextPoints = basePoints.map((coord, idx) => {
                          if (idx % 2 === 0) {
                            const relX = (Number(coord) || 0) - baseStartX;
                            const relY =
                              (Number(basePoints[idx + 1]) || 0) - baseStartY;
                            return (relX * cosR - relY * sinR) * scale;
                          }
                          const relX =
                            (Number(basePoints[idx - 1]) || 0) - baseStartX;
                          const relY = (Number(coord) || 0) - baseStartY;
                          return (relX * sinR + relY * cosR) * scale;
                        });
                      } else if (baseLen > 0.001) {
                        // Keep preset geometry for tiny first-drag movement so we
                        // don't flash a straight fallback line for one frame.
                        nextPoints = basePoints.map((coord, idx) =>
                          idx % 2 === 0
                            ? (Number(coord) || 0) - baseStartX
                            : (Number(coord) || 0) - baseStartY
                        );
                      }
                    }

                    let minX = Infinity;
                    let minY = Infinity;
                    let maxX = -Infinity;
                    let maxY = -Infinity;
                    for (let i = 0; i < nextPoints.length; i += 2) {
                      minX = Math.min(minX, nextPoints[i]);
                      maxX = Math.max(maxX, nextPoints[i]);
                      minY = Math.min(minY, nextPoints[i + 1]);
                      maxY = Math.max(maxY, nextPoints[i + 1]);
                    }

                    const bounded = clampLineGeometryInStage(
                      lineStartPoint.x,
                      lineStartPoint.y,
                      nextPoints,
                      line.strokeWidth || 2
                    );
                    return {
                      ...line,
                      x: bounded.x,
                      y: bounded.y,
                      points: bounded.points,
                      width: Math.max(0, maxX - minX),
                      height: Math.max(0, maxY - minY),
                      _deferInitialRender: false,
                    };
                  })()
                : line,
            );

            if (lineToUpdate) {
            } else {
            }

            return updated;
          });
        }
        break;
    }
  };
};

/**
 * Handles pointer up events for finalizing shape drawing on canvas
 * @param {Object} params - Parameters object
 */
export const createOnPointerUp = ({
  isPaining,
  isDrawingRect,
  setIsDrawingRect,
  setNewRectStart,
  newRectStart, // Current rectangle start position (state value)
  newRectStartRef = null,
  isDrawingRectRef = null,
  currentRectId,
  setRectangles,
  fillColor,
  setAction,
  saveToHistory,
  action,
  isDrawingCircle,
  setIsDrawingCircle,
  setNewCircleStart,
  newCircleStart, // Current circle start position (state value)
  currentCircleId,
  setCircles,
  isDrawingArrow,
  setIsDrawingArrow,
  setArrowStartPoint,
  arrowStartPoint, // Current arrow start position (state value)
  currentArrowId,
  currentShapeId,
  setArrows,
  isDrawingDoubleArrow,
  setIsDrawingDoubleArrow,
  setDoubleArrowStartPoint,
  currentDoubleArrowId,
  setDoubleSidedArrows,
  isDrawingLine,
  setIsDrawingLine,
  currentLineId,
  stageRef,
  setLines,
  setLineStartPoint,
  lineStartPoint, // Current line start position (state value)
  isDrawingText,
  setIsDrawingText,
  newTextStart,
  setNewTextStart,
  currentTextId,
  setTexts,
  texts,
  setSelectedText,
  setSelectedTextId,
  setShowTextBox,
  setSelectedObject,
  setSelectedObjects,
  setEditTextBoxContent,
  transformerRef,
  ACTIONS_SELECT,
  ACTIONS_RECTANGLE,
  ACTIONS_CIRCLE,
  ACTIONS_ARROW,
  ACTIONS_DOUBLE_SIDED_ARROW,
  ACTIONS_LINE,
  ACTIONS_TEXT,
  ACTIONS_SCRIBBLE,
  ACTIONS_IMAGE,
  pendingSelection,
  lockedAction, // Track which action is locked
  initialPointerPosition, // Ref to track initial pointer position for click detection
  applyDrawingRestrictions, // Function to apply drawing restrictions
  markerCount, // Ref for marker count
  setLocationMarkers, // Setter for location markers
  pageId, // Current page ID
  selectedObject, // Currently selected object (needed to save previous element before creating new one)
  rectangles, // Current rectangles array (needed to save previous element state)
  circles, // Current circles array
  cloneTemplate, // Template object for cloning shapes with exact properties
  cloneTemplates = [], // Array of template objects for cloning multiple elements
  arrows, // Current arrows array
  doubleSidedArrows, // Current double-sided arrows array
  lines, // Current lines array
  isDrawingImage, // Image drawing state
  setIsDrawingImage, // Setter for image drawing state
  imageStartPoint, // Image start point
  setImageStartPoint, // Setter for image start point
  pendingImageData, // Pending image data
  setPendingImageData, // Setter for pending image data
  addImage, // Function to add image to canvas
  setImages, // Setter for images array
  currentImageId, // Ref to track preview image ID
  imageConstraintRef = null, // Ref to track last used constraint for smooth transitions
  previewImageDataRef = null, // Ref to store preview image data for perfect alignment
  isCloneMode = false, // Track if clone mode is active (prevents selection changes)
  // Off-canvas state setters (optional)
  offCanvasStageRef = null, // Ref to off-canvas stage to detect which canvas is active
  setOffCanvasRectangles = null,
  setOffCanvasCircles = null,
  setOffCanvasArrows = null,
  setOffCanvasDoubleSidedArrows = null,
  setOffCanvasLines = null,
  offCanvasLines = null, // Current off-canvas lines array (needed for name generation)
  setOffCanvasTexts = null,
  offCanvasTexts = null, // Current off-canvas texts array (needed for fallback selection)
  setOffCanvasImages = null,
  isCropModalOpen = false, // When crop modal is open, don't change selection (resize end must not close modal)
  isCropModalOpenRef = null, // Ref set synchronously on Crop click (fixes first-time close)
  justFinishedShapeDrawRef = null, // Skip capture-phase/stage deselect after draw end (hollow circle: click hits bg)
  /** Id of the source element when duplicate mode started — avoids using a newly selected clone as template */
  cloneTemplateElementIdRef = null,
}) => {
  return async function onPointerUp(e) {
    isPaining.current = false;

    const lineByIdMainOrOff = (lineId) => {
      if (!lineId) return null;
      return (
        (lines || []).find((l) => l.id === lineId) ||
        (offCanvasLines || []).find((l) => l.id === lineId) ||
        null
      );
    };

    const cloneSourceLineId = cloneTemplateElementIdRef?.current || null;

    const bumpShapeDrawSelectionGuard = () => {
      if (!justFinishedShapeDrawRef) return;
      // Use a short timestamp window to ignore only the synthetic click that
      // fires immediately after draw-end. A long boolean window causes the
      // user's first intentional background click to be ignored (2-click unselect).
      justFinishedShapeDrawRef.current = Date.now();
      setTimeout(() => {
        if (justFinishedShapeDrawRef) justFinishedShapeDrawRef.current = 0;
      }, 60);
    };

    // Guard the synthetic/stage click that often fires right after draw end.
    // This must run for all shape draws (not only specific finalize branches),
    // otherwise Background can immediately clear the freshly selected shape.
    const anyShapeDrawInProgress =
      Boolean(isDrawingRect) ||
      Boolean(isDrawingCircle) ||
      Boolean(isDrawingArrow) ||
      Boolean(isDrawingDoubleArrow) ||
      Boolean(isDrawingLine) ||
      Boolean(isDrawingText) ||
      Boolean(isDrawingImage);
    if (anyShapeDrawInProgress) {
      bumpShapeDrawSelectionGuard();
    }

    // When crop modal is open (state or ref), don't run any logic that could clear selection (e.g. on resize end)
    if (isCropModalOpen || isCropModalOpenRef?.current) {
      if (e?.evt) {
        e.evt.preventDefault?.();
        e.evt.stopPropagation?.();
      }
      return;
    }

    // Deduplicate overlapping pointer-up paths for image draw flow.
    // mouseup + pointerup (and forwarded equivalents) can both arrive and
    // trigger image finalize/click-fallback twice.
    const isImagePointerUpCandidate =
      isDrawingImage &&
      (action === ACTIONS_IMAGE ||
        lockedAction === ACTIONS_IMAGE ||
        (isCloneMode && cloneTemplate && lockedAction === ACTIONS_IMAGE));
    if (isImagePointerUpCandidate) {
      const evtTs = e?.evt?.timeStamp || Date.now();
      const evtX =
        e?.evt?.clientX ??
        e?.evt?.touches?.[0]?.clientX ??
        e?.evt?.changedTouches?.[0]?.clientX ??
        null;
      const evtY =
        e?.evt?.clientY ??
        e?.evt?.touches?.[0]?.clientY ??
        e?.evt?.changedTouches?.[0]?.clientY ??
        null;
      const currentImageKey =
        currentImageId?.current ||
        cloneTemplate?.id ||
        cloneTemplate?.name ||
        pendingImageData?.imageSrc ||
        null;
      const prev = lastImagePointerUpRef;
      if (
        evtTs - (prev.ts || 0) < 180 &&
        prev.imageId === currentImageKey &&
        evtX !== null &&
        evtY !== null &&
        prev.x !== null &&
        prev.y !== null &&
        Math.abs(evtX - prev.x) <= 3 &&
        Math.abs(evtY - prev.y) <= 3
      ) {
        return;
      }
      lastImagePointerUpRef = {
        ts: evtTs,
        x: evtX,
        y: evtY,
        imageId: currentImageKey,
      };
      // Keep legacy clone ref in sync for existing clone-only guard behavior.
      lastCloneImagePointerUpRef = {
        ts: evtTs,
        x: evtX,
        y: evtY,
        imageId: currentImageKey,
      };
    }

    // Helper function to generate unique name by checking existing names
    // Simple approach: count all elements of the same type, add 1
    const generateUniqueNameForClone = (baseName, existingNames) => {

      // Extract base name (e.g., "Rectangle 2" -> "Rectangle", "Arrow 1" -> "Arrow")
      const nameMatch = baseName.match(/^(.+?)\s+\d+$/);
      const base = nameMatch ? nameMatch[1] : baseName;

      // Count all existing names that match this base pattern
      const regexPattern = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+\\d+$`);
      const matchingNames = Array.from(existingNames).filter(name => regexPattern.test(name));
      const count = matchingNames.length;

      // Next number is count + 1
      const nextNumber = count + 1;
      const finalName = `${base} ${nextNumber}`;


      return finalName;
    };

    // Check if this was a click (no significant movement) and tool is locked
    const stage = stageRef.current;

    // Determine which canvas we're drawing on by comparing stage refs
    const isOffCanvas =
      offCanvasStageRef && stage === offCanvasStageRef.current;

    // Deduplicate overlapping pointer-up paths for clone shape draw flows near stage boundaries
    // (e.g. off-canvas draw events that can also bubble through main/top-layer forwarding).
    const isCloneShapeDrawMode =
      isCloneMode &&
      cloneTemplate &&
      lockedAction &&
      lockedAction !== ACTIONS_SELECT &&
      lockedAction !== "PAN";
    if (isCloneShapeDrawMode) {
      const evtTs = e?.evt?.timeStamp || Date.now();
      const evtX =
        e?.evt?.clientX ??
        e?.evt?.touches?.[0]?.clientX ??
        e?.evt?.changedTouches?.[0]?.clientX ??
        null;
      const evtY =
        e?.evt?.clientY ??
        e?.evt?.touches?.[0]?.clientY ??
        e?.evt?.changedTouches?.[0]?.clientY ??
        null;
      const stageKey = isOffCanvas ? "off-canvas" : "main";
      const activeShapeId =
        currentRectId?.current ||
        currentCircleId?.current ||
        currentImageId?.current ||
        currentLineId?.current ||
        currentArrowId?.current ||
        currentDoubleArrowId?.current ||
        cloneTemplate?.id ||
        null;
      const prevShapeUp = lastCloneShapePointerUpRef;
      const hasNearSamePointer =
        evtX !== null &&
        evtY !== null &&
        prevShapeUp.x !== null &&
        prevShapeUp.y !== null &&
        Math.abs(evtX - prevShapeUp.x) <= 3 &&
        Math.abs(evtY - prevShapeUp.y) <= 3;
      const hasSameActiveShape =
        activeShapeId && prevShapeUp.shapeId === activeShapeId;
      if (
        evtTs - (prevShapeUp.ts || 0) < 180 &&
        prevShapeUp.action === lockedAction &&
        (hasNearSamePointer || hasSameActiveShape)
      ) {
        return;
      }
      lastCloneShapePointerUpRef = {
        ts: evtTs,
        x: evtX,
        y: evtY,
        action: lockedAction,
        stage: stageKey,
        shapeId: activeShapeId,
      };
    }

    // Choose the appropriate state setters based on which canvas is active
    const activeSetRectangles =
      isOffCanvas && setOffCanvasRectangles
        ? setOffCanvasRectangles
        : setRectangles;
    const activeSetCircles =
      isOffCanvas && setOffCanvasCircles ? setOffCanvasCircles : setCircles;
    const lockedRectCanvas = currentRectId?.current
      ? cloneDrawCanvasByShapeId.get(currentRectId.current)
      : null;
    const lockedCircleCanvas = currentCircleId?.current
      ? cloneDrawCanvasByShapeId.get(currentCircleId.current)
      : null;
    const isRectOffCanvas =
      lockedRectCanvas === "off-canvas"
        ? true
        : lockedRectCanvas === "main"
          ? false
          : isOffCanvas;
    const isCircleOffCanvas =
      lockedCircleCanvas === "off-canvas"
        ? true
        : lockedCircleCanvas === "main"
          ? false
          : isOffCanvas;
    const activeSetRectanglesForCloneUp =
      lockedRectCanvas === "off-canvas" && setOffCanvasRectangles
        ? setOffCanvasRectangles
        : lockedRectCanvas === "main"
          ? setRectangles
          : activeSetRectangles;
    const activeSetCirclesForCloneUp =
      lockedCircleCanvas === "off-canvas" && setOffCanvasCircles
        ? setOffCanvasCircles
        : lockedCircleCanvas === "main"
          ? setCircles
          : activeSetCircles;
    const activeSetArrows =
      isOffCanvas && setOffCanvasArrows ? setOffCanvasArrows : setArrows;
    const activeSetDoubleSidedArrows =
      isOffCanvas && setOffCanvasDoubleSidedArrows
        ? setOffCanvasDoubleSidedArrows
        : setDoubleSidedArrows;
    const activeSetLines =
      isOffCanvas && setOffCanvasLines ? setOffCanvasLines : setLines;
    const activeSetTexts =
      isOffCanvas && setOffCanvasTexts ? setOffCanvasTexts : setTexts;
    const activeSetImages =
      isOffCanvas && setOffCanvasImages ? setOffCanvasImages : setImages;

    const rectangleDefaultPreset = getDefaultStylePreset(
      "rectangleDefaultStylePreset",
    );
    const circleDefaultPreset = getDefaultStylePreset(
      "circleDefaultStylePreset",
    );
    const unifiedLineArrowDefaultPreset = getDefaultStylePreset(
      "unifiedLineArrowDefaultStylePreset",
    );
    const debugUnifiedPresetCreate =
      typeof window !== "undefined" &&
      window.localStorage?.getItem("debugUnifiedPresetCreate") === "1";
    const stageWidth = stage?.width?.() ?? 0;
    const stageHeight = stage?.height?.() ?? 0;
    const stageClampBufferPx =
      action === ACTIONS_SELECT ||
      action === ACTIONS_RECTANGLE ||
      action === ACTIONS_CIRCLE ||
      action === ACTIONS_ARROW ||
      action === ACTIONS_DOUBLE_SIDED_ARROW ||
      action === ACTIONS_LINE ||
      action === ACTIONS_TEXT ||
      action === ACTIONS_IMAGE ||
      lockedAction === ACTIONS_RECTANGLE ||
      lockedAction === ACTIONS_CIRCLE ||
      lockedAction === ACTIONS_ARROW ||
      lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
      lockedAction === ACTIONS_LINE ||
      lockedAction === ACTIONS_TEXT ||
      lockedAction === ACTIONS_IMAGE
        ? DRAW_END_TRANSFORMER_BUFFER_PX
        : 0;
    const maxClickRadius = getMainCanvasMaxRadiusPx(
      undefined,
      stageHeight > 0
        ? stageHeight
        : getCanvasStageHeightPx(),
    );
    const clampRectTopLeftInsideStage = (x, y, width, height, strokeWidth = 2) => {
      const strokeHalf = Math.max(0, Number(strokeWidth) || 0) / 2;
      const safeWidth = Math.max(0, Number(width) || 0);
      const safeHeight = Math.max(0, Number(height) || 0);
      const maxInsetX = Math.max(0, (stageWidth - safeWidth - strokeHalf * 2) / 2);
      const maxInsetY = Math.max(0, (stageHeight - safeHeight - strokeHalf * 2) / 2);
      const edgeInsetX = Math.min(stageClampBufferPx, maxInsetX);
      const edgeInsetY = Math.min(stageClampBufferPx, maxInsetY);
      const minX = strokeHalf + edgeInsetX;
      const minY = strokeHalf + edgeInsetY;
      const maxX = Math.max(minX, stageWidth - safeWidth - strokeHalf - edgeInsetX);
      const maxY = Math.max(minY, stageHeight - safeHeight - strokeHalf - edgeInsetY);
      return {
        x: Math.max(minX, Math.min(Number(x) || 0, maxX)),
        y: Math.max(minY, Math.min(Number(y) || 0, maxY)),
      };
    };
    const clampCircleCenterInsideStage = (x, y, radius, strokeWidth = 2) => {
      const safeRadius = Math.max(0, Number(radius) || 0);
      const strokeHalf = Math.max(0, Number(strokeWidth) || 0) / 2;
      const maxInsetX = Math.max(0, (stageWidth - safeRadius * 2 - strokeHalf * 2) / 2);
      const maxInsetY = Math.max(0, (stageHeight - safeRadius * 2 - strokeHalf * 2) / 2);
      const edgeInsetX = Math.min(stageClampBufferPx, maxInsetX);
      const edgeInsetY = Math.min(stageClampBufferPx, maxInsetY);
      const minX = safeRadius + strokeHalf + edgeInsetX;
      const minY = safeRadius + strokeHalf + edgeInsetY;
      const maxX = Math.max(minX, stageWidth - safeRadius - strokeHalf - edgeInsetX);
      const maxY = Math.max(minY, stageHeight - safeRadius - strokeHalf - edgeInsetY);
      return {
        x: Math.max(minX, Math.min(Number(x) || 0, maxX)),
        y: Math.max(minY, Math.min(Number(y) || 0, maxY)),
      };
    };
    const clampImageCenterInsideStage = (x, y, width, height, border = 0) => {
      const halfW = Math.max(0, Number(width) || 0) / 2 + border;
      const halfH = Math.max(0, Number(height) || 0) / 2 + border;
      const maxInsetX = Math.max(0, (stageWidth - halfW * 2) / 2);
      const maxInsetY = Math.max(0, (stageHeight - halfH * 2) / 2);
      const edgeInsetX = Math.min(stageClampBufferPx, maxInsetX);
      const edgeInsetY = Math.min(stageClampBufferPx, maxInsetY);
      return {
        x: Math.max(
          halfW + edgeInsetX,
          Math.min(Number(x) || 0, stageWidth - halfW - edgeInsetX),
        ),
        y: Math.max(
          halfH + edgeInsetY,
          Math.min(Number(y) || 0, stageHeight - halfH - edgeInsetY),
        ),
      };
    };

    const clampMainCanvasRectClone = (r) => {
      if (!r || stageWidth <= 0 || stageHeight <= 0) return r;
      
      // Better clone detection: only preserve dimensions for actual clones, not new drawings
      const isLikelyClone = (r._preserveSizeOnClamp === true) && (
        // Must have original dimensions to be considered a clone
        (r.originalWidth && r.originalHeight) && (
          // Clones often have reduced opacity during creation
          (r.opacity < 1) ||
          // Clones have dimensions that match or significantly differ from original
          (Math.abs(r.width - r.originalWidth) < 1 && 
           Math.abs(r.height - r.originalHeight) < 1) ||
          // Main canvas clones often have significant dimension differences from original
          (Math.abs(r.width - r.originalWidth) > 10 || 
           Math.abs(r.height - r.originalHeight) > 10)
        )
      );
      
      if (!isOffCanvas) {
        // Main-stage draw-end: if rect is larger than stage, shrink first, then clamp position.
        const strokeHalf = Math.max(0, Number(r.strokeWidth ?? 2) || 0) / 2;
        // Shrink against buffered bounds so the rect stays fully visible
        // (including transformer/anchor buffer inset).
        const edgePad = isLikelyClone
          ? stageClampBufferPx
          : stageClampBufferPx; // For new drawings, use just the buffer to achieve exact 700x816
        const maxWidth = Math.max(1, stageWidth - edgePad * 2);
        const maxHeight = Math.max(1, stageHeight - edgePad * 2);
        
        let safeRect;
        if (isLikelyClone) {
          // For clones, preserve original dimensions - don't scale to fit stage
          console.log("[RECT-CLAMP][MAIN] Preserving clone dimensions, skipping scaling");
          safeRect = {
            ...r,
            width: Number(r.width) || 0,
            height: Number(r.height) || 0,
          };
        } else {
          // Apply scaling for non-clones to fit stage
          safeRect = {
            ...r,
            width: Math.max(1, Math.min(Number(r.width) || 0, maxWidth)),
            height: Math.max(1, Math.min(Number(r.height) || 0, maxHeight)),
          };
        }
        
        console.log("[RECT-CLAMP][MAIN]", {
          id: r?.id,
          preserveSizeOnClamp: r._preserveSizeOnClamp,
          isLikelyClone: isLikelyClone,
          inputWidth: r?.width,
          inputHeight: r?.height,
          strokeWidth: r?.strokeWidth,
          stageWidth,
          stageHeight,
          edgePad,
          maxWidth,
          maxHeight,
          safeWidth: safeRect.width,
          safeHeight: safeRect.height,
        });
        
        const clampedRect = clampRectangleTopLeftToStageBounds(
          safeRect,
          stageWidth,
          stageHeight,
          r.strokeWidth ?? 2,
          stageClampBufferPx,
        );
        const strictMinNodeX = stageClampBufferPx + strokeHalf;
        const strictMinNodeY = stageClampBufferPx + strokeHalf;
        return {
          ...clampedRect,
          x: Math.max(strictMinNodeX, Number(clampedRect?.x) || 0),
          y: Math.max(strictMinNodeY, Number(clampedRect?.y) || 0),
        };
      }
      const strokeHalf = Math.max(0, Number(r.strokeWidth ?? 2) || 0) / 2;
      // Off-canvas target behavior:
      // - preserve-size mode should still keep the 10px side inset (max width ~700 on 720 strip),
      //   but avoid the extra stroke-half shrink that caused 698.
      // - normal mode keeps previous tighter clamp.
      const edgePad = isLikelyClone
        ? stageClampBufferPx
        : stageClampBufferPx; // For new drawings, use just the buffer to achieve exact 700x816
      // Force draw-end off-canvas placement into the left visible strip only.
      const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
      const maxStripWidth = Math.max(1, Math.max(0, mainZoneStart - edgePad * 2));
      const maxRectHeight = Math.max(1, stageHeight - edgePad * 2);
      
      let safeWidth, safeHeight;
      if (isLikelyClone) {
        // For clones, preserve original dimensions - don't scale to fit off-canvas strip
        console.log("[RECT-CLAMP][OFF] Preserving clone dimensions, skipping scaling");
        safeWidth = Math.max(1, Number(r.width) || 0);
        safeHeight = Math.max(1, Number(r.height) || 0);
      } else {
        // Apply scaling for non-clones to fit off-canvas strip
        safeWidth = Math.max(
          1,
          Math.min(Number(r.width) || 0, maxStripWidth),
        );
        safeHeight = Math.max(
          1,
          Math.min(Number(r.height) || 0, maxRectHeight),
        );
      }
      const strictMinNodeX = edgePad + strokeHalf;
      const strictMinNodeY = edgePad + strokeHalf;
      
      // Keep off-canvas draw-end node origin consistent with main stage behavior.
      // If strict min origin would overflow the strip with current width, shrink width first.
      // But only apply this for non-clones - preserve dimensions for clones
      if (!isLikelyClone) {
        const maxWidthAtStrictMin = Math.max(
          1,
          mainZoneStart - edgePad - strictMinNodeX + 1,
        );
        if (safeWidth > maxWidthAtStrictMin) {
          safeWidth = maxWidthAtStrictMin;
        }
        // For new drawings, use just the buffer to achieve exact 816 height
        const maxHeightAtStrictMin = Math.max(
          1,
          stageHeight - edgePad * 2, // Use edgePad * 2 instead of edgePad + strictMinNodeY
        );
        if (safeHeight > maxHeightAtStrictMin) {
          safeHeight = maxHeightAtStrictMin;
        }
      }
      const clampedRect = {
        ...r,
        x: Math.max(
          strictMinNodeX,
          Math.min(
            Number(r.x) || 0,
            Math.max(strictMinNodeX, mainZoneStart - edgePad - safeWidth + 1),
          ),
        ),
        y: Math.max(
          strictMinNodeY,
          Math.min(
            stageHeight - safeHeight - edgePad,
            Number(r.y) || 0,
          ),
        ),
        width: safeWidth,
        height: safeHeight,
      };
      console.log("[DRAW-END-CLAMP][OFFCANVAS][RECT]", {
        id: r?.id,
        input: { x: r?.x, y: r?.y, width: r?.width, height: r?.height, strokeWidth: r?.strokeWidth },
        bounds: { stageWidth, stageHeight, mainZoneStart, maxStripWidth },
        output: { x: clampedRect.x, y: clampedRect.y, width: clampedRect.width, height: clampedRect.height },
      });
      
      console.log("[RECT-CLAMP][EXIT]", {
        id: r?.id,
        preserveSizeOnClamp: r._preserveSizeOnClamp,
        isLikelyClone: isLikelyClone,
        inputWidth: r?.width,
        inputHeight: r?.height,
        outputWidth: clampedRect.width,
        outputHeight: clampedRect.height,
        heightChanged: (r?.height) !== clampedRect.height,
        isOffCanvas: isOffCanvas
      });
      
      return clampedRect;
    };

    const clampMainCanvasCircleClone = (c) => {
      if (!c || stageWidth <= 0 || stageHeight <= 0) return c;
      const rawRadius = Math.max(
        0,
        Number(
          c.radius ??
            Math.max(Number(c.radiusX) || 0, Number(c.radiusY) || 0),
        ) || 0,
      );
      // Main-stage draw-end: use MAX_RADIUS (350) to match CircleElement behavior
      if (!isOffCanvas) {
        const strokeHalf = Math.max(0, Number(c.strokeWidth ?? 2) || 0) / 2;
        const edgePad = strokeHalf + stageClampBufferPx;
        const maxRadiusByWidth = Math.max(0, (stageWidth - edgePad * 2) / 2);
        const maxRadiusByHeight = Math.max(0, (stageHeight - edgePad * 2) / 2);
        // Use MAX_RADIUS (350) for radiusX but stage boundary for radiusY to match CircleElement behavior
        const MAX_RADIUS = 350;
        const safeRadius = Math.min(rawRadius, MAX_RADIUS);
        // Calculate stage boundary for radiusY
        const stageBoundaryY = Math.max(0, (stageHeight - (strokeHalf + stageClampBufferPx) * 2) / 2);
        
        // Clamp radii first
        const clampedRadiusX = Number.isFinite(c.radiusX) 
          ? Math.min(Number(c.radiusX) || 0, MAX_RADIUS)
          : safeRadius;
        const clampedRadiusY = Number.isFinite(c.radiusY)
          ? Math.min(Number(c.radiusY) || 0, stageBoundaryY)
          : safeRadius;
        
        // For ellipses, clamp position based on the actual radii
        const isEllipse = Number.isFinite(c.radiusX) || Number.isFinite(c.radiusY);
        let clampedCenter;
        if (isEllipse) {
          // Clamp position for ellipse based on its actual radii
          const maxInsetX = Math.max(0, (stageWidth - clampedRadiusX * 2 - strokeHalf * 2) / 2);
          const maxInsetY = Math.max(0, (stageHeight - clampedRadiusY * 2 - strokeHalf * 2) / 2);
          const edgeInsetX = Math.min(stageClampBufferPx, maxInsetX);
          const edgeInsetY = Math.min(stageClampBufferPx, maxInsetY);
          
          clampedCenter = {
            x: Math.max(
              clampedRadiusX + strokeHalf + edgeInsetX,
              Math.min(Number(c.x) || 0, stageWidth - clampedRadiusX - strokeHalf - edgeInsetX)
            ),
            y: Math.max(
              clampedRadiusY + strokeHalf + edgeInsetY,
              Math.min(Number(c.y) || 0, stageHeight - clampedRadiusY - strokeHalf - edgeInsetY)
            )
          };
        } else {
          // For circles, use the existing clamping logic
          clampedCenter = clampCircleCenterInsideStage(
            Number(c.x) || 0,
            Number(c.y) || 0,
            safeRadius,
            c.strokeWidth ?? 2,
          );
        }
        
        return {
          ...c,
          x: clampedCenter.x,
          y: clampedCenter.y,
          radius: safeRadius,
          ...(Number.isFinite(c.radiusX)
            ? { radiusX: clampedRadiusX }
            : {}),
          ...(Number.isFinite(c.radiusY)
            ? { radiusY: clampedRadiusY }
            : {}),
        };
      }

      // Off-canvas draw end: use MAX_RADIUS (350) to match CircleElement behavior
      const strokeHalf = Math.max(0, Number(c.strokeWidth ?? 2) || 0) / 2;
      const edgePad = strokeHalf + stageClampBufferPx;
      const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
      const maxStripWidth = Math.max(
        1,
        Math.max(
          0,
          mainZoneStart - edgePad * 2,
          stageWidth - mainZoneEnd - edgePad * 2,
        ),
      );
      const maxRadiusByStrip = Math.max(0, maxStripWidth / 2);
      const maxRadiusByHeight = Math.max(0, (stageHeight - edgePad * 2) / 2);
      // Use MAX_RADIUS (350) for radiusX but stage boundary for radiusY to match CircleElement behavior
      const MAX_RADIUS = 350;
      const radius = Math.min(rawRadius, MAX_RADIUS);
      // Calculate stage boundary for radiusY in off-canvas
      const stageBoundaryY = Math.max(0, (stageHeight - edgePad * 2) / 2);
      const radiusY = Math.min(Number(c.radiusY) || radius, stageBoundaryY);

      const minY = edgePad + radiusY;
      const maxY = Math.max(minY, stageHeight - edgePad - radiusY);
      const clampedY = Math.max(minY, Math.min(Number(c.y) || 0, maxY));

      // For ellipses, use clampedRadiusX for X position clamping
      const isEllipse = Number.isFinite(c.radiusX) || Number.isFinite(c.radiusY);
      const clampedRadiusX = isEllipse && Number.isFinite(c.radiusX) 
        ? Math.min(Number(c.radiusX) || 0, MAX_RADIUS)
        : radius;
      
      const mid = (mainZoneStart + mainZoneEnd) / 2;
      const preferLeft = Number(c.x) < mid;
      const leftMin = edgePad + clampedRadiusX;
      const leftMax = Math.max(leftMin, mainZoneStart - edgePad - clampedRadiusX);
      const rightMin = Math.min(
        stageWidth - edgePad - clampedRadiusX,
        mainZoneEnd + edgePad + clampedRadiusX,
      );
      const rightMax = stageWidth - edgePad - clampedRadiusX;
      const hasLeft = leftMax >= leftMin;
      const hasRight = rightMax >= rightMin;

      let clampedX = Number(c.x) || 0;
      if (hasLeft && hasRight) {
        if (preferLeft) {
          clampedX = Math.max(leftMin, Math.min(clampedX, leftMax));
        } else {
          clampedX = Math.max(rightMin, Math.min(clampedX, rightMax));
        }
      } else if (hasLeft) {
        clampedX = Math.max(leftMin, Math.min(clampedX, leftMax));
      } else if (hasRight) {
        clampedX = Math.max(rightMin, Math.min(clampedX, rightMax));
      }

      return {
        ...c,
        x: clampedX,
        y: clampedY,
        radius,
        ...(Number.isFinite(c.radiusX)
          ? { radiusX: Math.min(Number(c.radiusX) || 0, MAX_RADIUS) }
          : {}),
        ...(Number.isFinite(c.radiusY)
          ? { radiusY: Math.min(Number(c.radiusY) || 0, stageBoundaryY) }
          : {}),
      };
    };

    const clampMainCanvasPolylineClone = (lineLike) =>
      stageWidth > 0 &&
      stageHeight > 0 &&
      Array.isArray(lineLike?.points) &&
      lineLike.points.length >= 4
        ? (() => {
            const preserveSizeOnClamp =
              lineLike?._preserveSizeOnClamp === true ||
              lineLike?.isNew === false;
            const inputDebug = {
              id: lineLike?.id,
              x: Number(lineLike?.x) || 0,
              y: Number(lineLike?.y) || 0,
              strokeWidth: Number(lineLike?.strokeWidth) || 0,
              pointsLen: Array.isArray(lineLike?.points) ? lineLike.points.length : 0,
            };
            const stageClamped = clampPolylineGroupToStageBounds(
              lineLike,
              stageWidth,
              stageHeight,
              preserveSizeOnClamp ? 0 : (lineLike.strokeWidth ?? 2),
              stageClampBufferPx,
            );
            if (!isOffCanvas) {
              // Main-stage: if the line's point spread is larger than the stage capacity,
              // shrink the points (scale) instead of only shifting x/y.
              const strokeHalf = Math.max(
                0,
                Number(lineLike.strokeWidth ?? 2) || 0,
              ) / 2;
              const edgePad = preserveSizeOnClamp
                ? stageClampBufferPx
                : strokeHalf + stageClampBufferPx;

              const pts = Array.isArray(stageClamped?.points)
                ? stageClamped.points
                : [];
              const baseX = Number(stageClamped?.x) || 0;
              const baseY = Number(stageClamped?.y) || 0;

              let minX = Infinity;
              let maxX = -Infinity;
              let minY = Infinity;
              let maxY = -Infinity;
              for (let i = 0; i < pts.length; i += 2) {
                const px = baseX + (Number(pts[i]) || 0);
                const py = baseY + (Number(pts[i + 1]) || 0);
                minX = Math.min(minX, px);
                maxX = Math.max(maxX, px);
                minY = Math.min(minY, py);
                maxY = Math.max(maxY, py);
              }

              const currentWidth = Math.max(0, maxX - minX);
              const currentHeight = Math.max(0, maxY - minY);
              const availableWidth = Math.max(0, stageWidth - edgePad * 2);
              const availableHeight = Math.max(0, stageHeight - edgePad * 2);

              const widthScale =
                currentWidth > 0 ? availableWidth / currentWidth : 1;
              const heightScale =
                currentHeight > 0 ? availableHeight / currentHeight : 1;
              const scale = Math.max(
                0.0001,
                Math.min(1, widthScale, heightScale),
              );

              if (scale < 0.9999 && pts.length >= 4) {
                const anchorX = Number(pts[0]) || 0;
                const anchorY = Number(pts[1]) || 0;
                const scaledPoints = pts.map((coord, idx) => {
                  const val = Number(coord) || 0;
                  if (idx % 2 === 0) {
                    return anchorX + (val - anchorX) * scale;
                  }
                  return anchorY + (val - anchorY) * scale;
                });
                const scaledItem = {
                  ...stageClamped,
                  points: scaledPoints,
                };
                const finalMain = clampPolylineGroupToStageBounds(
                  scaledItem,
                  stageWidth,
                  stageHeight,
                  preserveSizeOnClamp ? 0 : (lineLike.strokeWidth ?? 2),
                  stageClampBufferPx,
                );
                console.log("[DRAW-END-CLAMP][MAIN][LINE]", {
                  stage: { stageWidth, stageHeight, stageClampBufferPx },
                  input: inputDebug,
                  output: {
                    x: Number(finalMain?.x) || 0,
                    y: Number(finalMain?.y) || 0,
                    pointsLen: Array.isArray(finalMain?.points)
                      ? finalMain.points.length
                      : 0,
                  },
                  scaled: { scale },
                });
                return finalMain;
              }

              console.log("[DRAW-END-CLAMP][MAIN][LINE]", {
                stage: { stageWidth, stageHeight, stageClampBufferPx },
                input: inputDebug,
                output: {
                  x: Number(stageClamped?.x) || 0,
                  y: Number(stageClamped?.y) || 0,
                  pointsLen: Array.isArray(stageClamped?.points)
                    ? stageClamped.points.length
                    : 0,
                },
                scaled: { scale: 1 },
              });
              return stageClamped;
            }
            const elementType = getUnifiedLineArrowSelectionType(stageClamped);
            const strokeHalf = Math.max(
              0,
              Number(lineLike.strokeWidth ?? 2) || 0,
            ) / 2;
            const edgePad = preserveSizeOnClamp
              ? stageClampBufferPx
              : strokeHalf + stageClampBufferPx;
            const shifted = applyOffCanvasMainColumnAvoidanceX(
              stageClamped,
              elementType,
              Number(stageClamped?.x) || 0,
              Number(stageClamped?.y) || 0,
              stageWidth,
              edgePad,
            );
            let offCanvasClamped = clampPolylineGroupToStageBounds(
              {
                ...stageClamped,
                x: shifted.x,
                y: shifted.y,
              },
              stageWidth,
              stageHeight,
              preserveSizeOnClamp ? 0 : (lineLike.strokeWidth ?? 2),
              stageClampBufferPx,
            );
            const pts = Array.isArray(offCanvasClamped?.points)
              ? offCanvasClamped.points
              : [];
            if (pts.length >= 4) {
              const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
              const maxStripWidth = Math.max(
                1,
                Math.max(
                  0,
                  mainZoneStart - edgePad * 2,
                ),
              );
              const maxHeight = Math.max(1, stageHeight - edgePad * 2);

              let minAbsX = Infinity;
              let maxAbsX = -Infinity;
              let minAbsY = Infinity;
              let maxAbsY = -Infinity;
              for (let i = 0; i < pts.length; i += 2) {
                const absX =
                  (Number(offCanvasClamped.x) || 0) + (Number(pts[i]) || 0);
                const absY =
                  (Number(offCanvasClamped.y) || 0) + (Number(pts[i + 1]) || 0);
                minAbsX = Math.min(minAbsX, absX);
                maxAbsX = Math.max(maxAbsX, absX);
                minAbsY = Math.min(minAbsY, absY);
                maxAbsY = Math.max(maxAbsY, absY);
              }
              const currentWidth = Math.max(0, maxAbsX - minAbsX);
              const currentHeight = Math.max(0, maxAbsY - minAbsY);
              const widthScale =
                currentWidth > 0 ? Math.min(1, maxStripWidth / currentWidth) : 1;
              const heightScale =
                currentHeight > 0 ? Math.min(1, maxHeight / currentHeight) : 1;
              const scale = Math.max(0.0001, Math.min(widthScale, heightScale));

              if (scale < 0.9999) {
                const anchorX = Number(pts[0]) || 0;
                const anchorY = Number(pts[1]) || 0;
                const scaledPoints = pts.map((coord, idx) => {
                  const val = Number(coord) || 0;
                  if (idx % 2 === 0) {
                    return anchorX + (val - anchorX) * scale;
                  }
                  return anchorY + (val - anchorY) * scale;
                });
                const scaled = {
                  ...offCanvasClamped,
                  points: scaledPoints,
                };
                const scaledShifted = applyOffCanvasMainColumnAvoidanceX(
                  scaled,
                  elementType,
                  Number(scaled?.x) || 0,
                  Number(scaled?.y) || 0,
                  stageWidth,
                  edgePad,
                );
                offCanvasClamped = clampPolylineGroupToStageBounds(
                  {
                    ...scaled,
                    x: scaledShifted.x,
                    y: scaledShifted.y,
                  },
                  stageWidth,
                  stageHeight,
                  preserveSizeOnClamp ? 0 : (lineLike.strokeWidth ?? 2),
                  stageClampBufferPx,
                );
              }
            }
            // Force final draw-end into the left visible strip only.
            // Clamp by absolute point extents so the shape bbox never enters
            // the off-canvas main overlay column.
            {
              const { mainZoneStart: leftMainZoneStart } =
                getOffCanvasMainColumnBounds(stageWidth);
              const pts = Array.isArray(offCanvasClamped?.points)
                ? offCanvasClamped.points
                : [];
              let minAbsX = Infinity;
              let maxAbsX = -Infinity;
              const baseX = Number(offCanvasClamped?.x) || 0;
              for (let i = 0; i < pts.length; i += 2) {
                const absX = baseX + (Number(pts[i]) || 0);
                minAbsX = Math.min(minAbsX, absX);
                maxAbsX = Math.max(maxAbsX, absX);
              }
              const leftMinAbs = edgePad;
              const leftMaxAbs = leftMainZoneStart - edgePad;
              let shiftX = 0;
              if (minAbsX < leftMinAbs) shiftX = leftMinAbs - minAbsX;
              else if (maxAbsX > leftMaxAbs) shiftX = leftMaxAbs - maxAbsX;
              if (shiftX !== 0) {
                offCanvasClamped = {
                  ...offCanvasClamped,
                  x: (Number(offCanvasClamped?.x) || 0) + shiftX,
                };
              }
            }
            console.log("[DRAW-END-CLAMP][OFFCANVAS][LINE]", {
              id: lineLike?.id,
              input: {
                x: lineLike?.x,
                y: lineLike?.y,
                pointsLen: Array.isArray(lineLike?.points) ? lineLike.points.length : 0,
                strokeWidth: lineLike?.strokeWidth,
              },
              output: {
                x: offCanvasClamped?.x,
                y: offCanvasClamped?.y,
                pointsLen: Array.isArray(offCanvasClamped?.points)
                  ? offCanvasClamped.points.length
                  : 0,
              },
            });
            return offCanvasClamped;
          })()
        : lineLike;

    const clampMainCanvasImageClone = (imgLike) => {
      if (stageWidth <= 0 || stageHeight <= 0 || !imgLike) {
        return imgLike;
      }
      const width = Math.max(0, Number(imgLike.width) || 0);
      const height = Math.max(0, Number(imgLike.height) || 0);
      const strokeHalf = Math.max(0, Number(imgLike.strokeWidth) || 0) / 2;

      // DEBUG: Log to identify if this is a clone being processed
      // Better clone detection: check if this image has originalWidth/originalHeight that match current dimensions
      // and if it was recently created (has opacity < 1 or isNew flag)
      // Also check for significant dimension differences which indicate cloning
      const isLikelyClone = (imgLike.isNew === true) || 
        (imgLike.opacity < 1) ||
        (imgLike.originalWidth && imgLike.originalHeight && 
         Math.abs(imgLike.width - imgLike.originalWidth) < 1 && 
         Math.abs(imgLike.height - imgLike.originalHeight) < 1) ||
        // Main canvas clones often have significant dimension differences from original
        (imgLike.originalWidth && imgLike.originalHeight &&
         (Math.abs(imgLike.width - imgLike.originalWidth) > 10 || 
          Math.abs(imgLike.height - imgLike.originalHeight) > 10));
      
      console.log("DEBUG: clampMainCanvasImageClone processing", {
        id: imgLike.id,
        isClone: isLikelyClone,
        isNew: imgLike.isNew,
        opacity: imgLike.opacity,
        width: width,
        height: height,
        originalWidth: imgLike.originalWidth,
        originalHeight: imgLike.originalHeight,
        widthDiff: imgLike.originalWidth ? Math.abs(imgLike.width - imgLike.originalWidth) : 'N/A',
        heightDiff: imgLike.originalHeight ? Math.abs(imgLike.height - imgLike.originalHeight) : 'N/A'
      });

      const clampImageSizePreserveAspect = (w, h, maxW, maxH) => {
        const safeW = Math.max(0, Number(w) || 0);
        const safeH = Math.max(0, Number(h) || 0);
        const capW = Math.max(0, Number(maxW) || 0);
        const capH = Math.max(0, Number(maxH) || 0);
        if (safeW <= 0 || safeH <= 0) return { w: safeW, h: safeH };
        if (capW <= 0 || capH <= 0) return { w: 0, h: 0 };
        if (safeW <= capW && safeH <= capH) return { w: safeW, h: safeH };
        const scaleW = capW / safeW;
        const scaleH = capH / safeH;
        const scale = Math.min(scaleW, scaleH);
        return { w: safeW * scale, h: safeH * scale };
      };

      if (isOffCanvas) {
        // Force draw-end off-canvas placement into the left visible strip only.
        const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
        const edgePadForStrip = strokeHalf + stageClampBufferPx;
        const maxStripWidth = Math.max(
          1,
          Math.max(0, mainZoneStart - edgePadForStrip * 2),
        );
        const maxHeight = Math.max(1, stageHeight - edgePadForStrip * 2);
        let safeWidth = width;
        let safeHeight = height;
        
        // For clones, preserve original dimensions - don't scale to fit off-canvas strip
        if (isLikelyClone) {
          // This is a clone being created - preserve original dimensions
          console.log("DEBUG: Preserving clone dimensions in off-canvas, skipping scaling");
          // Don't modify safeWidth and safeHeight for clones
        } else {
          // Apply scaling for non-clones to fit off-canvas strip
          if (safeWidth > maxStripWidth && safeWidth > 0) {
            const scale = maxStripWidth / safeWidth;
            safeWidth = maxStripWidth;
            safeHeight = Math.max(1, safeHeight * scale);
          }
          if (safeHeight > maxHeight && safeHeight > 0) {
            const scale = maxHeight / safeHeight;
            safeHeight = maxHeight;
            safeWidth = Math.max(1, safeWidth * scale);
          }
          console.log("DEBUG: Scaled dimensions for non-clone in off-canvas", {
            originalWidth: width,
            originalHeight: height,
            scaledWidth: safeWidth,
            scaledHeight: safeHeight
          });
        }

        // Define edgePad for both clones and non-clones
        const edgePad = strokeHalf + stageClampBufferPx;
        
        // For clones, preserve original dimensions - don't scale to fit stage
        if (isLikelyClone) {
          // This is a clone being created - preserve original dimensions
          console.log("DEBUG: Preserving clone dimensions, skipping scaling");
          // Don't modify safeWidth and safeHeight for clones
        } else {
          // Respect transformer buffer inset in sizing, but preserve aspect ratio.
          const maxFitW = Math.max(0, mainZoneStart - edgePad * 2);
          const maxFitH = Math.max(0, stageHeight - edgePad * 2);
          const scaledToFit = clampImageSizePreserveAspect(
            safeWidth,
            safeHeight,
            maxFitW,
            maxFitH,
          );
          safeWidth = scaledToFit.w;
          safeHeight = scaledToFit.h;
          console.log("DEBUG: Scaled dimensions for non-clone", {
            originalWidth: width,
            originalHeight: height,
            scaledWidth: safeWidth,
            scaledHeight: safeHeight
          });
        }

        if (imgLike.useTopLeftAnchor === true) {
          // For clones, preserve original position - don't clamp to stage bounds
          if (isLikelyClone) {
            console.log("DEBUG: Preserving clone position in top-left anchor, skipping clamping");
            const clampedImageTopLeft = {
              ...imgLike,
              width: safeWidth,
              height: safeHeight,
            };
            console.log("[DRAW-END-CLAMP][OFFCANVAS][IMAGE][TOPLEFT]", {
              id: imgLike?.id,
              input: { x: imgLike?.x, y: imgLike?.y, width: imgLike?.width, height: imgLike?.height },
              output: { x: clampedImageTopLeft.x, y: clampedImageTopLeft.y, width: clampedImageTopLeft.width, height: clampedImageTopLeft.height },
            });
            return clampedImageTopLeft;
          } else {
            const edgePad = strokeHalf + stageClampBufferPx;
            const minY = edgePad;
            const maxY = Math.max(minY, stageHeight - safeHeight - edgePad);
            const clampedImageTopLeft = {
              ...imgLike,
              x: Math.max(
                edgePad,
                Math.min(
                  Number(imgLike.x) || 0,
                  Math.max(edgePad, mainZoneStart - edgePad - safeWidth),
                ),
              ),
              y: Math.max(minY, Math.min(Number(imgLike.y) || 0, maxY)),
              width: safeWidth,
              height: safeHeight,
            };
            console.log("[DRAW-END-CLAMP][OFFCANVAS][IMAGE][TOPLEFT]", {
              id: imgLike?.id,
              input: { x: imgLike?.x, y: imgLike?.y, width: imgLike?.width, height: imgLike?.height },
              output: { x: clampedImageTopLeft.x, y: clampedImageTopLeft.y, width: clampedImageTopLeft.width, height: clampedImageTopLeft.height },
            });
            return clampedImageTopLeft;
          }
        }
        // For clones, preserve dimensions but apply rotation-aware position constraints for off-canvas
        if (isLikelyClone) {
          console.log("DEBUG: Preserving clone dimensions with rotation-aware position constraints for off-canvas");
          
          // Apply gentle position constraints to keep clone within visible off-canvas area
          const originalX = Number(imgLike.x) || 0;
          const originalY = Number(imgLike.y) || 0;
          const rotation = Number(imgLike.rotation) || 0;
          
          // Calculate rotation-aware bounding box
          const theta = (rotation * Math.PI) / 180;
          const absCos = Math.abs(Math.cos(theta));
          const absSin = Math.abs(Math.sin(theta));
          
          // For rotated images, calculate the actual bounding box dimensions
          const rotatedWidth = safeWidth * absCos + safeHeight * absSin;
          const rotatedHeight = safeWidth * absSin + safeHeight * absCos;
          
          // For off-canvas, constrain X to be within the left strip using rotated dimensions
          // More strict left boundary to prevent going outside canvas
          const minX = rotatedWidth / 2 + edgePad;
          const constrainedX = Math.max(minX, Math.min(originalX, mainZoneStart - rotatedWidth / 2));
          
          // For Y, apply minimal constraints using rotated dimensions to keep it visible
          const minY = rotatedHeight / 2 + edgePad;
          const maxY = stageHeight - rotatedHeight / 2 - edgePad;
          const constrainedY = Math.max(minY, Math.min(originalY, maxY));
          
          const clampedImageCentered = {
            ...imgLike,
            x: constrainedX,
            y: constrainedY,
            width: safeWidth,
            height: safeHeight,
          };
          
          console.log("[DRAW-END-CLAMP][OFFCANVAS][IMAGE][CENTER]", {
            id: imgLike?.id,
            input: { x: imgLike?.x, y: imgLike?.y, width: imgLike?.width, height: imgLike?.height, rotation: rotation },
            output: { x: clampedImageCentered.x, y: clampedImageCentered.y, width: clampedImageCentered.width, height: clampedImageCentered.height },
            rotatedBounds: { width: rotatedWidth, height: rotatedHeight },
            positionConstrained: originalX !== constrainedX || originalY !== constrainedY
          });
          return clampedImageCentered;
        } else {
          const halfW = safeWidth / 2;
          const halfH = safeHeight / 2;
          const rawTopLeftX = (Number(imgLike.x) || 0) - halfW;
          const clampedTopLeftX = Math.max(
            edgePad,
            Math.min(
              rawTopLeftX,
              Math.max(edgePad, mainZoneStart - edgePad - safeWidth),
            ),
          );
          const minY = halfH + edgePad;
          const maxY = Math.max(minY, stageHeight - halfH - edgePad);
          const clampedImageCentered = {
            ...imgLike,
            x: clampedTopLeftX + halfW,
            y: Math.max(minY, Math.min(Number(imgLike.y) || 0, maxY)),
            width: safeWidth,
            height: safeHeight,
          };
          console.log("[DRAW-END-CLAMP][OFFCANVAS][IMAGE][CENTER]", {
            id: imgLike?.id,
            input: { x: imgLike?.x, y: imgLike?.y, width: imgLike?.width, height: imgLike?.height },
            output: { x: clampedImageCentered.x, y: clampedImageCentered.y, width: clampedImageCentered.width, height: clampedImageCentered.height },
          });
          return clampedImageCentered;
        }
      }
      // For clones, preserve original dimensions - don't scale to fit stage
      console.log("DEBUG: Main canvas clone preservation check", {
        isLikelyClone: isLikelyClone,
        isOffCanvas: isOffCanvas,
        imgLikeId: imgLike?.id,
        width: width,
        height: height
      });
      
      if (isLikelyClone) {
        console.log("DEBUG: Preserving clone dimensions in main canvas, skipping scaling");
        // Don't modify dimensions for clones, just clamp position
        if (imgLike.useTopLeftAnchor === true) {
          const rectLike = { ...imgLike, width: width, height: height };
          return clampRectangleTopLeftToStageBounds(
            rectLike,
            stageWidth,
            stageHeight,
            imgLike.strokeWidth ?? 2,
            stageClampBufferPx,
          );
        } else {
          const edgePad = strokeHalf + stageClampBufferPx;
          const originalX = Number(imgLike.x) || 0;
          const originalY = Number(imgLike.y) || 0;
          const rotation = Number(imgLike.rotation) || 0;
          // Match off-canvas clone behavior: clamp by rendered rotated bounds,
          // not unrotated width/height. This avoids loose/incorrect edge behavior.
          const theta = (rotation * Math.PI) / 180;
          const absCos = Math.abs(Math.cos(theta));
          const absSin = Math.abs(Math.sin(theta));
          const rotatedWidth = width * absCos + height * absSin;
          const rotatedHeight = width * absSin + height * absCos;
          const minCenterX = rotatedWidth / 2 + edgePad;
          const maxCenterX = Math.max(minCenterX, stageWidth - rotatedWidth / 2 - edgePad);
          const minCenterY = rotatedHeight / 2 + edgePad;
          const maxCenterY = Math.max(
            minCenterY,
            stageHeight - rotatedHeight / 2 - edgePad,
          );
          const constrainedX = Math.max(minCenterX, Math.min(originalX, maxCenterX));
          const constrainedY = Math.max(minCenterY, Math.min(originalY, maxCenterY));
          return {
            ...imgLike,
            width: width,
            height: height,
            x: constrainedX,
            y: constrainedY,
          };
        }
      }

      if (imgLike.useTopLeftAnchor === true) {
        // Main-stage draw-end: shrink to stage capacity, preserving aspect ratio.
        const edgePad = strokeHalf + stageClampBufferPx;
        const maxFitW = Math.max(0, stageWidth - edgePad * 2);
        const maxFitH = Math.max(0, stageHeight - edgePad * 2);
        const scaled = clampImageSizePreserveAspect(width, height, maxFitW, maxFitH);
        const rectLike = { ...imgLike, width: scaled.w, height: scaled.h };
        return clampRectangleTopLeftToStageBounds(
          rectLike,
          stageWidth,
          stageHeight,
          imgLike.strokeWidth ?? 2,
          stageClampBufferPx,
        );
      }

      // For clones, preserve original dimensions - don't scale to fit stage
      if (isLikelyClone) {
        console.log("DEBUG: Preserving clone dimensions in main canvas, skipping scaling");
        // Don't modify dimensions for clones, just clamp position
        const edgePad = strokeHalf + stageClampBufferPx;
        const centered = clampImageCenterInsideStage(
          Number(imgLike.x) || 0,
          Number(imgLike.y) || 0,
          width,
          height,
          edgePad,
        );
        return { ...imgLike, width: width, height: height, x: centered.x, y: centered.y };
      }

      // Center-anchored: shrink to stage capacity (preserve aspect ratio), then clamp center.
      const edgePad = strokeHalf + stageClampBufferPx;
      const maxFitW = Math.max(0, stageWidth - edgePad * 2);
      const maxFitH = Math.max(0, stageHeight - edgePad * 2);
      const scaled = clampImageSizePreserveAspect(width, height, maxFitW, maxFitH);
      const centered = clampImageCenterInsideStage(
        Number(imgLike.x) || 0,
        Number(imgLike.y) || 0,
        scaled.w,
        scaled.h,
        edgePad,
      );
      return { ...imgLike, width: scaled.w, height: scaled.h, x: centered.x, y: centered.y };
    };
    const fitImageSizeToOffCanvasStrip = (width, height) => {
      const safeWidth = Math.max(10, Number(width) || 0);
      const safeHeight = Math.max(10, Number(height) || 0);
      if (!isOffCanvas || stageWidth <= 0) {
        return { width: safeWidth, height: safeHeight };
      }
      const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
      const leftStripWidth = Math.max(0, mainZoneStart);
      const rightStripWidth = Math.max(0, stageWidth - mainZoneEnd);
      const maxStripWidth = Math.max(leftStripWidth, rightStripWidth);
      if (!Number.isFinite(maxStripWidth) || maxStripWidth <= 0) {
        return { width: safeWidth, height: safeHeight };
      }
      if (safeWidth <= maxStripWidth) {
        return { width: safeWidth, height: safeHeight };
      }
      const scale = maxStripWidth / safeWidth;
      return {
        width: Math.max(10, maxStripWidth),
        height: Math.max(10, safeHeight * scale),
      };
    };

    const getEventClientPoint = (evt) => {
      if (!evt) return null;
      const px =
        evt.clientX ??
        evt.touches?.[0]?.clientX ??
        evt.changedTouches?.[0]?.clientX;
      const py =
        evt.clientY ??
        evt.touches?.[0]?.clientY ??
        evt.changedTouches?.[0]?.clientY;
      if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
      return { x: px, y: py };
    };

    let currentPos = stage?.getPointerPosition?.() || null;
    // When pointerup happens outside stage bounds, Konva can return null.
    // Fallback to raw client coordinates mapped into stage space so drag-vs-click
    // detection and finalization stay correct.
    if (!currentPos && stage?.container && e?.evt) {
      const clientPt = getEventClientPoint(e.evt);
      const containerRect = stage.container().getBoundingClientRect();
      if (clientPt && containerRect) {
        currentPos = {
          x: clientPt.x - containerRect.left,
          y: clientPt.y - containerRect.top,
        };
      }
    }
    const initialPos = initialPointerPosition?.current;

    const rectDrawCommitted = isDrawingRectRef?.current ?? isDrawingRect;
    const rectStartCommitted = newRectStartRef?.current ?? newRectStart;

    let wasClick = false;
    if (initialPos && currentPos) {
      const distance = Math.hypot(
        currentPos.x - initialPos.x,
        currentPos.y - initialPos.y,
      );
      wasClick = distance < POINTER_CLICK_MOVE_THRESHOLD_PX;
    } else {
      const anyDrawInProgress =
        rectDrawCommitted ||
        isDrawingCircle ||
        isDrawingArrow ||
        isDrawingDoubleArrow ||
        isDrawingLine ||
        isDrawingText ||
        isDrawingImage;
      wasClick = !anyDrawInProgress;
    }

    // Multi-template click-to-clone is handled only by cloneElementsAtPosition (Canvas
    // handleStageMouseDown / Background onMouseDown). This pointerUp block duplicated the entire
    // template set on every click (mousedown + pointerUp both cloned).
    if (
      wasClick &&
      isCloneMode &&
      cloneTemplates &&
      cloneTemplates.length > 0
    ) {
      return;
    }


    // Handle drag completion: If we dragged (not clicked) and we're drawing, finalize the element
    // This prevents duplicate clones when dragging
    // Check lockedAction instead of action because in clone mode, action is SELECT but lockedAction is the shape type

    // First handle clone mode drag completion
    if (!wasClick && isCloneMode && cloneTemplate && lockedAction) {
      // Check if we were drawing a rectangle
      if (
        lockedAction === ACTIONS_RECTANGLE &&
        rectDrawCommitted &&
        currentRectId.current
      ) {
        // Store the ID before clearing it
        const rectIdToFinalize = currentRectId.current;

        let finalizedCloneRect = null;

        // Finalize the rectangle that was created in onPointerMove (when dragging started)
        // CRITICAL: Save the updated array directly (like main canvas does) to ensure proper dimensions
        // flushSync: updater must run before we read finalizedCloneRect; otherwise selection is skipped
        // (React 18 batching) and opacity stays wrong (stale selectedObjects / selectedObject).
        flushSync(() => {
          activeSetRectanglesForCloneUp((prev) => {
          let rectangleExists = prev.some(
            (rect) => rect.id === rectIdToFinalize
          );

          let updatedRects;

          if (!rectangleExists && cloneTemplate && rectStartCommitted) {
            const tw = Number(cloneTemplate?.width);
            const th = Number(cloneTemplate?.height);
            const templateWidth = Math.max(
              RECT_CLICK_MIN_W,
              Number.isFinite(tw) && tw > 0 ? tw : 50,
            );
            const templateHeight = Math.max(
              RECT_CLICK_MIN_H,
              Number.isFinite(th) && th > 0 ? th : 50,
            );
            const rectangleStrokeWidthInsert = Number(
              cloneTemplate?.strokeWidth ?? rectangleDefaultPreset?.strokeWidth ?? 2,
            );
            const centeredInsertX = rectStartCommitted.x - templateWidth / 2;
            const centeredInsertY = rectStartCommitted.y - templateHeight / 2;
            const boundedInsert = clampRectTopLeftInsideStage(
              centeredInsertX,
              centeredInsertY,
              templateWidth,
              templateHeight,
              rectangleStrokeWidthInsert,
            );
            const restrictedInsert = applyDrawingRestrictions("rectangle", {
              x: boundedInsert.x,
              y: boundedInsert.y,
              width: templateWidth,
              height: templateHeight,
            });
            const { id: _tid, name: _tn, ...cleanCloneTemplate } =
              cloneTemplate || {};
            const existingNames = new Set(prev.map((r) => r.name));
            const baseName = cloneTemplate?.name || "Rectangle 1";
            const insertName = generateUniqueNameForClone(
              baseName,
              existingNames,
            );
            const baseProps = {
              ...cleanCloneTemplate,
              x: restrictedInsert.x,
              y: restrictedInsert.y,
              width: restrictedInsert.width,
              height: restrictedInsert.height,
            };
            const insertedRect = clampMainCanvasRectClone({
              ...restrictedInsert,
              _preserveSizeOnClamp: true,
              ...baseProps,
              name: insertName,
              id: rectIdToFinalize,
              fillOpacity:
                cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
              strokeOpacity:
                cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
              opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
              isNew: false,
              isHighlighted: false,
            });
            
            console.log("[RECT-INSERTION] Final inserted rectangle after clamping", {
              id: rectIdToFinalize,
              inputDimensions: { width: restrictedInsert.width, height: restrictedInsert.height },
              clampedDimensions: { width: insertedRect.width, height: insertedRect.height },
              wasClamped: (restrictedInsert.width !== insertedRect.width || restrictedInsert.height !== insertedRect.height)
            });
            
            updatedRects = [...prev, insertedRect];
            finalizedCloneRect = insertedRect;
            rectangleExists = true;
          } else if (!rectangleExists) {
            return prev;
          } else {
            updatedRects = prev.map((rect) =>
              rect.id === rectIdToFinalize
                ? clampMainCanvasRectClone({
                  ...rect,
                  isNew: false,
                  _preserveSizeOnClamp: true,
                })
                : rect
            );
            finalizedCloneRect = updatedRects.find(
              (r) => r.id === rectIdToFinalize
            );
          }

          if (pendingSelection && rectIdToFinalize) {
            pendingSelection.current = {
              elementId: rectIdToFinalize,
              elementType: "Rect",
              elementArray: null,
            };
          }

          // CRITICAL: Save to history immediately with the updated array (like main canvas does)
          // This ensures we save the rectangle with proper dimensions, not reading from potentially stale state
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            if (isRectOffCanvas) {
              // CRITICAL: Off-canvas rectangles should be stored only in offCanvasRectangles.
              // Do NOT override main canvas rectangles here; let saveToHistory use the current
              // main-canvas state from MyCanvas so API/main rectangles are preserved.
              const rectangleToSave = updatedRects.find(
                (r) => r.id === rectIdToFinalize,
              );
              if (
                rectangleToSave &&
                (!rectangleToSave.width ||
                  !rectangleToSave.height ||
                  rectangleToSave.width < 1 ||
                  rectangleToSave.height < 1)
              ) {
                console.warn("⚠️ [HISTORY-SAVE-DRAG] Rectangle has invalid dimensions!", {
                  id: rectangleToSave.id,
                  width: rectangleToSave.width,
                  height: rectangleToSave.height,
                });
              }
              saveToHistory(
                isCloneMode ? "add_rectangle_clone" : "add_rectangle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null, // currentRectanglesOverride - let history use current main rectangles
                updatedRects, // currentOffCanvasRectanglesOverride - finalized off-canvas rectangles
              );
            } else {
              // Main canvas: pass the full updated main-rectangle array so history
              // has a complete snapshot (API + newly added rectangle).
              saveToHistory(
                isCloneMode ? "add_rectangle_clone" : "add_rectangle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                updatedRects, // currentRectanglesOverride - main canvas rectangles
                null, // currentOffCanvasRectanglesOverride - none for main canvas
              );
            }
          }, saveOrder * 10);

          return updatedRects;
        });
        });
        // CRITICAL: Clear drawing state BEFORE clearing currentRectId to prevent onPointerMove from creating a new rectangle
        setIsDrawingRect(false);
        setNewRectStart(null);

        // CRITICAL: Clear currentRectId AFTER a small delay to ensure onPointerMove has finished processing
        // This prevents onPointerMove from creating a new rectangle after onPointerUp has finalized
        setTimeout(() => {
          cloneDrawCanvasByShapeId.delete(rectIdToFinalize);
          currentRectId.current = null; // Clear the ID after onPointerMove has finished
        }, 0);

        if (finalizedCloneRect) {
          const sel = { ...finalizedCloneRect, type: "Rect" };
          const applyCloneSelection = () => {
            flushSync(() => {
              if (typeof setSelectedObjects === "function") setSelectedObjects([sel]);
              if (typeof setSelectedObject === "function") setSelectedObject(sel);
            });
          };
          applyCloneSelection();
          queueMicrotask(applyCloneSelection);
        }

        return; // Skip the click-based clone creation below
      }
      // Check if we were drawing a circle
      else if (
        lockedAction === ACTIONS_CIRCLE &&
        isDrawingCircle &&
        currentCircleId.current
      ) {
        const circleIdToFinalize = currentCircleId.current;
        let finalizedCloneCircle = null;
        let finalizedCirclesForHistory = null;

        flushSync(() => {
          activeSetCirclesForCloneUp((prev) => {
            const circleExists = prev.some(
              (circle) => circle.id === circleIdToFinalize,
            );
            if (!circleExists) {
              finalizedCirclesForHistory = prev;
              return prev;
            }

            const updatedCircles = prev.map((circle) =>
              circle.id === circleIdToFinalize
                ? clampMainCanvasCircleClone({ ...circle, isNew: false })
                : circle,
            );

            finalizedCirclesForHistory = updatedCircles;
            finalizedCloneCircle = updatedCircles.find(
              (c) => c.id === circleIdToFinalize,
            );

            if (pendingSelection && circleIdToFinalize) {
              pendingSelection.current = {
                elementId: circleIdToFinalize,
                elementType: "Circle",
                elementArray: null,
              };
            }

            return updatedCircles;
          });
        });

        setIsDrawingCircle(false);
        setNewCircleStart(null);
        cloneDrawCanvasByShapeId.delete(circleIdToFinalize);
        currentCircleId.current = null;

        if (finalizedCloneCircle) {
          bumpShapeDrawSelectionGuard();
          const sel = { ...finalizedCloneCircle, type: "Circle" };
          const applyCloneSelection = () => {
            flushSync(() => {
              if (typeof setSelectedObjects === "function") {
                setSelectedObjects([sel]);
              }
              if (typeof setSelectedObject === "function") {
                setSelectedObject(sel);
              }
            });
          };
          applyCloneSelection();
          queueMicrotask(applyCloneSelection);
        }

        const saveOrder = ++historySaveCounter;
        setTimeout(() => {
          if (finalizedCirclesForHistory) {
            if (isCircleOffCanvas) {
              saveToHistory(
                isCloneMode ? "add_circle_clone" : "add_circle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                finalizedCirclesForHistory,
              );
            } else {
              saveToHistory(
                isCloneMode ? "add_circle_clone" : "add_circle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                finalizedCirclesForHistory,
                null,
              );
            }
          }
        }, saveOrder * 10);

        return; // Skip the click-based clone creation below
      }
      // Check if we were drawing a unified arrow/line
      else if (
        (lockedAction === ACTIONS_ARROW ||
          lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
          lockedAction === ACTIONS_LINE) &&
        isDrawingLine &&
        currentLineId.current &&
        lineStartPoint
      ) {

        // Check if this is a unified element
        const hasStartArrow =
          cloneTemplate?.hasStartArrow === true ||
          cloneTemplate?.pointerAtBeginning === true;
        const hasEndArrow =
          cloneTemplate?.hasEndArrow === true ||
          cloneTemplate?.pointerAtEnd === true ||
          cloneTemplate?.pointerAtEnding === true;
        const isUnifiedElement =
          cloneTemplate && (hasStartArrow || hasEndArrow);


        // Handle both unified arrows and plain lines
        // For plain lines, isUnifiedElement will be false, but we still need to finalize them
        if (
          isUnifiedElement ||
          lockedAction === ACTIONS_LINE ||
          lockedAction === ACTIONS_ARROW ||
          lockedAction === ACTIONS_DOUBLE_SIDED_ARROW
        ) {
          if (
            lockedAction === ACTIONS_ARROW ||
            lockedAction === ACTIONS_DOUBLE_SIDED_ARROW
          ) {
            console.log("[CLONE-DRAW][UP-FINALIZE]", {
              action,
              lockedAction,
              wasClick,
              isDrawingLine,
              currentLineId: currentLineId.current,
              hasStartArrow,
              hasEndArrow,
              isUnifiedElement,
              isOffCanvas,
            });
          }
          // Store the ID before clearing it
          const lineIdToFinalize = currentLineId.current;


          // Choose the appropriate state setter based on which canvas is active
          const activeSetLinesForFinalize =
            isOffCanvas && setOffCanvasLines ? setOffCanvasLines : setLines;

          let finalizedCloneUnifiedLine = null;

          // Finalize the unified arrow that was created in onPointerMove (when dragging started)
          flushSync(() => {
            activeSetLinesForFinalize((prev) => {
              const lineExists = prev.some(
                (line) => line.id === lineIdToFinalize
              );

              if (!lineExists) {
                return prev;
              }

              // CRITICAL: Fetch latest element properties from lines array before finalizing
              // This ensures we use the most up-to-date arrow properties (hasStartArrow/hasEndArrow)
              // even if they were changed in UnifiedLineArrowEditor after clone mode was enabled
              let latestHasStartArrow = null;
              let latestHasEndArrow = null;
              const arrowSyncId = cloneSourceLineId || selectedObject?.id;
              if (arrowSyncId) {
                const latestElement = lineByIdMainOrOff(arrowSyncId);
                if (latestElement) {
                  latestHasStartArrow = latestElement.hasStartArrow;
                  latestHasEndArrow = latestElement.hasEndArrow;
                }
              }

              const next = prev.map((line) => {
                if (line.id === lineIdToFinalize) {
                  let updated = { ...line, isNew: false };
                  // Update arrow properties if we found the latest element
                  if (latestHasStartArrow !== null) {
                    updated.hasStartArrow = latestHasStartArrow;
                  }
                  if (latestHasEndArrow !== null) {
                    updated.hasEndArrow = latestHasEndArrow;
                  }
                  return clampMainCanvasPolylineClone(updated);
                }
                return line;
              });
              finalizedCloneUnifiedLine = next.find(
                (l) => l.id === lineIdToFinalize,
              );
              return next;
            });
          });

          if (pendingSelection && lineIdToFinalize) {
            pendingSelection.current = {
              elementId: lineIdToFinalize,
              elementType: "Line",
              elementArray: null,
            };
          }

          if (finalizedCloneUnifiedLine) {
            const selType = getUnifiedLineArrowSelectionType(
              finalizedCloneUnifiedLine,
            );
            const sel = { ...finalizedCloneUnifiedLine, type: selType };
            const applyUnifiedCloneSelection = () => {
              flushSync(() => {
                if (typeof setSelectedObjects === "function") {
                  setSelectedObjects([sel]);
                }
                if (typeof setSelectedObject === "function") {
                  setSelectedObject(sel);
                }
              });
            };
            applyUnifiedCloneSelection();
            queueMicrotask(applyUnifiedCloneSelection);
          }

          // CRITICAL: Clear drawing state BEFORE clearing currentLineId to prevent onPointerMove from creating a new line
          setIsDrawingLine(false);
          setLineStartPoint(null);

          // Save to history - for off-canvas pass null for main lines and use currentOffCanvasLinesOverride
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            activeSetLinesForFinalize((currentLines) => {
              if (isOffCanvas) {
                saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, null, null, null, null, null, null, null, null, null, null, currentLines);
              } else {
                saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, currentLines);
              }
              return currentLines;
            });
          }, saveOrder * 10);

          // CRITICAL: Clear currentLineId AFTER a small delay to ensure onPointerMove has finished processing
          // This prevents onPointerMove from creating a new line after onPointerUp has finalized
          setTimeout(() => {
            currentLineId.current = null; // Clear the ID after onPointerMove has finished
          }, 0);


          return; // Skip the click-based clone creation below
        }
      } else if (
        lockedAction === ACTIONS_IMAGE &&
        isDrawingImage &&
        currentImageId.current &&
        imageStartPoint
      ) {
        // Clone drag-to-draw image (same idea as rectangle clone drag finalization above).
        const imageIdToFinalize = currentImageId.current;
        let finalizedCloneImage = null;
        let finalizedImagesForHistory = null;

        flushSync(() => {
          activeSetImages((prev) => {
            const imageExists = prev.some((img) => img.id === imageIdToFinalize);
            if (!imageExists) {
              finalizedImagesForHistory = prev;
              return prev;
            }

            const refData = previewImageDataRef?.current;
            const updatedImages = prev.map((img) => {
              if (img.id !== imageIdToFinalize) return img;
              const needsDataUrl =
                img.image &&
                img.image.naturalWidth &&
                (!img.imageDataUrl || (img.src && img.src.startsWith("blob:")));
              const dataUrl = needsDataUrl ? imageElementToDataUrl(img.image) : null;
              const natW = img.image?.naturalWidth;
              const natH = img.image?.naturalHeight;
              const safeW = (v) => (typeof v === "number" && v > 10 ? v : natW || 100);
              const safeH = (v) => (typeof v === "number" && v > 10 ? v : natH || 100);
              const width = refData?.width ?? safeW(img.width);
              const height = refData?.height ?? safeH(img.height);
              const x = refData?.x ?? img.x;
              const y = refData?.y ?? img.y;
              return clampMainCanvasImageClone({
                ...img,
                x,
                y,
                width,
                height,
                ...(dataUrl
                  ? {
                    imageDataUrl: img.imageDataUrl || dataUrl,
                    originalImageDataUrl: img.originalImageDataUrl || dataUrl,
                  }
                  : {}),
                isNew: false,
                opacity: 1,
              });
            });
            finalizedImagesForHistory = updatedImages;
            finalizedCloneImage = updatedImages.find(
              (im) => im.id === imageIdToFinalize,
            );

            if (pendingSelection && imageIdToFinalize) {
              pendingSelection.current = {
                elementId: imageIdToFinalize,
                elementType: "Image",
                elementArray: null,
              };
            }
            return updatedImages;
          });
        });

        setIsDrawingImage(false);
        setImageStartPoint(null);
        currentImageId.current = null;

        if (finalizedCloneImage) {
          const sel = { ...finalizedCloneImage, type: "Image" };
          const applyCloneSelection = () => {
            flushSync(() => {
              if (typeof setSelectedObjects === "function") setSelectedObjects([sel]);
              if (typeof setSelectedObject === "function") setSelectedObject(sel);
            });
          };
          applyCloneSelection();
          queueMicrotask(applyCloneSelection);
        }

        const isOffCanvasAdd = activeSetImages === setOffCanvasImages;
        const saveOrder = ++historySaveCounter;
        setTimeout(() => {
          if (finalizedImagesForHistory) {
            const toSave = isOffCanvasAdd
              ? finalizedImagesForHistory.map((img) => ({ ...img, isOnTopLayer: false }))
              : finalizedImagesForHistory;
            saveToHistory(
              isCloneMode ? "add_image_clone" : "add_image",
              isOffCanvasAdd ? null : toSave,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              isOffCanvasAdd ? { currentOffCanvasImagesOverride: toSave } : null,
            );
          }
        }, saveOrder * 10);

        return;
      }
    }

    // Handle non-clone drag completion for images
    // This runs when we're NOT in clone mode but dragging to create an image
    if (!wasClick && !isCloneMode && (action === ACTIONS_IMAGE || lockedAction === ACTIONS_IMAGE) && isDrawingImage && currentImageId.current && imageStartPoint) {
      // Store the ID before clearing it
      const imageIdToFinalize = currentImageId.current;


      // Finalize the image that was created in onPointerDown (preview image)
      let finalizedImagesForHistory = null;
      activeSetImages((prev) => {
        const imageExists = prev.some((img) => img.id === imageIdToFinalize);

        if (!imageExists) {
          console.warn("🔵 [POINTER-UP] Image not found for finalization:", {
            imageIdToFinalize,
            prevCount: prev.length,
            allIds: prev.map((img) => img.id),
          });
          return prev;
        }

        // Finalize the image: remove isNew flag, set opacity to 1
        // CRITICAL: Use previewImageDataRef so final dimensions are from drag end (fixes tiny image on redo)
        const refData = previewImageDataRef?.current;
        const updatedImages = prev.map((img) => {
          if (img.id !== imageIdToFinalize) return img;
          const needsDataUrl =
            img.image &&
            img.image.naturalWidth &&
            (!img.imageDataUrl || (img.src && img.src.startsWith("blob:")));
          const dataUrl = needsDataUrl ? imageElementToDataUrl(img.image) : null;
          // Always persist final dimensions: prefer ref (drag end), else current img, else natural size (fixes tiny image on redo)
          const natW = img.image?.naturalWidth;
          const natH = img.image?.naturalHeight;
          const safeW = (v) => (typeof v === "number" && v > 10 ? v : natW || 100);
          const safeH = (v) => (typeof v === "number" && v > 10 ? v : natH || 100);
          const width = refData?.width ?? safeW(img.width);
          const height = refData?.height ?? safeH(img.height);
          const x = refData?.x ?? img.x;
          const y = refData?.y ?? img.y;
          return clampMainCanvasImageClone({
            ...img,
            x,
            y,
            width,
            height,
            ...(dataUrl ? { imageDataUrl: img.imageDataUrl || dataUrl, originalImageDataUrl: img.originalImageDataUrl || dataUrl } : {}),
            isNew: false,
            opacity: 1, // Set full opacity when finalizing
          });
        });
        finalizedImagesForHistory = updatedImages;

        // CRITICAL FIX: Immediately trigger selection after state update
        // This ensures selection happens regardless of pendingSelection timing
        const finalizedImage = updatedImages.find(img => img.id === imageIdToFinalize);
        if (finalizedImage && pendingSelection) {
          // Use requestAnimationFrame to ensure state has been updated
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {

              // Set pendingSelection immediately
              pendingSelection.current = {
                elementId: imageIdToFinalize,
                elementType: "Image",
                elementArray: null,
              };

              // CRITICAL FIX: For both main canvas and off-canvas images, trigger selection directly as fallback
              // This ensures selection works immediately regardless of useEffect timing
              if (setSelectedObject) {

                // Find the image in the updated images array
                const imageToSelect = updatedImages.find(img => img.id === imageIdToFinalize);
                if (imageToSelect) {
                  setSelectedObject({
                    ...imageToSelect,
                    type: "Image",
                  });
                }
              }

              // CRITICAL FIX: Clear recentDraggableElementMousedownRef after image creation
              // This prevents the two-click deselection issue after drag-to-create
              // The ref should only prevent deselection during drag initiation, not after creation
              if (typeof window !== 'undefined' && window.recentDraggableElementMousedownRef) {
                window.recentDraggableElementMousedownRef.current = {
                  nodeId: null,
                  timestamp: 0,
                };
              }
            });
          });
        }

        // Set up pending selection for the finalized image (backup mechanism)
        if (pendingSelection) {
          // Use a longer timeout to ensure state is updated before useEffect processes the selection
          setTimeout(() => {
            // Don't pass elementArray - let useEffect search through all arrays
            pendingSelection.current = {
              elementId: imageIdToFinalize,
              elementType: "Image",
              elementArray: null,
            };
          }, 50); // Increased delay to ensure state update
        }

        // CRITICAL FIX: Also trigger selection via a different mechanism
        // Use a callback that will be called after the state update is committed
        setTimeout(() => {
          if (finalizedImage) {

            // Force trigger the useEffect by setting pendingSelection again
            if (pendingSelection) {
              pendingSelection.current = {
                elementId: imageIdToFinalize,
                elementType: "Image",
                elementArray: null,
              };
            }
          }
        }, 100);

        return updatedImages;
      });

      // CRITICAL: Clear drawing state BEFORE clearing currentImageId to prevent onPointerMove from creating issues
      setIsDrawingImage(false);
      setImageStartPoint(null);

      // Reset action to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_IMAGE) {
        setAction(ACTIONS_SELECT);
      }

      // Clear currentImageId after finalization
      currentImageId.current = null;
      if (previewImageDataRef) {
        previewImageDataRef.current = null;
      }
      setPendingImageData(null);

      // Save to history - use finalized array (same as main canvas) so dimensions are correct on redo
      const isOffCanvasAdd = activeSetImages === setOffCanvasImages;
      const saveOrder = ++historySaveCounter;
      setTimeout(() => {
        if (finalizedImagesForHistory) {
          const toSave = isOffCanvasAdd
            ? finalizedImagesForHistory.map((img) => ({ ...img, isOnTopLayer: false }))
            : finalizedImagesForHistory;
          saveToHistory(
            isCloneMode ? "add_image_clone" : "add_image",
            isOffCanvasAdd ? null : toSave,
            null, null, null, null, null, null, null, null, null, null, null, null, null,
            null,
            isOffCanvasAdd ? { currentOffCanvasImagesOverride: toSave } : null
          );
        }
      }, saveOrder * 10);


      return; // Skip the click-based clone creation below
    }

    // Check if we should handle non-clone drag completion for other shapes
    // This would be where we handle rectangles, circles, etc. drawn from scratch (not in clone mode)
    // For now, we're only handling images in non-clone mode

    // Handle click-based creation (when wasClick is true)

    // Handle pure clicks in clone mode: Create rectangle/circle on click (not drag)
    // This only runs if it was a pure click (wasClick === true) and we're in clone mode
    // Only log if we're actually going to handle it to reduce console spam
    const willHandlePureClick =
      wasClick && isCloneMode && cloneTemplate && lockedAction;
    if (willHandlePureClick) {
    }

    if (wasClick && isCloneMode && cloneTemplate && lockedAction) {
      if (
        lockedAction === ACTIONS_RECTANGLE &&
        rectDrawCommitted &&
        currentRectId.current &&
        rectStartCommitted
      ) {

        // Pure click - create rectangle now
        // Use ALL properties from cloneTemplate including width/height
        const id = currentRectId.current;

        const tw = Number(cloneTemplate?.width);
        const th = Number(cloneTemplate?.height);
        const templateWidth = Math.max(
          RECT_CLICK_MIN_W,
          Number.isFinite(tw) && tw > 0 ? tw : 50,
        );
        const templateHeight = Math.max(
          RECT_CLICK_MIN_H,
          Number.isFinite(th) && th > 0 ? th : 50,
        );

        const rectangleStrokeWidth = Number(
          cloneTemplate?.strokeWidth ?? rectangleDefaultPreset?.strokeWidth ?? 2,
        );
        const centeredRectX = rectStartCommitted.x - templateWidth / 2;
        const centeredRectY = rectStartCommitted.y - templateHeight / 2;
        const boundedRectPos = clampRectTopLeftInsideStage(
          centeredRectX,
          centeredRectY,
          templateWidth,
          templateHeight,
          rectangleStrokeWidth,
        );

        // IMPORTANT: For rectangle clone-finalize, preserve template dimensions exactly.
        // applyDrawingRestrictions("rectangle", ...) uses click-create caps (height<=600),
        // which incorrectly shrinks valid template clones (e.g. 816 -> 600).
        const restrictedProps = {
          x: boundedRectPos.x,
          y: boundedRectPos.y,
          width: templateWidth,
          height: templateHeight,
        };

        let placedCloneRect = null;
        flushSync(() => {
          activeSetRectangles((prev) => {
          // Generate unique name by checking existing names
          const existingNames = new Set(prev.map((r) => r.name));
          const baseName = cloneTemplate?.name || "Rectangle 1";
          const newName = generateUniqueNameForClone(baseName, existingNames);

          // Create a clean template without id and name to prevent conflicts
          const { id: templateId, name: templateName, ...cleanCloneTemplate } = cloneTemplate || {};

          // Use ALL properties from cloneTemplate, including width/height
          const baseProps = cloneTemplate
            ? {
              ...clearCrossCanvasCloneServerIdentity(cleanCloneTemplate),
              x: restrictedProps.x,
              y: restrictedProps.y,
              width: restrictedProps.width, // Use template width (or default)
              height: restrictedProps.height, // Use template height (or default)
            }
            : {
              fill: "#FFFFFF",
              stroke: "#FF0000",
              fillColor: "#FFFFFF",
              strokeColor: "#FF0000",
              maintainAspectRatio: false,
              strokeWidth: 2,
            };

          // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
          // Also explicitly set id after spreading clean template to ensure it's always unique
          const newRect = {
            ...restrictedProps,
            ...baseProps,
            name: newName, // Set name AFTER spreading to prevent template name from overwriting
            id, // Always set a fresh unique ID
            fillOpacity:
              cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
            strokeOpacity:
              cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
            opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
            isNew: false, // Not new, it's finalized immediately
            isHighlighted: false,
          };

          let effectiveId = id;
          while (prev.some((rect) => rect.id === effectiveId)) {
            effectiveId = generateMarkerId();
          }
          const newRectResolved = clampMainCanvasRectClone({
            ...newRect,
            id: effectiveId,
            _preserveSizeOnClamp: true,
          });
          console.log("[RECT-CLONE-FINALIZE][RESOLVED]", {
            id: effectiveId,
            templateWidth,
            templateHeight,
            restrictedWidth: restrictedProps?.width,
            restrictedHeight: restrictedProps?.height,
            resolvedWidth: newRectResolved?.width,
            resolvedHeight: newRectResolved?.height,
          });

          placedCloneRect = newRectResolved;

          const updatedRects = [...prev, newRectResolved];

          // CRITICAL: Filter out any temporary rectangles (small rectangles from onPointerDown) before saving to history
          // This prevents small rectangles from being saved to history (same logic as main canvas)
          // The temporary rectangle gets clamped to 10x10 by applyDrawingRestrictions, so filter by size
          // BUT: Always include the new rectangle we just created (it has proper dimensions from template: 50x50 default)
          const tempShapeIdForFilter = currentRectId.current;
          const rectanglesToSave = updatedRects.filter(
            (rect) => {
              if (rect.id === effectiveId) {
                return true;
              }
              return (
                rect.id !== tempShapeIdForFilter &&
                !(rect.width <= 10 && rect.height <= 10 && rect.isNew)
              );
            }
          );

          let finalRectanglesToSave = rectanglesToSave;
          const newRectInSaveArray = finalRectanglesToSave.find((r) => r.id === effectiveId);
          if (!newRectInSaveArray) {
            console.error("❌ [HISTORY-SAVE] New rectangle was filtered out! Adding it back.", {
              rectangleId: effectiveId,
              newRectWidth: newRectResolved.width,
              newRectHeight: newRectResolved.height,
              newRectIsNew: newRectResolved.isNew,
              rectanglesToSaveCount: finalRectanglesToSave.length,
            });
            finalRectanglesToSave = [...finalRectanglesToSave, newRectResolved];
          } else if (!newRectInSaveArray.width || !newRectInSaveArray.height || newRectInSaveArray.width < 1 || newRectInSaveArray.height < 1) {
            console.error("❌ [HISTORY-SAVE] New rectangle has invalid dimensions!", {
              rectangleId: effectiveId,
              width: newRectInSaveArray.width,
              height: newRectInSaveArray.height,
              x: newRectInSaveArray.x,
              y: newRectInSaveArray.y,
              templateWidth: templateWidth,
              templateHeight: templateHeight,
            });
          }

          if (pendingSelection && effectiveId) {
            pendingSelection.current = {
              elementId: effectiveId,
              elementType: "Rect",
              elementArray: null,
            };
          }

          // CRITICAL: Save to history immediately with the filtered array (like main canvas does)
          // This ensures we save the rectangle with proper dimensions, not reading from potentially stale state
          // For off-canvas rectangles, save to offCanvasRectanglesOverride
          // For main canvas rectangles, save to rectanglesOverride
          // CRITICAL: Use longer delay (50ms like main canvas) to ensure rectangle is fully initialized
          const saveOrder = ++historySaveCounter;
          // CRITICAL: Capture finalRectanglesToSave in a const for the setTimeout closure
          const capturedRectanglesToSave = finalRectanglesToSave;
          setTimeout(() => {
            if (isOffCanvas) {
              // CRITICAL: For off-canvas rectangles, ensure main canvas is explicitly empty
              // Use empty array to guarantee the rectangle is NOT saved to main canvas
              const finalMainCanvasRects = [];
              const rectangleToSave = capturedRectanglesToSave.find((r) => r.id === effectiveId);
              saveToHistory(
                isCloneMode ? "add_rectangle_clone" : "add_rectangle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                finalMainCanvasRects, // currentRectanglesOverride - explicitly empty for off-canvas rectangles
                capturedRectanglesToSave // currentOffCanvasRectanglesOverride - contains the new off-canvas rectangle with proper dimensions
              );
            } else {
              saveToHistory(
                isCloneMode ? "add_rectangle_clone" : "add_rectangle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                capturedRectanglesToSave, // currentRectanglesOverride - pass main canvas rectangles here
                null // currentOffCanvasRectanglesOverride - null for main canvas
              );
            }
          }, saveOrder * 50); // Increased delay to 50ms (like main canvas) to ensure rectangle is fully initialized

          return updatedRects;
        });
        });

        setIsDrawingRect(false);
        setNewRectStart(null);
        currentRectId.current = null;

        if (placedCloneRect) {
          const sel = { ...placedCloneRect, type: "Rect" };
          const applyPlacedSelection = () => {
            flushSync(() => {
              if (typeof setSelectedObjects === "function") setSelectedObjects([sel]);
              if (typeof setSelectedObject === "function") setSelectedObject(sel);
            });
          };
          applyPlacedSelection();
          queueMicrotask(applyPlacedSelection);
        }

        return; // Skip further processing
      } else if (
        lockedAction === ACTIONS_CIRCLE &&
        isDrawingCircle &&
        currentCircleId.current &&
        newCircleStart
      ) {
        // Pure click - create circle now
        // Use ALL properties from cloneTemplate including radius
        const id = currentCircleId.current;

        // Use cloneTemplate's radius if available, otherwise use default
        const templateRadius = cloneTemplate?.radius ?? 25;

        const restrictedProps = applyDrawingRestrictions("circle", {
          x: newCircleStart.x,
          y: newCircleStart.y,
          radius: templateRadius, // Use template radius (or default)
        });

        let placedCloneCircle = null;
        flushSync(() => {
          activeSetCircles((prev) => {
          // Generate unique name by checking existing names
          const existingNames = new Set(prev.map((c) => c.name));
          const baseName = cloneTemplate?.name || "Circle 1";
          const newName = generateUniqueNameForClone(baseName, existingNames);

          // Create a clean template without id to prevent any ID conflicts
          const { id: templateId, name: templateName, ...cleanCloneTemplate } = cloneTemplate || {};

          const baseProps = cloneTemplate
            ? {
              ...clearCrossCanvasCloneServerIdentity(cleanCloneTemplate),
              x: restrictedProps.x,
              y: restrictedProps.y,
              radius: restrictedProps.radius,
            }
            : {
              fill: "#FFFFFF",
              stroke: "#FF0000",
              fillColor: "#FFFFFF",
              maintainAspectRatio: true,
              strokeColor: "#FF0000",
              strokeWidth: 2,
            };

          // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
          // Also explicitly set id after spreading clean template to ensure it's always unique
          const newCircle = clampMainCanvasCircleClone({
            ...restrictedProps,
            ...baseProps,
            name: newName, // Set name AFTER spreading to prevent template name from overwriting
            id, // Always set a fresh unique ID
            fillOpacity:
              cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
            strokeOpacity:
              cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
            opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
            isNew: false, // Not new, it's finalized immediately
            isHighlighted: false,
          });

          // CRITICAL: Check if ID already exists to prevent duplicates
          const idExists = prev.some((circle) => circle.id === id);
          if (idExists) {
            console.error(
              "❌ [POINTER-UP] Duplicate circle ID detected, skipping",
              {
                duplicateId: id,
                existingIds: prev.map((c) => c.id).slice(-5),
              }
            );
            return prev; // Don't add if duplicate detected
          }

          placedCloneCircle = newCircle;

          if (pendingSelection && id) {
            pendingSelection.current = {
              elementId: id,
              elementType: "Circle",
              elementArray: null,
            };
          }

          return [...prev, newCircle];
        });
        });

        setIsDrawingCircle(false);
        setNewCircleStart(null);
        currentCircleId.current = null;

        if (placedCloneCircle) {
          bumpShapeDrawSelectionGuard();
          const sel = { ...placedCloneCircle, type: "Circle" };
          const applyPlacedSelection = () => {
            flushSync(() => {
              if (typeof setSelectedObjects === "function") setSelectedObjects([sel]);
              if (typeof setSelectedObject === "function") setSelectedObject(sel);
            });
          };
          applyPlacedSelection();
          queueMicrotask(applyPlacedSelection);
        }

        // Save to history
        const saveOrder = ++historySaveCounter;
        setTimeout(() => {
          activeSetCircles((currentCircles) => {
            saveToHistory(
              isCloneMode ? "add_circle_clone" : "add_circle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              currentCircles
            );
            return currentCircles;
          });
        }, saveOrder * 10);

        return; // Skip further processing
      } else if (
        (lockedAction === ACTIONS_ARROW ||
          lockedAction === ACTIONS_DOUBLE_SIDED_ARROW ||
          lockedAction === ACTIONS_LINE) && // Also handle LINE action - unified arrows might have lockedAction as LINE
        isDrawingLine &&
        currentLineId.current &&
        lineStartPoint &&
        cloneTemplate
      ) {
        // Pure click - create unified arrow/line now
        // Use lineStartPoint position (from onPointerDown) like rectangles use newRectStart
        const { x, y } = lineStartPoint;

        // CRITICAL: Check where the original arrow is stored
        const originalInArrows = cloneTemplate?.id && arrows
          ? arrows.some((a) => a.id === cloneTemplate.id)
          : false;

        // Check if this is a unified element (stored in lines array)
        // This check determines if we should create a unified arrow or a regular line
        const hasStartArrow =
          cloneTemplate?.hasStartArrow === true ||
          cloneTemplate?.pointerAtBeginning === true;
        const hasEndArrow =
          cloneTemplate?.hasEndArrow === true ||
          cloneTemplate?.pointerAtEnd === true ||
          cloneTemplate?.pointerAtEnding === true;

        // Only treat as unified if it has arrow properties AND is NOT in arrows array
        const isUnifiedElement =
          cloneTemplate && (hasStartArrow || hasEndArrow) && !originalInArrows;


        // Create in lines array if unified AND not in arrows array, otherwise check if we should create in arrows array
        // If original is in arrows array, always clone to arrows array (regardless of unified status)
        if (
          !originalInArrows &&
          setLines &&
          isDrawingLine &&
          currentLineId.current &&
          (isUnifiedElement ||
            lockedAction === ACTIONS_LINE ||
            lockedAction === ACTIONS_ARROW ||
            lockedAction === ACTIONS_DOUBLE_SIDED_ARROW)
        ) {
          // Create unified arrow/line in lines array
          const id = currentLineId.current;
          // Use lineStartPoint position (from onPointerDown) like rectangles use newRectStart
          const x = lineStartPoint.x;
          const y = lineStartPoint.y;

          // CRITICAL: Get points from the correct source in priority order:
          // 1. latestElement from lines array (already relative, most accurate)
          // 2. selectedObject.points (might be absolute, need to check)
          // 3. cloneTemplate.points (might be absolute, need to check)

          let templatePoints;
          let defaultLength = 100;
          let pointsSource = 'none';
          let pointsAnchorX = undefined;
          let pointsAnchorY = undefined;

          if (cloneSourceLineId) {
            const sourceEl = lineByIdMainOrOff(cloneSourceLineId);
            if (sourceEl?.points?.length >= 4) {
              templatePoints = [...sourceEl.points];
              const sourceAnchor = getElementAnchor(sourceEl);
              pointsAnchorX = sourceAnchor.x;
              pointsAnchorY = sourceAnchor.y;
              defaultLength = Math.sqrt(
                Math.pow(templatePoints[2] - templatePoints[0], 2) +
                Math.pow(templatePoints[3] - templatePoints[1], 2)
              );
              pointsSource = 'latestElement';
            }
          }

          if (
            !templatePoints &&
            cloneTemplate?.points &&
            Array.isArray(cloneTemplate.points) &&
            cloneTemplate.points.length >= 4
          ) {
            templatePoints = [...cloneTemplate.points];
            const templateAnchor = getElementAnchor(cloneTemplate);
            pointsAnchorX = templateAnchor.x;
            pointsAnchorY = templateAnchor.y;
            pointsSource = 'cloneTemplate';
          }

          // Legacy fallback when duplicate-mode ref is missing
          if (
            !templatePoints &&
            selectedObject &&
            selectedObject.points &&
            Array.isArray(selectedObject.points) &&
            selectedObject.points.length >= 4
          ) {
            templatePoints = [...selectedObject.points];
            const selectedAnchor = getElementAnchor(selectedObject);
            pointsAnchorX = selectedAnchor.x;
            pointsAnchorY = selectedAnchor.y;
            pointsSource = 'selectedObject';
          }

          if (!templatePoints) {
            templatePoints = cloneTemplate.points || [0, 0, 100, 0];
            const templateAnchor = getElementAnchor(cloneTemplate);
            pointsAnchorX = templateAnchor.x;
            pointsAnchorY = templateAnchor.y;
            pointsSource = 'cloneTemplate';
          }


          // IMPORTANT:
          // - `latestElement.points` are typically already relative to element x/y.
          // - Normalizing those again against anchor can incorrectly shift points far
          //   upward/left (seen as large negative offsets on click clone).
          // Only normalize latestElement points when they clearly look absolute
          // (first point approximately equals the source anchor).
          const pointsLookAbsoluteAgainstAnchor =
            pointsSource === "latestElement" &&
            Array.isArray(templatePoints) &&
            templatePoints.length >= 2 &&
            Number.isFinite(Number(pointsAnchorX)) &&
            Number.isFinite(Number(pointsAnchorY)) &&
            Math.abs((Number(templatePoints[0]) || 0) - (Number(pointsAnchorX) || 0)) <= 3 &&
            Math.abs((Number(templatePoints[1]) || 0) - (Number(pointsAnchorY) || 0)) <= 3;

          if (pointsSource !== "latestElement" || pointsLookAbsoluteAgainstAnchor) {
            const normalizedPoints = normalizeRelativePolylinePoints(
              templatePoints,
              pointsAnchorX,
              pointsAnchorY,
            );
            if (normalizedPoints && normalizedPoints.length >= 4) {
              templatePoints = normalizedPoints;
            }
          }
          // Some stored unified elements can still carry absolute stage points.
          // Detect this by comparing centroid distance to the source anchor under
          // two interpretations:
          // - raw points are absolute (centroid ~= anchor)
          // - raw points are relative (centroid of anchor+raw ~= anchor)
          // If raw-absolute fits much better, convert explicitly.
          let forceConvertedLatestAbsolute = false;
          let rebasedLatestToFirstPoint = false;
          if (
            pointsSource === "latestElement" &&
            Number.isFinite(Number(pointsAnchorX)) &&
            Number.isFinite(Number(pointsAnchorY)) &&
            Array.isArray(templatePoints) &&
            templatePoints.length >= 4
          ) {
            const ax = Number(pointsAnchorX) || 0;
            const ay = Number(pointsAnchorY) || 0;
            let sumRawX = 0;
            let sumRawY = 0;
            let sumRelAsAbsX = 0;
            let sumRelAsAbsY = 0;
            let count = 0;
            for (let i = 0; i < templatePoints.length; i += 2) {
              const rx = Number(templatePoints[i]) || 0;
              const ry = Number(templatePoints[i + 1]) || 0;
              sumRawX += rx;
              sumRawY += ry;
              sumRelAsAbsX += ax + rx;
              sumRelAsAbsY += ay + ry;
              count += 1;
            }
            if (count > 0) {
              const rawCx = sumRawX / count;
              const rawCy = sumRawY / count;
              const relCx = sumRelAsAbsX / count;
              const relCy = sumRelAsAbsY / count;
              const distRawToAnchor = Math.hypot(rawCx - ax, rawCy - ay);
              const distRelToAnchor = Math.hypot(relCx - ax, relCy - ay);
              const shouldForceAbsoluteToRelative =
                distRawToAnchor < distRelToAnchor * 0.55;
              if (shouldForceAbsoluteToRelative) {
                templatePoints = templatePoints.map((coord, idx) =>
                  idx % 2 === 0 ? (Number(coord) || 0) - ax : (Number(coord) || 0) - ay,
                );
                forceConvertedLatestAbsolute = true;
              }
            }
          }
          // Final safety net for click-to-create clones:
          // if latestElement points still look absolute (all far from origin),
          // rebase to first point so geometry is relative to clone x/y.
          if (pointsSource === "latestElement" && Array.isArray(templatePoints) && templatePoints.length >= 4) {
            const numericPts = templatePoints.map((n) => Number(n) || 0);
            const maxAbs = Math.max(...numericPts.map((n) => Math.abs(n)));
            const minAbs = Math.min(...numericPts.map((n) => Math.abs(n)));
            const hasNearOriginCoord = numericPts.some((n) => Math.abs(n) <= 8);
            const looksStillAbsoluteLike =
              !forceConvertedLatestAbsolute &&
              maxAbs > 220 &&
              minAbs > 16 &&
              !hasNearOriginCoord;
            if (looksStillAbsoluteLike) {
              const sx = numericPts[0] || 0;
              const sy = numericPts[1] || 0;
              templatePoints = numericPts.map((coord, idx) =>
                idx % 2 === 0 ? coord - sx : coord - sy,
              );
              rebasedLatestToFirstPoint = true;
            }
          }
          defaultLength =
            templatePoints.length >= 4
              ? Math.sqrt(
                Math.pow(templatePoints[2] - templatePoints[0], 2) +
                Math.pow(templatePoints[3] - templatePoints[1], 2)
              )
              : 100;


          // Preserve the exact click anchor for click-to-create unified line/arrow clones.
          // Generic line restrictions here can shift long templates upward before
          // polyline-aware clamp/placement is applied.
          const restrictedProps = {
            x: Number(x) || 0,
            y: Number(y) || 0,
            width: defaultLength,
            height: 0,
          };

          // Choose the appropriate state setter based on which canvas is active
          const activeSetLines =
            isOffCanvas && setOffCanvasLines ? setOffCanvasLines : setLines;

          let placedCloneUnifiedLine = null;

          flushSync(() => {
            activeSetLines((prev) => {
            // For pure click, we need to UPDATE the temporary element created in onPointerDown
            // instead of adding a new one, to avoid duplicates
            const existingIndex = prev.findIndex((line) => line.id === id);
            // Generate unique name by checking existing names
            // Simple approach: count all elements of the same type, add 1
            const currentLinesArray = (isOffCanvas && offCanvasLines ? offCanvasLines : lines) || [];

            // Get all line elements (from both current state and prev)
            const allLines = [
              ...currentLinesArray,
              ...prev.filter((l) => !currentLinesArray.some((cl) => cl.id === l.id)), // Add prev items not in currentLinesArray
            ];

            // For Arrows, filter to only Arrow elements (those with hasStartArrow or hasEndArrow)
            // Check if this is an Arrow by looking at cloneTemplate properties
            const isArrow = cloneTemplate && (
              (cloneTemplate.hasStartArrow === true || cloneTemplate.hasEndArrow === true) ||
              (cloneTemplate.pointerAtBeginning === true || cloneTemplate.pointerAtEnd === true || cloneTemplate.pointerAtEnding === true)
            );

            // If this is an Arrow, filter to only Arrow elements, otherwise use all lines
            const relevantLines = isArrow
              ? allLines.filter((l) =>
                (l.hasStartArrow === true || l.hasEndArrow === true) ||
                (l.pointerAtBeginning === true || l.pointerAtEnd === true || l.pointerAtEnding === true)
              )
              : allLines;

            // CRITICAL: For arrows, also collect names from arrows and doubleSidedArrows arrays
            // This ensures we count ALL existing arrows across all three arrays
            const allArrowNames = [];
            if (isArrow) {
              // From lines array (unified arrows)
              allArrowNames.push(...relevantLines.map((l) => l.name).filter(Boolean));

              // From arrows array (legacy arrows)
              if (arrows) {
                allArrowNames.push(...arrows.map((a) => a.name).filter(Boolean));
              }

              // From doubleSidedArrows array (legacy double-sided arrows)
              if (doubleSidedArrows) {
                allArrowNames.push(...doubleSidedArrows.map((a) => a.name).filter(Boolean));
              }
            } else {
              // For non-arrows (lines), just use relevantLines
              allArrowNames.push(...relevantLines.map((l) => l.name).filter(Boolean));
            }

            const existingNames = new Set(allArrowNames);
            const baseName = cloneTemplate?.name || "Stroke 1";


            const newName = generateUniqueNameForClone(baseName, existingNames);


            // CRITICAL: Fetch latest element properties from lines array before using cloneTemplate
            // This ensures we use the most up-to-date arrow properties (hasStartArrow/hasEndArrow)
            // even if they were changed in UnifiedLineArrowEditor after clone mode was enabled
            // Check the main lines array (not prev, which is the target array we're adding to)
            let latestTemplate = cloneTemplate;
            const styleSyncId = cloneSourceLineId || selectedObject?.id;
            if (styleSyncId) {
              const latestElement = lineByIdMainOrOff(styleSyncId);
              if (latestElement) {
                // Create a clean template without id and name to prevent conflicts
                const { id: templateId, name: templateName, ...cleanCloneTemplate } =
                  cloneTemplate || {};

                // Use latest element properties, but merge with cloneTemplate to preserve other properties
                latestTemplate = {
                  ...cleanCloneTemplate,
                  hasStartArrow: latestElement.hasStartArrow,
                  hasEndArrow: latestElement.hasEndArrow,
                  // Also update other properties that might have changed
                  stroke: latestElement.stroke || cloneTemplate.stroke,
                  strokeColor:
                    latestElement.strokeColor || cloneTemplate.strokeColor,
                  strokeWidth:
                    latestElement.strokeWidth || cloneTemplate.strokeWidth,
                  ...(latestElement.fill !== undefined
                    ? { fill: latestElement.fill }
                    : {}),
                  ...(latestElement.fillColor !== undefined
                    ? { fillColor: latestElement.fillColor }
                    : {}),
                  ...(latestElement.isDashed !== undefined
                    ? { isDashed: latestElement.isDashed }
                    : {}),
                  ...(Array.isArray(latestElement.dash)
                    ? { dash: [...latestElement.dash] }
                    : {}),
                  ...(latestElement.opacity !== undefined
                    ? { opacity: latestElement.opacity }
                    : {}),
                };
              }
            }

            // Use clone template properties, preserving unified element properties
            // CRITICAL: Explicitly preserve hasStartArrow and hasEndArrow from latestTemplate
            // Must check for explicit true values, not just truthy
            const templateHasStartArrow =
              latestTemplate.hasStartArrow === true ||
              latestTemplate.pointerAtBeginning === true;
            const templateHasEndArrow =
              latestTemplate.hasEndArrow === true ||
              latestTemplate.pointerAtEnd === true ||
              latestTemplate.pointerAtEnding === true;

            // Exclude legacy pointer properties, id, and name from latestTemplate before spreading
            // UnifiedLineArrowElement derives these from hasStartArrow/hasEndArrow, so we shouldn't set them
            const {
              id: templateId2,
              name: templateName2,
              pointerAtBeginning,
              pointerAtEnd,
              pointerAtEnding,
              ...cleanTemplate
            } = latestTemplate;

            // Validate that points have a valid length (not zero)
            // If points result in zero length, try to get correct points from latestElement
            let finalPoints = templatePoints;
            if (templatePoints && templatePoints.length >= 4) {
              const pointLength = Math.sqrt(
                Math.pow(templatePoints[2] - templatePoints[0], 2) +
                Math.pow(templatePoints[3] - templatePoints[1], 2)
              );
              if (pointLength < 0.1) {
                // Points are too close together (zero or near-zero length)
                // CRITICAL: Try to get correct points from latestElement if available
                // This handles the case where selectedObject.points has absolute coordinates
                const zeroFixId = cloneSourceLineId || selectedObject?.id;
                if (zeroFixId) {
                  const latestElement = lineByIdMainOrOff(zeroFixId);
                  if (latestElement && latestElement.points && latestElement.points.length >= 4) {
                    const latestLength = Math.sqrt(
                      Math.pow(latestElement.points[2] - latestElement.points[0], 2) +
                      Math.pow(latestElement.points[3] - latestElement.points[1], 2)
                    );
                    if (latestLength >= 0.1) {
                      // Use points from latestElement since they're valid
                      finalPoints = [...latestElement.points];
                      defaultLength = latestLength;
                      console.warn("⚠️ [CLONE] Zero-length points detected, using latestElement.points:", {
                        originalPoints: templatePoints,
                        latestElementPoints: finalPoints,
                        defaultLength,
                      });
                    } else {
                      // Even latestElement has zero length, use default
                      finalPoints = [0, 0, defaultLength || 100, 0];
                      console.warn("⚠️ [CLONE] Both template and latestElement have zero length, using default:", {
                        originalPoints: templatePoints,
                        latestElementPoints: latestElement.points,
                        defaultLength: defaultLength || 100,
                        finalPoints,
                      });
                    }
                  } else {
                    // No valid latestElement, use default
                    finalPoints = [0, 0, defaultLength || 100, 0];
                    console.warn("⚠️ [CLONE] Points have zero length and no valid latestElement, using default:", {
                      originalPoints: templatePoints,
                      defaultLength: defaultLength || 100,
                      finalPoints,
                    });
                  }
                } else {
                  // No selectedObject or lines array, use default
                  finalPoints = [0, 0, defaultLength || 100, 0];
                  console.warn("⚠️ [CLONE] Points have zero length, using default:", {
                    originalPoints: templatePoints,
                    defaultLength: defaultLength || 100,
                    finalPoints,
                  });
                }
              }
            }

            const baseProps = {
              ...cleanTemplate,
              // Override position with click position
              x: restrictedProps.x,
              y: restrictedProps.y,
              // Use relative points relative to new position
              points: finalPoints,
              // Explicitly preserve arrow properties - use explicit boolean values
              hasStartArrow: templateHasStartArrow,
              hasEndArrow: templateHasEndArrow,
            };


            // Name already generated above using generateUniqueNameForClone

            // Exclude any legacy pointer properties that might have leaked through
            const {
              pointerAtBeginning: _pb2,
              pointerAtEnd: _pe2,
              pointerAtEnding: _pee2,
              ...finalBaseProps
            } = baseProps;

            // IMPORTANT: Set name AFTER spreading finalBaseProps to ensure it's not overwritten
            const newLine = clampMainCanvasPolylineClone({
              id,
              ...restrictedProps,
              ...finalBaseProps,
              name: newName, // Set name AFTER spreading to prevent template name from overwriting
              width: defaultLength,
              height: 0,
              isNew: false,
              // Explicitly ensure arrow properties are preserved (override any potential undefined values)
              hasStartArrow: templateHasStartArrow,
              hasEndArrow: templateHasEndArrow,
            });
            const pointsStats = (() => {
              const pts = Array.isArray(finalPoints) ? finalPoints : [];
              let minY = Infinity;
              let maxY = -Infinity;
              for (let i = 1; i < pts.length; i += 2) {
                const py = Number(pts[i]) || 0;
                minY = Math.min(minY, py);
                maxY = Math.max(maxY, py);
              }
              if (!Number.isFinite(minY) || !Number.isFinite(maxY)) {
                return { minY: null, maxY: null, spanY: null };
              }
              return { minY, maxY, spanY: maxY - minY };
            })();
            console.log("[clone][click][unified-line-placement]", {
              id,
              stage: { stageWidth, stageHeight, stageClampBufferPx, isOffCanvas },
              pointsSource,
              forceConvertedLatestAbsolute,
              rebasedLatestToFirstPoint,
              click: { x, y },
              restricted: { x: restrictedProps.x, y: restrictedProps.y },
              sourceAnchor: { x: pointsAnchorX, y: pointsAnchorY },
              templatePointsBeforeClamp: templatePoints,
              pointsStats,
              placed: { x: newLine?.x, y: newLine?.y, points: newLine?.points },
              arrows: {
                hasStartArrow: templateHasStartArrow,
                hasEndArrow: templateHasEndArrow,
              },
            });

            placedCloneUnifiedLine = newLine;

            // Update existing element if found, otherwise add new one
            if (existingIndex >= 0) {
              const existingElement = prev[existingIndex];
              const updated = [...prev];
              updated[existingIndex] = newLine;
              return updated;
            } else {
              return [...prev, newLine];
            }
          });
          });

          if (pendingSelection && id) {
            pendingSelection.current = {
              elementId: id,
              elementType: "Line",
              elementArray: null,
            };
          }

          if (placedCloneUnifiedLine) {
            const selType = getUnifiedLineArrowSelectionType(
              placedCloneUnifiedLine,
            );
            const sel = { ...placedCloneUnifiedLine, type: selType };
            const applyPlacedUnifiedSelection = () => {
              flushSync(() => {
                if (typeof setSelectedObjects === "function") {
                  setSelectedObjects([sel]);
                }
                if (typeof setSelectedObject === "function") {
                  setSelectedObject(sel);
                }
              });
            };
            applyPlacedUnifiedSelection();
            queueMicrotask(applyPlacedUnifiedSelection);
          }

          // Log what was actually saved to state (after state update completes)
          // Note: We can only check the main lines array here since offCanvasLines is not accessible
          setTimeout(() => {
            const savedElement = lines?.find((line) => line.id === id);
          }, 0);

          setIsDrawingLine(false);
          setLineStartPoint(null);
          currentLineId.current = null;

          // Save to history - for off-canvas pass null for main lines and use currentOffCanvasLinesOverride
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            activeSetLines((currentLines) => {
              if (isOffCanvas) {
                saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, null, null, null, null, null, null, null, null, null, null, currentLines);
              } else {
                saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, currentLines);
              }
              return currentLines;
            });
          }, saveOrder * 10);


          return; // Skip further processing - IMPORTANT: prevent duplicate creation
        } else if (originalInArrows && isDrawingArrow && currentArrowId.current && arrowStartPoint && setArrows) {
          // Original arrow is in arrows array (legacy) - clone to arrows array

          const id = currentArrowId.current;
          const { x, y } = arrowStartPoint;

          const defaultLength =
            cloneTemplate?.width || cloneTemplate?.points
              ? cloneTemplate.points && cloneTemplate.points.length >= 4
                ? Math.sqrt(
                  Math.pow(
                    cloneTemplate.points[2] - cloneTemplate.points[0],
                    2
                  ) +
                  Math.pow(
                    cloneTemplate.points[3] - cloneTemplate.points[1],
                    2
                  )
                )
                : 100
              : 100;

          const restrictedProps = applyDrawingRestrictions("arrow", {
            x,
            y,
            width: defaultLength,
            height: 0,
          });

          setArrows((prev) => {
            // Find existing temporary arrow created in onPointerDown
            const existingIndex = prev.findIndex((arrow) => arrow.id === id);

            // Collect names from arrows array for name generation
            const existingNames = new Set(prev.map((a) => a.name).filter(Boolean));
            const baseName = cloneTemplate?.name || "Arrow 1";

            // Generate unique name using simple count approach
            const nameMatch = baseName.match(/^(.+?)\s+(\d+)$/);
            const base = nameMatch ? nameMatch[1] : baseName;

            // Count all existing names that match this base pattern
            const regexPattern = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+\\d+$`);
            const matchingNames = Array.from(existingNames).filter(name => regexPattern.test(name));
            const count = matchingNames.length;

            // Next number is count + 1
            const newName = `${base} ${count + 1}`;

            const { id: templateId, name: templateName, ...cleanCloneTemplate } =
              cloneTemplate || {};

            const newArrow = clampMainCanvasPolylineClone({
              id,
              name: newName,
              ...restrictedProps,
              ...cleanCloneTemplate,
              x: restrictedProps.x,
              y: restrictedProps.y,
              points: cloneTemplate?.points || [0, 0, defaultLength, 0],
              width: defaultLength,
              height: 0,
              isNew: false,
            });

            // Update existing element if found, otherwise add new one
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = newArrow;
              return updated;
            } else {
              return [...prev, newArrow];
            }
          });

          setIsDrawingArrow(false);
          setArrowStartPoint(null);
          currentArrowId.current = null;

          // Save to history
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            saveToHistory(isCloneMode ? "add_arrow_clone" : "add_arrow");
          }, saveOrder * 10);

          return; // Skip further processing
        }
      } else if (lockedAction === ACTIONS_IMAGE) {

        if (isDrawingImage && currentImageId.current) {
          // Pure click - finalize the preview image that was created in onPointerDown
          const imageIdToFinalize = currentImageId.current;

          let updatedImages;
          let finalizedCloneImage = null;

          flushSync(() => {
            activeSetImages((prev) => {
              const imageExists = prev.some(
                (img) => img.id === imageIdToFinalize
              );

              if (!imageExists) {
                updatedImages = prev;
                return prev;
              }

              const refData = previewImageDataRef?.current;
              const imgToFinalize = prev.find((img) => img.id === imageIdToFinalize);
              if (!imgToFinalize) {
                updatedImages = prev;
                return prev;
              }
              let finalImage = imgToFinalize.image;
              if (!finalImage || !(finalImage instanceof window.Image)) {
                if (imgToFinalize.src) {
                  const newImg = new window.Image();
                  newImg.crossOrigin = "anonymous";
                  newImg.onload = () => {
                    activeSetImages((current) =>
                      current.map((i) =>
                        i.id === imageIdToFinalize
                          ? { ...i, image: newImg }
                          : i
                      )
                    );
                  };
                  newImg.src = imgToFinalize.src;
                  finalImage = newImg;
                }
              }
              const needsDataUrl =
                finalImage &&
                finalImage.naturalWidth &&
                (!imgToFinalize.imageDataUrl || (imgToFinalize.src && imgToFinalize.src.startsWith("blob:")));
              const dataUrlForHistory = needsDataUrl ? imageElementToDataUrl(finalImage) : null;
              const hasValidRefData =
                refData &&
                Number.isFinite(refData.x) &&
                Number.isFinite(refData.y) &&
                Number.isFinite(refData.width) &&
                Number.isFinite(refData.height) &&
                refData.width > 0 &&
                refData.height > 0;
              const minTemplateWidth = Math.max(
                10,
                Number(cloneTemplate?.width) || 0,
              );
              const minTemplateHeight = Math.max(
                10,
                Number(cloneTemplate?.height) || 0,
              );
              const resolvedWidth = Math.max(
                Number(imgToFinalize?.width) || 0,
                minTemplateWidth,
              );
              const resolvedHeight = Math.max(
                Number(imgToFinalize?.height) || 0,
                minTemplateHeight,
              );
              const finalizedItem = clampMainCanvasImageClone({
                ...imgToFinalize,
                ...(hasValidRefData
                  ? {
                    x: refData.x,
                    y: refData.y,
                    width: refData.width,
                    height: refData.height,
                  }
                  : {}),
                ...(!hasValidRefData
                  ? {
                    width: resolvedWidth,
                    height: resolvedHeight,
                  }
                  : {}),
                image: finalImage,
                ...(dataUrlForHistory ? { imageDataUrl: imgToFinalize.imageDataUrl || dataUrlForHistory, originalImageDataUrl: imgToFinalize.originalImageDataUrl || dataUrlForHistory } : {}),
                isNew: false,
                opacity: 1,
              });
              const finalized = prev.map((img) =>
                img.id === imageIdToFinalize ? finalizedItem : img
              );
              updatedImages = finalized;
              finalizedCloneImage = finalizedItem;
              if (pendingSelection && imageIdToFinalize) {
                pendingSelection.current = {
                  elementId: imageIdToFinalize,
                  elementType: "Image",
                  elementArray: null,
                };
              }
              return finalized;
            });
          });

          if (finalizedCloneImage && isCloneMode) {
            const sel = { ...finalizedCloneImage, type: "Image" };
            const applyCloneSelection = () => {
              flushSync(() => {
                if (typeof setSelectedObjects === "function") setSelectedObjects([sel]);
                if (typeof setSelectedObject === "function") setSelectedObject(sel);
              });
            };
            applyCloneSelection();
            queueMicrotask(applyCloneSelection);
          }

          // Clear drawing state
          setIsDrawingImage(false);
          setImageStartPoint(null);
          currentImageId.current = null;

          // Reset action to SELECT mode (unless action is locked)
          if (lockedAction !== ACTIONS_IMAGE) {
            setAction(ACTIONS_SELECT);
          }

          // Save to history
          // CRITICAL: When adding to off-canvas, use closure value (updatedImages) for override so we don't rely on
          // React having flushed state yet - otherwise first add can save [] and one undo removes both images.
          const isOffCanvasAdd = activeSetImages === setOffCanvasImages;
          const stripForHistory = (img) => {
            const { image, originalImage, compressedImage, ...rest } = img;
            const w = Number(img.width ?? rest.width) || 100;
            const h = Number(img.height ?? rest.height) || 100;
            const ow = Number(img.originalWidth ?? rest.originalWidth) || w;
            const oh = Number(img.originalHeight ?? rest.originalHeight) || h;
            return { ...rest, width: w, height: h, originalWidth: ow, originalHeight: oh };
          };
          const offCanvasToSave = isOffCanvasAdd && updatedImages?.length
            ? updatedImages.map((img) => ({ ...stripForHistory(img), isOnTopLayer: false }))
            : null;
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            activeSetImages((currentImagesAtSave) => {
              saveToHistory(
                isCloneMode ? "add_image_clone" : "add_image",
                isOffCanvasAdd ? null : currentImagesAtSave,
                null, null, null, null, null, null, null, null, null, null, null, null, null,
                null,
                isOffCanvasAdd ? { currentOffCanvasImagesOverride: (offCanvasToSave || currentImagesAtSave) } : null
              );
              return currentImagesAtSave;
            });

            // For locked tools, immediately deselect after saving to history
            // BUT: Don't deselect in clone mode - keep the original selected so user can continue cloning
            if (
              lockedAction === ACTIONS_IMAGE &&
              setSelectedObject &&
              !isCloneMode
            ) {
              setSelectedObject(null);
            }
          }, saveOrder * 10);


          return; // Skip further processing - IMPORTANT: prevent duplicate creation
        } else {
        }
      }
    }

    // If it was a click and the tool is locked, create shape with default properties
    // BUT: Skip if we're in clone mode with cloneTemplate/cloneTemplates - handleStageMouseDown already called cloneElementsAtPosition
    // This prevents duplicate clones from being created
    // Only skip if we have cloneTemplate or cloneTemplates (meaning cloneElementsAtPosition will be called)
    const hasCloneTemplates = cloneTemplates && cloneTemplates.length > 0;
    const hasCloneTemplate = cloneTemplate !== null;
    // CRITICAL: Skip if we're in clone mode OR if lockedAction is set with cloneTemplate
    // This prevents the default 50x50 rectangle from being created when we're cloning
    const shouldSkipForCloneMode =
      (isCloneMode && (hasCloneTemplate || hasCloneTemplates)) ||
      (lockedAction && hasCloneTemplate); // Also skip if lockedAction is set and we have a cloneTemplate


    if (
      wasClick &&
      lockedAction &&
      !shouldSkipForCloneMode
    ) {
      // Only log once per click to reduce console spam

      // Clean up any shape that was started in pointerDown (with zero/small dimensions)
      // Remove the temporary shape created in pointerDown based on the action type
      // This must complete BEFORE creating the new rectangle to avoid saving the temporary shape to history
      // For locked tools: this cleanup happens for clicks (not drags), so we remove the temporary rectangle
      // For non-locked tools: this cleanup also happens for clicks
      // NOTE: For unified elements in clone mode, no temporary element was created, so skip cleanup
      // CRITICAL: If we're in clone mode with cloneTemplate, the pure click handler above already handled it
      // So we should NOT clean up here - the temporary rectangle was never created in clone mode
      if (
        action === ACTIONS_RECTANGLE &&
        isDrawingRect &&
        currentRectId.current &&
        !(isCloneMode && cloneTemplate) // Don't clean up if we're in clone mode - no temp rectangle was created
      ) {
        const tempShapeId = currentRectId.current;
        // Clean up the temporary rectangle created in onPointerDown
        // This must complete BEFORE creating the new rectangle
        activeSetRectangles((prev) =>
          prev.filter((rect) => rect.id !== tempShapeId)
        );
        setIsDrawingRect(false);
        setNewRectStart(null);
        // Wait to ensure the cleanup completes before creating the new rectangle
        await new Promise((resolve) => setTimeout(resolve, 50));
      } else if (
        action === ACTIONS_RECTANGLE &&
        isDrawingRect &&
        currentRectId.current &&
        isCloneMode &&
        cloneTemplate
      ) {
      } else if (
        action === ACTIONS_CIRCLE &&
        isDrawingCircle &&
        currentCircleId.current
      ) {
        const tempShapeId = currentCircleId.current;
        activeSetCircles((prev) =>
          prev.filter((circle) => circle.id !== tempShapeId)
        );
        setIsDrawingCircle(false);
        setNewCircleStart(null);
      } else if (
        action === ACTIONS_ARROW &&
        isDrawingArrow &&
        currentArrowId.current
      ) {
        // Only clean up if we actually created a temporary arrow (not for unified elements)
        const tempShapeId = currentArrowId.current;
        setArrows((prev) => prev.filter((arrow) => arrow.id !== tempShapeId));
        setIsDrawingArrow(false);
        setArrowStartPoint(null);
      } else if (
        action === ACTIONS_DOUBLE_SIDED_ARROW &&
        isDrawingDoubleArrow &&
        currentDoubleArrowId.current
      ) {
        // Only clean up if we actually created a temporary arrow (not for unified elements)
        const tempShapeId = currentDoubleArrowId.current;
        setDoubleSidedArrows((prev) =>
          prev.filter((arrow) => arrow.id !== tempShapeId)
        );
        setIsDrawingDoubleArrow(false);
        setDoubleArrowStartPoint(null);
      } else if (
        (action === ACTIONS_ARROW || action === ACTIONS_DOUBLE_SIDED_ARROW) &&
        isDrawingLine &&
        currentLineId.current &&
        isCloneMode &&
        cloneTemplate &&
        !wasClick
      ) {
        // Unified element was drawn (dragged) - finalize it instead of cleaning up
        // The element is already in the lines array, just mark it as not new
        setLines((prev) => {
          const updated = prev.map((line) =>
            line.id === currentLineId.current
              ? clampMainCanvasPolylineClone({ ...line, isNew: false })
              : line
          );

          // Save to history
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, updated);
          }, saveOrder * 10);

          return updated;
        });
        setIsDrawingLine(false);
        setLineStartPoint(null);

        // Skip the click-based clone creation below
        return;
      } else if (
        action === ACTIONS_LINE &&
        isDrawingLine &&
        currentLineId.current
      ) {
        const tempShapeId = currentLineId.current;
        activeSetLines((prev) => prev.filter((line) => line.id !== tempShapeId));
        setIsDrawingLine(false);
        setLineStartPoint(null);
      } else if (
        action === ACTIONS_TEXT &&
        isDrawingText &&
        currentTextId.current
      ) {
        const tempShapeId = currentTextId.current;
        activeSetTexts((prev) => prev.filter((text) => text.id !== tempShapeId));
        setIsDrawingText(false);
        setNewTextStart(null);
      }

      const { x, y } = currentPos;
      const id = generateMarkerId();
      markerCount.current += 1;
      currentShapeId.current = id;

      const marker = { id, x, y };
      setLocationMarkers((prev) => [...prev, marker]);
      storeMarkerInIndexedDB({ markerData: marker, pageId });


      switch (lockedAction) {
        case ACTIONS_RECTANGLE: {
          // CRITICAL: If we have a cloneTemplate, we're in clone mode - don't create default rectangle here
          // The clone should have been created by the pure click handler above
          if (cloneTemplate) {
            return; // Skip creating default rectangle - clone was already created above
          }


          // CRITICAL: Before creating new rectangle, deselect and save the previous one if it exists
          // This ensures each rectangle gets its own history entry BEFORE the next one is created
          // The key is to save the previous rectangle's state BEFORE creating the new one
          if (
            selectedObject &&
            selectedObject.type === "Rect" &&
            setSelectedObject &&
            rectangles
          ) {
            // Save the previous rectangle's creation to history (current state before adding new one)
            // This must complete BEFORE we create the new rectangle
            const previousSaveOrder = ++historySaveCounter;
            const previousRectangleId = selectedObject.id;

            // Filter out any temporary rectangles (10x10 or smaller) before saving
            // The temporary rectangle from onPointerDown might still be in the state
            const tempShapeIdForFilter = currentRectId.current;
            const previousRectangles = (rectangles || []).filter(
              (rect) =>
                rect.id !== tempShapeIdForFilter && // Exclude the temporary rectangle by ID
                !(rect.width <= 10 && rect.height <= 10 && rect.isNew) // Also filter by size (10x10 or smaller)
            );


            // Save immediately and deselect - use flushSync to ensure it completes
            saveToHistory(
              isCloneMode ? "add_rectangle_clone" : "add_rectangle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              previousRectangles
            );
            // CONDITION:
            // - If creating NEW element (no cloneTemplate) → Deselect previous before creating new
            // - If creating CLONE (cloneTemplate exists) → Keep original selected
            const isCreatingClone = isCloneMode || !!cloneTemplate;
            if (!isCreatingClone) {
              setSelectedObject(null); // Deselect previous for normal drag-to-create
            }
            // If creating clone, keep original selected (don't deselect)

            // Wait a bit to ensure the save completes before creating the new rectangle
            // Use a small delay to ensure React processes the state update
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          // Create rectangle: locked click uses default preset size (else 100×100)
          const { width: defaultWidth, height: defaultHeight } =
            presetRectangleWidthHeight(
              rectangleDefaultPreset,
              100,
              100,
              stageWidth,
            );
          const rectX = x - defaultWidth / 2;
          const rectY = y - defaultHeight / 2;

          const restrictedProps = applyDrawingRestrictions("rectangle", {
            x: rectX,
            y: rectY,
            width: defaultWidth,
            height: defaultHeight,
          });

          let updatedRectangles;
          // CRITICAL: Use activeSetRectangles instead of setRectangles to handle both main canvas and off-canvas
          activeSetRectangles((prev) => {
            // Filter out any temporary rectangles (small rectangles from onPointerDown that weren't cleaned up)
            // This ensures we don't save small rectangles to history
            // The temporary rectangle gets clamped to 10x10 by applyDrawingRestrictions, so filter by size
            // Also filter by the tempShapeId to be extra safe
            const tempShapeIdForFilter = currentRectId.current;
            const filteredPrev = prev.filter(
              (rect) =>
                rect.id !== tempShapeIdForFilter && // Exclude the temporary rectangle by ID
                !(rect.width <= 10 && rect.height <= 10 && rect.isNew) // Also filter by size (10x10 or smaller)
            );

            const rectangleNumber = filteredPrev.length + 1;

            // Create a clean template without id to prevent any ID conflicts
            const { id: templateId, name: templateName, ...cleanCloneTemplate } =
              cloneTemplate || {};

            // Use clone template properties if available, otherwise use defaults
            const baseProps = cloneTemplate
              ? {
                ...cleanCloneTemplate,
                // Override position and size with click position and default size
                x: restrictedProps.x,
                y: restrictedProps.y,
                width: restrictedProps.width,
                height: restrictedProps.height,
              }
              : {
                fill: "#FFFFFF",
                stroke: "#FF0000",
                fillColor: "#FFFFFF",
                strokeColor: "#FF0000",
                maintainAspectRatio: false,
                strokeWidth: 2,
                ...pickStyleFields(rectangleDefaultPreset, [
                  "fill",
                  "fillColor",
                  "fillOpacity",
                  "stroke",
                  "strokeColor",
                  "strokeOpacity",
                  "strokeWidth",
                  "fontSize",
                  "textColor",
                  "fontStyle",
                  "textDecoration",
                  "align",
                  "verticalAlign",
                  "text",
                  "maintainAspectRatio",
                ]),
              };

            // Generate unique name by checking existing names
            const existingNames = new Set(filteredPrev.map((r) => r.name));
            const baseName = cloneTemplate?.name || "Rectangle 1";
            const newName = generateUniqueNameForClone(baseName, existingNames);

            // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
            // Also explicitly set id after spreading clean template to ensure it's always unique
            const newRect = clampMainCanvasRectClone({
              ...restrictedProps,
              ...baseProps,
              _preserveSizeOnClamp: !!cloneTemplate,
              name: newName, // Set name AFTER spreading to prevent template name from overwriting
              id, // Always set a fresh unique ID
              fillOpacity:
                cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
              strokeOpacity:
                cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
              opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
              isNew: false,
              isHighlighted: false,
              createdOnCanvas: true, // Mark as user-created element
            });

            updatedRectangles = [...filteredPrev, newRect];

            // For locked tools, don't auto-select - we'll deselect immediately after saving to history
            // This prevents selection changes from batching multiple rectangles into one history entry
            if (pendingSelection && lockedAction !== ACTIONS_RECTANGLE) {
              // Don't pass elementArray - let useEffect search through all arrays
              pendingSelection.current = {
                elementId: id,
                elementType: "Rect",
                elementArray: null,
              };
            }

            return updatedRectangles;
          });

          // Save the new rectangle to history immediately after creation
          // The previous rectangle (if any) was already saved above before creating this one
          const rafStartTime = Date.now();
          const rectangleId = id;
          const newSaveOrder = ++historySaveCounter;

          // Wait a bit to ensure the state update is processed, then save
          await new Promise((resolve) => setTimeout(resolve, 50));

          const delayTime = Date.now();
          // DEBUG: Log rectangle creation for locked click

          // Filter out any temporary rectangles (small rectangles from onPointerDown) before saving to history
          // This prevents small rectangles from being saved to history
          // The temporary rectangle gets clamped to 10x10 by applyDrawingRestrictions, so filter by size
          // Also filter by the tempShapeId to be extra safe
          const tempShapeIdForFilter = currentRectId.current;
          const rectanglesToSave = updatedRectangles.filter(
            (rect) =>
              rect.id !== tempShapeIdForFilter && // Exclude the temporary rectangle by ID
              !(rect.width <= 10 && rect.height <= 10 && rect.isNew) // Also filter by size (10x10 or smaller)
          );

          // CRITICAL: For off-canvas rectangles, save offCanvasRectanglesOverride only.
          // Pass null for currentRectanglesOverride so main canvas rectangles are preserved from current state.
          // Otherwise we would save rectangles: [] and undo would incorrectly clear main canvas.
          if (isOffCanvas) {
            saveToHistory(
              isCloneMode ? "add_rectangle_clone" : "add_rectangle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null, // currentRectanglesOverride - null so main canvas rectangles are preserved
              rectanglesToSave // currentOffCanvasRectanglesOverride - contains the new off-canvas rectangle with proper dimensions
            );
          } else {
            // Pass the filtered rectangles array directly to saveToHistory for main canvas
            saveToHistory(
              isCloneMode ? "add_rectangle_clone" : "add_rectangle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              rectanglesToSave, // currentRectanglesOverride - pass main canvas rectangles here
              null // currentOffCanvasRectanglesOverride - null for main canvas
            );
          }

          await new Promise((resolve) => setTimeout(resolve, 10));

          if (setSelectedObject) {
            const newRect = updatedRectangles.find((rect) => rect.id === id);
            if (newRect) {
              setSelectedObject({
                ...newRect,
                type: "Rect",
              });
            } else {
              console.warn("⚠️ [POINTER-UP] Could not find new rectangle for selection", {
                rectangleId: id,
                isOffCanvas,
                updatedRectanglesCount: updatedRectangles?.length || 0,
                rectanglesToSaveCount: rectanglesToSave?.length || 0,
              });
            }
          }

          if (pendingSelection) {
            pendingSelection.current = {
              elementId: id,
              elementType: "Rect",
              elementArray: null,
            };
          }
          consumeDefaultStylePreset("rectangleDefaultStylePreset");
          break;
        }

        case ACTIONS_CIRCLE: {
          // CRITICAL: If we have a cloneTemplate, we're in clone mode - don't create default circle here
          // The clone should have been created by the pure click handler above
          if (cloneTemplate) {
            return; // Skip creating default circle - clone was already created above
          }


          const { defaultRadius, extras: circleEllipseExtras } =
            circleClickDimensionsFromPreset(
              circleDefaultPreset,
              25,
              maxClickRadius,
            );

          const restrictedProps = applyDrawingRestrictions("circle", {
            x,
            y,
            radius: defaultRadius,
          });

          let updatedCircles;
          activeSetCircles((prev) => {
            // Filter out any temporary circles (small circles from onPointerDown that weren't cleaned up)
            // This ensures we don't save small circles to history
            const tempShapeIdForFilter = currentCircleId.current;
            const filteredPrev = prev.filter(
              (circle) =>
                circle.id !== tempShapeIdForFilter && // Exclude the temporary circle by ID
                !(circle.radius <= 5 && circle.isNew) // Also filter by size (radius 5 or smaller)
            );
            // Generate unique name by checking existing names
            const existingNames = new Set(filteredPrev.map((c) => c.name));
            const baseName = cloneTemplate?.name || "Circle 1";
            const newName = generateUniqueNameForClone(baseName, existingNames);

            // Create a clean template without id and name to prevent conflicts
            const { id: templateId, name: templateName, ...cleanCloneTemplate } =
              cloneTemplate || {};

            // Use clone template properties if available, otherwise use defaults
            const baseProps = cloneTemplate
              ? {
                ...cleanCloneTemplate,
                // Override position and radius with click position and default radius
                x: restrictedProps.x,
                y: restrictedProps.y,
                radius: restrictedProps.radius,
              }
              : {
                fill: "#FFFFFF",
                stroke: "#FF0000",
                fillColor: "#FFFFFF",
                maintainAspectRatio: true,
                strokeColor: "#FF0000",
                strokeWidth: 2,
                ...pickStyleFields(circleDefaultPreset, [
                  "fill",
                  "fillColor",
                  "fillOpacity",
                  "stroke",
                  "strokeColor",
                  "strokeOpacity",
                  "strokeWidth",
                  "fontSize",
                  "textColor",
                  "fontStyle",
                  "textDecoration",
                  "align",
                  "verticalAlign",
                  "text",
                  "maintainAspectRatio",
                ]),
              };

            // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
            // Also explicitly set id after spreading clean template to ensure it's always unique
            const newCircle = clampMainCanvasCircleClone({
              ...restrictedProps,
              ...baseProps,
              ...circleEllipseExtras,
              name: newName, // Set name AFTER spreading to prevent template name from overwriting
              id, // Always set a fresh unique ID
              fillOpacity:
                cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
              strokeOpacity:
                cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
              opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
              isNew: false,
              isHighlighted: false,
              createdOnCanvas: true, // Mark as user-created element
            });

            updatedCircles = [...filteredPrev, newCircle];

            // For locked tools, don't set pendingSelection inside the updater (matches rectangle);
            // selection is applied after await below.
            if (pendingSelection && lockedAction !== ACTIONS_CIRCLE) {
              // Don't pass elementArray - let useEffect search through all arrays
              pendingSelection.current = {
                elementId: id,
                elementType: "Circle",
                elementArray: null,
              };
            }

            return updatedCircles;
          });

          // Filter out any temporary circles before saving to history
          const circlesToSave = updatedCircles.filter(
            (circle) =>
              circle.id !== currentCircleId.current &&
              !(circle.radius <= 5 && circle.isNew)
          );

          // Select the newly created circle after state flush (same as locked rectangle click path).

          await new Promise((resolve) => setTimeout(resolve, 10));

          if (setSelectedObject) {
            const newCircle = updatedCircles.find((circle) => circle.id === id);
            if (newCircle) {
              bumpShapeDrawSelectionGuard();
              setSelectedObject({
                ...newCircle,
                type: "Circle",
              });
            } else {
              console.warn("⚠️ [POINTER-UP] Could not find new circle for selection", {
                circleId: id,
                isOffCanvas,
                updatedCirclesCount: updatedCircles?.length || 0,
              });
            }
          }

          if (pendingSelection) {
            pendingSelection.current = {
              elementId: id,
              elementType: "Circle",
              elementArray: null,
            };
          }

          // Use sequential delay to ensure each saveToHistory executes after the previous one
          // This is critical for locked tools where multiple clicks happen quickly
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            saveToHistory(
              isCloneMode ? "add_circle_clone" : "add_circle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              circlesToSave
            );
          }, saveOrder * 10);
          consumeDefaultStylePreset("circleDefaultStylePreset");
          break;
        }

        case ACTIONS_ARROW: {
          // CRITICAL: Check where the original arrow is stored
          const originalInArrows = cloneTemplate?.id && arrows
            ? arrows.some((a) => a.id === cloneTemplate.id)
            : false;

          // Check if this is a unified element (stored in lines array)
          // Check multiple property name variations for unified elements
          // CRITICAL: Must explicitly check for true, not just truthy values

          const hasStartArrow =
            cloneTemplate?.hasStartArrow === true ||
            cloneTemplate?.pointerAtBeginning === true;
          const hasEndArrow =
            cloneTemplate?.hasEndArrow === true ||
            cloneTemplate?.pointerAtEnd === true ||
            cloneTemplate?.pointerAtEnding === true;

          // Only treat as unified if it has arrow properties AND is NOT in arrows array
          const isUnifiedElement =
            cloneTemplate && (hasStartArrow || hasEndArrow) && !originalInArrows;


          // If it's a unified element, add to lines array instead of arrows array
          if (isUnifiedElement && setLines) {

            // CRITICAL: Get points from the correct source in priority order:
            // 1. latestElement from lines array (already relative, most accurate)
            // 2. selectedObject.points (might be absolute, need to check)
            // 3. cloneTemplate.points (might be absolute, need to check)

            let relativePoints;
            let defaultLength = 100;
            let pointsSource = 'none';

            if (selectedObject && selectedObject.id && lines) {
              const latestElement = lines.find(
                (line) => line.id === selectedObject.id
              );
              if (latestElement && latestElement.points && latestElement.points.length >= 4) {
                // Use latestElement points directly - they're already relative and correct
                relativePoints = [...latestElement.points];
                defaultLength = Math.sqrt(
                  Math.pow(relativePoints[2] - relativePoints[0], 2) +
                  Math.pow(relativePoints[3] - relativePoints[1], 2)
                );
                pointsSource = 'latestElement';
              }
            }

            // If we didn't find latestElement, try selectedObject.points
            if (!relativePoints && selectedObject && selectedObject.points && Array.isArray(selectedObject.points) && selectedObject.points.length >= 4) {
              // selectedObject.points might be absolute, we'll check and convert if needed
              relativePoints = [...selectedObject.points];
              pointsSource = 'selectedObject';
            }

            // If we still don't have points, use cloneTemplate
            if (!relativePoints) {
              relativePoints = cloneTemplate.points || [0, 0, 100, 0];
              pointsSource = 'cloneTemplate';
            }


            // CRITICAL: Convert absolute points to relative coordinates if needed
            // If points came from selectedObject or cloneTemplate, they might be absolute
            // We need to convert them to relative coordinates relative to the new element's position
            if (pointsSource === 'selectedObject' || pointsSource === 'cloneTemplate') {
              const templatePointsRaw = relativePoints;
              // Use originalX/Y from cloneTemplate if available (stored when template was created)
              // Otherwise try to get from selectedObject or cloneTemplate
              const templateX = cloneTemplate.originalX !== undefined
                ? cloneTemplate.originalX
                : (selectedObject?.x !== undefined
                  ? selectedObject.x
                  : (cloneTemplate.x !== undefined ? cloneTemplate.x : 0));
              const templateY = cloneTemplate.originalY !== undefined
                ? cloneTemplate.originalY
                : (selectedObject?.y !== undefined
                  ? selectedObject.y
                  : (cloneTemplate.y !== undefined ? cloneTemplate.y : 0));

              // Check if points are absolute by comparing first point with template position
              const firstPointX = templatePointsRaw[0] || 0;
              const firstPointY = templatePointsRaw[1] || 0;
              const tolerance = 1.0;
              const firstPointMatchesPosition =
                templateX !== undefined && templateY !== undefined &&
                Math.abs(firstPointX - templateX) < tolerance &&
                Math.abs(firstPointY - templateY) < tolerance;

              // Also check if points are large values (typical of absolute coordinates)
              // Relative points are usually small (often starting near 0 or negative for normalized points)
              const pointsAreLarge = Math.abs(firstPointX) > 200 || Math.abs(firstPointY) > 200;

              // Convert absolute points to relative if needed
              const shouldConvert = (firstPointMatchesPosition || pointsAreLarge) &&
                templateX !== undefined && templateY !== undefined;

              if (shouldConvert && templatePointsRaw.length >= 4) {
                relativePoints = templatePointsRaw.map((coord, i) =>
                  i % 2 === 0 ? coord - templateX : coord - templateY
                );
                defaultLength = Math.sqrt(
                  Math.pow(relativePoints[2] - relativePoints[0], 2) +
                  Math.pow(relativePoints[3] - relativePoints[1], 2)
                );
              } else {
                defaultLength =
                  relativePoints.length >= 4
                    ? Math.sqrt(
                      Math.pow(relativePoints[2] - relativePoints[0], 2) +
                      Math.pow(relativePoints[3] - relativePoints[1], 2)
                    )
                    : 100;
              }
            }


            const restrictedProps = applyDrawingRestrictions("line", {
              x,
              y,
              width: defaultLength,
              height: 0,
            });

            // Choose the appropriate state setter based on which canvas is active
            const activeSetLinesForDrag =
              isOffCanvas && setOffCanvasLines ? setOffCanvasLines : setLines;

            let updatedLines;
            activeSetLinesForDrag((prev) => {
              // Generate unique name by checking existing names
              // CRITICAL: Use the actual lines array (passed to function) to ensure we see all existing names
              const currentLinesArray = lines || [];
              const existingNames = new Set([
                ...currentLinesArray.map((l) => l.name),
                ...prev.map((l) => l.name), // Also include prev in case of pending updates
              ]);
              const baseName = cloneTemplate?.name || "Stroke 1";
              const newName = generateUniqueNameForClone(baseName, existingNames);

              // CRITICAL: Fetch latest element properties from lines array before using cloneTemplate
              // This ensures we use the most up-to-date arrow properties (hasStartArrow/hasEndArrow)
              // even if they were changed in UnifiedLineArrowEditor after clone mode was enabled
              // Check the main lines array (not prev, which is the target array we're adding to)
              let latestTemplate = cloneTemplate;
              if (selectedObject && selectedObject.id && lines) {
                const latestElement = lines.find(
                  (line) => line.id === selectedObject.id
                );
                if (latestElement) {
                  // Create a clean template without id and name to prevent conflicts
                  const { id: templateId, name: templateName, ...cleanCloneTemplate } =
                    cloneTemplate || {};

                  // Use latest element properties, but merge with cloneTemplate to preserve other properties
                  latestTemplate = {
                    ...cleanCloneTemplate,
                    hasStartArrow: latestElement.hasStartArrow,
                    hasEndArrow: latestElement.hasEndArrow,
                    // Also update other properties that might have changed
                    stroke: latestElement.stroke || cloneTemplate.stroke,
                    strokeColor:
                      latestElement.strokeColor || cloneTemplate.strokeColor,
                    strokeWidth:
                      latestElement.strokeWidth || cloneTemplate.strokeWidth,
                  };
                }
              }

              // Use clone template properties, preserving unified element properties
              // CRITICAL: Explicitly preserve hasStartArrow and hasEndArrow from latestTemplate
              // Must check for explicit true values, not just truthy
              const templateHasStartArrow =
                latestTemplate.hasStartArrow === true ||
                latestTemplate.pointerAtBeginning === true;
              const templateHasEndArrow =
                latestTemplate.hasEndArrow === true ||
                latestTemplate.pointerAtEnd === true ||
                latestTemplate.pointerAtEnding === true;

              // Exclude legacy pointer properties, id, and name from latestTemplate before spreading
              // UnifiedLineArrowElement derives these from hasStartArrow/hasEndArrow, so we shouldn't set them
              const {
                id: templateId3,
                name: templateName3,
                pointerAtBeginning,
                pointerAtEnd,
                pointerAtEnding,
                ...cleanTemplate
              } = latestTemplate;

              // Validate that points have a valid length (not zero)
              // If points result in zero length, try to get correct points from latestElement
              let finalRelativePoints = relativePoints;
              if (relativePoints && relativePoints.length >= 4) {
                const pointLength = Math.sqrt(
                  Math.pow(relativePoints[2] - relativePoints[0], 2) +
                  Math.pow(relativePoints[3] - relativePoints[1], 2)
                );
                if (pointLength < 0.1) {
                  // Points are too close together (zero or near-zero length)
                  // CRITICAL: Try to get correct points from latestElement if available
                  // This handles the case where selectedObject.points has absolute coordinates
                  if (selectedObject && selectedObject.id && lines) {
                    const latestElement = lines.find(
                      (line) => line.id === selectedObject.id
                    );
                    if (latestElement && latestElement.points && latestElement.points.length >= 4) {
                      const latestLength = Math.sqrt(
                        Math.pow(latestElement.points[2] - latestElement.points[0], 2) +
                        Math.pow(latestElement.points[3] - latestElement.points[1], 2)
                      );
                      if (latestLength >= 0.1) {
                        // Use points from latestElement since they're valid
                        finalRelativePoints = [...latestElement.points];
                        defaultLength = latestLength;
                        console.warn("⚠️ [CLONE] Zero-length points detected (drag), using latestElement.points:", {
                          originalPoints: relativePoints,
                          latestElementPoints: finalRelativePoints,
                          defaultLength,
                        });
                      } else {
                        // Even latestElement has zero length, use default
                        finalRelativePoints = [0, 0, defaultLength || 100, 0];
                        console.warn("⚠️ [CLONE] Both template and latestElement have zero length (drag), using default:", {
                          originalPoints: relativePoints,
                          latestElementPoints: latestElement.points,
                          defaultLength: defaultLength || 100,
                          finalPoints: finalRelativePoints,
                        });
                      }
                    } else {
                      // No valid latestElement, use default
                      finalRelativePoints = [0, 0, defaultLength || 100, 0];
                      console.warn("⚠️ [CLONE] Points have zero length and no valid latestElement (drag), using default:", {
                        originalPoints: relativePoints,
                        defaultLength: defaultLength || 100,
                        finalPoints: finalRelativePoints,
                      });
                    }
                  } else {
                    // No selectedObject or lines array, use default
                    finalRelativePoints = [0, 0, defaultLength || 100, 0];
                    console.warn("⚠️ [CLONE] Points have zero length (drag), using default:", {
                      originalPoints: relativePoints,
                      defaultLength: defaultLength || 100,
                      finalPoints: finalRelativePoints,
                    });
                  }
                }
              }

              const baseProps = {
                ...cleanTemplate,
                // Override position with click position
                x: restrictedProps.x,
                y: restrictedProps.y,
                // Use relative points relative to new position
                points: finalRelativePoints,
                // Explicitly preserve arrow properties - use explicit boolean values
                hasStartArrow: templateHasStartArrow,
                hasEndArrow: templateHasEndArrow,
              };


              // Name already generated above using generateUniqueNameForClone

              // Exclude any legacy pointer properties that might have leaked through
              const {
                pointerAtBeginning: _pb2,
                pointerAtEnd: _pe2,
                pointerAtEnding: _pee2,
                ...finalBaseProps
              } = baseProps;

              // IMPORTANT: Set name AFTER spreading finalBaseProps to ensure it's not overwritten
              const newLine = clampMainCanvasPolylineClone({
                id,
                ...restrictedProps,
                ...finalBaseProps,
                name: newName, // Set name AFTER spreading to prevent template name from overwriting
                width: defaultLength,
                height: 0,
                isNew: false,
                // Explicitly ensure arrow properties are preserved (override any potential undefined values)
                hasStartArrow: templateHasStartArrow,
                hasEndArrow: templateHasEndArrow,
              });


              updatedLines = [...prev, newLine];

              // Log what was actually saved to state (after state update completes)
              // Note: We can only check the main lines array here since offCanvasLines is not accessible
              setTimeout(() => {
                const savedElement = lines?.find((line) => line.id === id);
              }, 0);

              // Unified clone lives in `lines`; pendingSelection must use "Line" so MyCanvas finds it.
              if (
                pendingSelection &&
                (isCloneMode || lockedAction !== ACTIONS_ARROW)
              ) {
                pendingSelection.current = {
                  elementId: id,
                  elementType: "Line",
                  elementArray: null,
                };
              }

              return updatedLines;
            });

            // Use sequential delay to ensure each saveToHistory executes after the previous one
            const saveOrder = ++historySaveCounter;
            setTimeout(() => {
              saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, updatedLines);

              // For locked tools, immediately deselect after saving to history
              // BUT: Don't deselect in clone mode - keep the original selected so user can continue cloning
              if (
                lockedAction === ACTIONS_ARROW &&
                setSelectedObject &&
                !isCloneMode
              ) {
                setSelectedObject(null);
              }
            }, saveOrder * 10);

            if (isCloneMode && updatedLines?.length) {
              const added = updatedLines[updatedLines.length - 1];
              if (added && added.id === id) {
                const selType = getUnifiedLineArrowSelectionType(added);
                const sel = { ...added, type: selType };
                const applyClickCloneSelection = () => {
                  flushSync(() => {
                    if (typeof setSelectedObjects === "function") {
                      setSelectedObjects([sel]);
                    }
                    if (typeof setSelectedObject === "function") {
                      setSelectedObject(sel);
                    }
                  });
                };
                applyClickCloneSelection();
                queueMicrotask(applyClickCloneSelection);
              }
            }
            break;
          }

          // Regular arrow (not unified) - add to arrows array
          // Use clone template properties if available, otherwise use defaults
          const defaultLength =
            cloneTemplate?.width || cloneTemplate?.points
              ? cloneTemplate.points && cloneTemplate.points.length >= 4
                ? Math.sqrt(
                  Math.pow(
                    cloneTemplate.points[2] - cloneTemplate.points[0],
                    2
                  ) +
                  Math.pow(
                    cloneTemplate.points[3] - cloneTemplate.points[1],
                    2
                  )
                )
                : 100
              : 100;
          const endX = x + defaultLength;
          const endY = y;

          const restrictedProps = applyDrawingRestrictions("arrow", {
            x,
            y,
            width: defaultLength,
            height: 0,
          });

          let updatedArrows;
          setArrows((prev) => {
            const arrowNumber = prev.length + 1;

            // Create a clean template without id and name to prevent conflicts
            const { id: templateId, name: templateName, ...cleanCloneTemplate } =
              cloneTemplate || {};

            // Use clone template properties if available
            const baseProps = cloneTemplate
              ? {
                ...cleanCloneTemplate,
                // Override position with click position
                x: restrictedProps.x,
                y: restrictedProps.y,
                // Override points to start from origin relative to new position
                points: cloneTemplate.points || [0, 0, defaultLength, 0],
              }
              : {
                fillColor: fillColor || "black",
                fill: fillColor || "black",
                strokeColor: fillColor || "black",
                strokeWidth: 2,
                stroke: fillColor || "black",
                points: [0, 0, defaultLength, 0],
              };

            // When cloning, generate a new name based on array length instead of using template name
            const newName = `Arrow ${arrowNumber}`;

            // IMPORTANT: Set name AFTER spreading baseProps to ensure it's not overwritten
            const newArrow = clampMainCanvasPolylineClone({
              ...restrictedProps,
              ...baseProps,
              name: newName, // Set name AFTER spreading to prevent template name from overwriting
              id, // Always set a fresh unique ID
              width: defaultLength,
              height: 0,
              isNew: false,
              createdOnCanvas: true, // Mark as user-created element
            });

            updatedArrows = [...prev, newArrow];

            // For locked tools, don't auto-select - we'll deselect immediately after saving to history
            if (pendingSelection && lockedAction !== ACTIONS_ARROW) {
              // Don't pass elementArray - let useEffect search through all arrays
              pendingSelection.current = {
                elementId: id,
                elementType: "Arrow",
                elementArray: null,
              };
            }

            return updatedArrows;
          });

          // Use sequential delay to ensure each saveToHistory executes after the previous one
          // This is critical for locked tools where multiple clicks happen quickly
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            saveToHistory(isCloneMode ? "add_arrow_clone" : "add_arrow", null, updatedArrows);

            // For locked tools, immediately deselect after saving to history
            // BUT: Don't deselect in clone mode - keep the original selected so user can continue cloning
            if (
              lockedAction === ACTIONS_ARROW &&
              setSelectedObject &&
              !isCloneMode
            ) {
              setSelectedObject(null);
            }
          }, saveOrder * 10);
          break;
        }

        case ACTIONS_DOUBLE_SIDED_ARROW: {
          // Check if this is a unified element (stored in lines array)
          // Check multiple property name variations for unified elements

          const hasStartArrow =
            cloneTemplate?.hasStartArrow === true ||
            cloneTemplate?.pointerAtBeginning === true;
          const hasEndArrow =
            cloneTemplate?.hasEndArrow === true ||
            cloneTemplate?.pointerAtEnd === true ||
            cloneTemplate?.pointerAtEnding === true;
          const isUnifiedElement =
            cloneTemplate && (hasStartArrow || hasEndArrow);


          // If it's a unified element, add to lines array instead of doubleSidedArrows array
          if (isUnifiedElement && setLines) {

            // Use clone template properties if available, otherwise use defaults
            // The template points might be absolute (from selectedElement) or relative (from element in lines array)
            // We need to convert them to relative points relative to the new position
            const templatePoints = cloneTemplate.points || [0, 0, 100, 0];
            const templateX = cloneTemplate.x || 0;
            const templateY = cloneTemplate.y || 0;

            // Check if points are absolute (larger than typical relative values) or relative
            // If the first point is close to 0 and templateX/Y are non-zero, points are likely relative
            // If points are large (e.g., > 100), they're likely absolute
            const firstPointX = templatePoints[0] || 0;
            const firstPointY = templatePoints[1] || 0;
            const arePointsAbsolute =
              Math.abs(firstPointX) > 100 || Math.abs(firstPointY) > 100;

            // Convert absolute points to relative if needed
            let relativePoints = templatePoints;
            if (arePointsAbsolute && templatePoints.length >= 4) {
              // Convert absolute points to relative by subtracting template position
              relativePoints = templatePoints.map((coord, i) =>
                i % 2 === 0 ? coord - templateX : coord - templateY
              );
            }

            const defaultLength =
              relativePoints.length >= 4
                ? Math.sqrt(
                  Math.pow(relativePoints[2] - relativePoints[0], 2) +
                  Math.pow(relativePoints[3] - relativePoints[1], 2)
                )
                : 100;

            const restrictedProps = applyDrawingRestrictions("line", {
              x,
              y,
              width: defaultLength,
              height: 0,
            });

            let updatedLines;
            setLines((prev) => {
              // Generate unique name by checking existing names
              // CRITICAL: For arrows, we need to check BOTH:
              // 1. lines array (unified elements with hasStartArrow/hasEndArrow)
              // 2. arrows array (legacy arrows)
              // 3. doubleSidedArrows array (legacy double-sided arrows)
              const isArrow = cloneTemplate && (
                (cloneTemplate.hasStartArrow === true || cloneTemplate.hasEndArrow === true) ||
                (cloneTemplate.pointerAtBeginning === true || cloneTemplate.pointerAtEnd === true || cloneTemplate.pointerAtEnding === true)
              );

              // Collect names from all sources
              const allArrowNames = [];

              // From lines array (prev) - unified arrows
              if (isArrow) {
                const unifiedArrows = prev.filter((l) =>
                  (l.hasStartArrow === true || l.hasEndArrow === true) ||
                  (l.pointerAtBeginning === true || l.pointerAtEnd === true || l.pointerAtEnding === true)
                );
                allArrowNames.push(...unifiedArrows.map((l) => l.name));
              } else {
                // For non-arrows (lines), just use prev
                allArrowNames.push(...prev.map((l) => l.name));
              }

              // From arrows array (legacy)
              if (isArrow && arrows) {
                allArrowNames.push(...arrows.map((a) => a.name).filter(Boolean));
              }

              // From doubleSidedArrows array (legacy)
              if (isArrow && doubleSidedArrows) {
                allArrowNames.push(...doubleSidedArrows.map((a) => a.name).filter(Boolean));
              }

              const existingNames = new Set(allArrowNames.filter(Boolean));
              const baseName = cloneTemplate?.name || "Stroke 1";


              const newName = generateUniqueNameForClone(baseName, existingNames);

              // Use clone template properties, preserving unified element properties
              // CRITICAL: Explicitly preserve hasStartArrow and hasEndArrow from cloneTemplate
              // Must check for explicit true values, not just truthy
              const templateHasStartArrow =
                cloneTemplate.hasStartArrow === true ||
                cloneTemplate.pointerAtBeginning === true;
              const templateHasEndArrow =
                cloneTemplate.hasEndArrow === true ||
                cloneTemplate.pointerAtEnd === true ||
                cloneTemplate.pointerAtEnding === true;

              // Create a clean template without id and name to prevent conflicts
              const { id: templateId, name: templateName, ...cleanCloneTemplate } =
                cloneTemplate || {};

              const baseProps = {
                ...cleanCloneTemplate,
                // Override position with click position
                x: restrictedProps.x,
                y: restrictedProps.y,
                // Use relative points relative to new position
                points: relativePoints,
                // Explicitly preserve arrow properties - use explicit boolean values
                hasStartArrow: templateHasStartArrow,
                hasEndArrow: templateHasEndArrow,
              };


              const newLine = clampMainCanvasPolylineClone({
                id,
                name: newName,
                ...restrictedProps,
                ...baseProps,
                width: defaultLength,
                height: 0,
                isNew: false,
                // Explicitly ensure arrow properties are preserved (override any potential undefined values)
                hasStartArrow: templateHasStartArrow,
                hasEndArrow: templateHasEndArrow,
              });


              updatedLines = [...prev, newLine];

              // Unified clone lives in `lines`; pendingSelection must use "Line".
              if (
                pendingSelection &&
                (isCloneMode || lockedAction !== ACTIONS_DOUBLE_SIDED_ARROW)
              ) {
                pendingSelection.current = {
                  elementId: id,
                  elementType: "Line",
                  elementArray: null,
                };
              }

              return updatedLines;
            });

            // Use sequential delay to ensure each saveToHistory executes after the previous one
            const saveOrder = ++historySaveCounter;
            setTimeout(() => {
              saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, updatedLines);

              // For locked tools, immediately deselect after saving to history
              // BUT: Don't deselect in clone mode - keep the original selected so user can continue cloning
              if (
                lockedAction === ACTIONS_DOUBLE_SIDED_ARROW &&
                setSelectedObject &&
                !isCloneMode
              ) {
                setSelectedObject(null);
              }
            }, saveOrder * 10);

            if (isCloneMode && updatedLines?.length) {
              const added = updatedLines[updatedLines.length - 1];
              if (added && added.id === id) {
                const selType = getUnifiedLineArrowSelectionType(added);
                const sel = { ...added, type: selType };
                const applyDsaClickCloneSelection = () => {
                  flushSync(() => {
                    if (typeof setSelectedObjects === "function") {
                      setSelectedObjects([sel]);
                    }
                    if (typeof setSelectedObject === "function") {
                      setSelectedObject(sel);
                    }
                  });
                };
                applyDsaClickCloneSelection();
                queueMicrotask(applyDsaClickCloneSelection);
              }
            }
          }

          // If original is in arrows array, clone to arrows array
          // Re-check originalInArrows to ensure it's in scope
          const isOriginalInArrows = cloneTemplate?.id && arrows
            ? arrows.some((a) => a.id === cloneTemplate.id)
            : false;

          // If original is in arrows array (regardless of unified element status), clone to arrows array
          if (isOriginalInArrows && setArrows) {
            // If original is in arrows array, clone to arrows array
            // Original arrow is in arrows array (legacy) - clone to arrows array

            const defaultLength =
              cloneTemplate?.width || cloneTemplate?.points
                ? cloneTemplate.points && cloneTemplate.points.length >= 4
                  ? Math.sqrt(
                    Math.pow(
                      cloneTemplate.points[2] - cloneTemplate.points[0],
                      2
                    ) +
                    Math.pow(
                      cloneTemplate.points[3] - cloneTemplate.points[1],
                      2
                    )
                  )
                  : 100
                : 100;

            const restrictedProps = applyDrawingRestrictions("arrow", {
              x,
              y,
              width: defaultLength,
              height: 0,
            });

            setArrows((prev) => {
              // Collect names from arrows array for name generation
              const existingNames = new Set(prev.map((a) => a.name).filter(Boolean));
              const baseName = cloneTemplate?.name || "Arrow 1";

              // Generate unique name using simple count approach
              const nameMatch = baseName.match(/^(.+?)\s+(\d+)$/);
              const base = nameMatch ? nameMatch[1] : baseName;

              // Count all existing names that match this base pattern
              const regexPattern = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+\\d+$`);
              const matchingNames = Array.from(existingNames).filter(name => regexPattern.test(name));
              const count = matchingNames.length;

              // Next number is count + 1
              const newName = `${base} ${count + 1}`;

              const { id: templateId, name: templateName, ...cleanCloneTemplate } =
                cloneTemplate || {};

              const newArrow = clampMainCanvasPolylineClone({
                id,
                name: newName,
                ...restrictedProps,
                ...cleanCloneTemplate,
                x: restrictedProps.x,
                y: restrictedProps.y,
                points: cloneTemplate?.points || [0, 0, defaultLength, 0],
                width: defaultLength,
                height: 0,
                isNew: false,
              });

              return [...prev, newArrow];
            });

            // Use sequential delay to ensure each saveToHistory executes after the previous one
            const saveOrder = ++historySaveCounter;
            setTimeout(() => {
              saveToHistory(isCloneMode ? "add_arrow_clone" : "add_arrow");
            }, saveOrder * 10);
          }

          // Break after handling both unified and legacy arrows
          break;

          // Regular double-sided arrow (not unified) - add to doubleSidedArrows array
          // Use clone template properties if available, otherwise use defaults
          const defaultLength =
            cloneTemplate?.width || cloneTemplate?.points
              ? cloneTemplate.points && cloneTemplate.points.length >= 4
                ? Math.sqrt(
                  Math.pow(
                    cloneTemplate.points[2] - cloneTemplate.points[0],
                    2
                  ) +
                  Math.pow(
                    cloneTemplate.points[3] - cloneTemplate.points[1],
                    2
                  )
                )
                : 100
              : 100;
          const endX = x + defaultLength;
          const endY = y;

          const restrictedProps = applyDrawingRestrictions("arrow", {
            x,
            y,
            width: defaultLength,
            height: 0,
          });

          let updatedDoubleSidedArrows;
          setDoubleSidedArrows((prev) => {
            const doubleSidedArrowNumber = prev.length + 1;

            // Create a clean template without id to prevent any ID conflicts
            const { id: templateId, ...cleanCloneTemplate } =
              cloneTemplate || {};

            // Use clone template properties if available
            const baseProps = cloneTemplate
              ? {
                ...cleanCloneTemplate,
                // Override position with click position
                x: restrictedProps.x,
                y: restrictedProps.y,
                // Override points to start from origin relative to new position
                points: cloneTemplate.points || [0, 0, defaultLength, 0],
              }
              : {
                fillColor: fillColor || "black",
                fill: fillColor || "black",
                strokeColor: fillColor || "black",
                strokeWidth: 2,
                stroke: fillColor || "black",
                points: [0, 0, defaultLength, 0],
              };

            // When cloning, generate a new name based on array length instead of using template name
            const newName = `Double Sided Arrow ${doubleSidedArrowNumber}`;

            const newArrow = clampMainCanvasPolylineClone({
              id,
              name: newName,
              ...restrictedProps,
              ...baseProps,
              width: defaultLength,
              height: 0,
              isNew: false,
              createdOnCanvas: true, // Mark as user-created element
            });

            updatedDoubleSidedArrows = [...prev, newArrow];

            // For locked tools, don't auto-select - we'll deselect immediately after saving to history
            if (
              pendingSelection &&
              lockedAction !== ACTIONS_DOUBLE_SIDED_ARROW
            ) {
              // Don't pass elementArray - let useEffect search through all arrays
              pendingSelection.current = {
                elementId: id,
                elementType: "DoubleSidedArrow",
                elementArray: null,
              };
            }

            return updatedDoubleSidedArrows;
          });

          // Use sequential delay to ensure each saveToHistory executes after the previous one
          // This is critical for locked tools where multiple clicks happen quickly
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            saveToHistory(
              isCloneMode ? "add_double_sided_arrow_clone" : "add_double_sided_arrow",
              null,
              null,
              updatedDoubleSidedArrows
            );

            // For locked tools, immediately deselect after saving to history
            if (
              lockedAction === ACTIONS_DOUBLE_SIDED_ARROW &&
              setSelectedObject
            ) {
              setSelectedObject(null);
            }
          }, saveOrder * 10);
          break;
        }

        case ACTIONS_LINE: {
          const unifiedDefaults = pickStyleFields(unifiedLineArrowDefaultPreset, [
            "stroke",
            "strokeColor",
            "fill",
            "fillColor",
            "strokeWidth",
            "isDashed",
            "dash",
            "opacity",
            "hasStartArrow",
            "hasEndArrow",
            "points",
          ]);
          const resolvedHasStartArrow =
            unifiedDefaults.hasStartArrow !== undefined
              ? Boolean(unifiedDefaults.hasStartArrow)
              : false;
          const resolvedHasEndArrow =
            unifiedDefaults.hasEndArrow !== undefined
              ? Boolean(unifiedDefaults.hasEndArrow)
              : true;
          if (debugUnifiedPresetCreate) {
            console.log("[UnifiedPresetDebug][pointerUp][line-click-create]", {
              unifiedLineArrowDefaultPreset,
              unifiedDefaults,
              resolvedHasStartArrow,
              resolvedHasEndArrow,
              isCloneMode,
              hasCloneTemplate: Boolean(cloneTemplate),
            });
          }
          const defaultPoints =
            Array.isArray(unifiedDefaults.points) &&
            unifiedDefaults.points.length >= 4
              ? unifiedDefaults.points
              : null;
          // Use clone template properties if available, otherwise use defaults
          const defaultLength =
            cloneTemplate?.width || cloneTemplate?.points
              ? cloneTemplate.points && cloneTemplate.points.length >= 4
                ? Math.sqrt(
                  Math.pow(
                    cloneTemplate.points[2] - cloneTemplate.points[0],
                    2
                  ) +
                  Math.pow(
                    cloneTemplate.points[3] - cloneTemplate.points[1],
                    2
                  )
                )
                : 100
              : defaultPoints
                ? Math.sqrt(
                  Math.pow(
                    defaultPoints[defaultPoints.length - 2] - defaultPoints[0],
                    2
                  ) +
                  Math.pow(
                    defaultPoints[defaultPoints.length - 1] - defaultPoints[1],
                    2
                  )
                ) || 100
                : 100;
          const endX = x + defaultLength;
          const endY = y;

          const restrictedProps = applyDrawingRestrictions("line", {
            x,
            y,
            width: defaultLength,
            height: 0,
          });

          let updatedLines;
          const linesArrayForNames =
            (isOffCanvas && offCanvasLines ? offCanvasLines : lines) || [];
          activeSetLines((prev) => {
            // Generate unique name by checking existing names
            // CRITICAL: Use the actual lines array (passed to function) to ensure we see all existing names
            const currentLinesArray = linesArrayForNames;
            const existingNames = new Set([
              ...currentLinesArray.map((l) => l.name),
              ...prev.map((l) => l.name), // Also include prev in case of pending updates
            ]);
            const baseName = cloneTemplate?.name || "Stroke 1";
            const newName = generateUniqueNameForClone(baseName, existingNames);

            // Create a clean template without id and name to prevent conflicts
            const { id: templateId, name: templateName, ...cleanCloneTemplate } =
              cloneTemplate || {};

            // Use clone template properties if available
            const baseProps = cloneTemplate
              ? {
                ...cleanCloneTemplate,
                // Override position with click position
                x: restrictedProps.x,
                y: restrictedProps.y,
                // Override points to start from origin relative to new position
                points: cloneTemplate.points || [0, 0, defaultLength, 0],
              }
              : {
                fillColor: fillColor || "black",
                fill: fillColor || "black",
                strokeColor: fillColor || "black",
                strokeWidth: 2,
                stroke: fillColor || "black",
                points: defaultPoints || [0, 0, defaultLength, 0],
                ...unifiedDefaults,
                hasStartArrow: resolvedHasStartArrow,
                hasEndArrow: resolvedHasEndArrow,
                // Keep legacy properties for backward compatibility
                pointerAtBeginning: resolvedHasStartArrow,
                pointerAtEnd: resolvedHasEndArrow,
                startDot: {
                  radius: 4,
                  fill: fillColor || "black",
                  stroke: fillColor || "black",
                  strokeWidth: 1,
                },
                endDot: {
                  radius: 4,
                  fill: fillColor || "black",
                  stroke: fillColor || "black",
                  strokeWidth: 1,
                },
              };

            const newLine = clampMainCanvasPolylineClone({
              id,
              name: newName,
              ...restrictedProps,
              ...baseProps,
              width: defaultLength,
              height: 0,
              isNew: false,
              createdOnCanvas: true, // Mark as user-created element
            });

            updatedLines = [...prev, newLine];

            // For locked tools, don't auto-select - we'll deselect immediately after saving to history
            if (
              pendingSelection &&
              (isCloneMode || lockedAction !== ACTIONS_LINE)
            ) {
              // Don't pass elementArray - let useEffect search through all arrays
              pendingSelection.current = {
                elementId: id,
                elementType: "Line",
                elementArray: null,
              };
            }

            return updatedLines;
          });

          // Use sequential delay to ensure each saveToHistory executes after the previous one
          // This is critical for locked tools where multiple clicks happen quickly
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            activeSetLines((currentLines) => {
              if (isOffCanvas) {
                saveToHistory(
                  isCloneMode ? "add_line_clone" : "add_line",
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  null,
                  currentLines,
                );
              } else {
                saveToHistory(
                  isCloneMode ? "add_line_clone" : "add_line",
                  null,
                  null,
                  null,
                  currentLines,
                );
              }
              return currentLines;
            });

            // For locked tools, immediately deselect after saving to history
            // BUT: Don't deselect in clone mode - keep the original selected so user can continue cloning
            if (
              lockedAction === ACTIONS_LINE &&
              setSelectedObject &&
              !isCloneMode
            ) {
              setSelectedObject(null);
            } else {
            }
          }, saveOrder * 10);

          if (isCloneMode && updatedLines?.length) {
            const added = updatedLines[updatedLines.length - 1];
            if (added && added.id === id) {
              const selType = getUnifiedLineArrowSelectionType(added);
              const sel = { ...added, type: selType };
              const applyLineClickCloneSelection = () => {
                flushSync(() => {
                  if (typeof setSelectedObjects === "function") {
                    setSelectedObjects([sel]);
                  }
                  if (typeof setSelectedObject === "function") {
                    setSelectedObject(sel);
                  }
                });
              };
              applyLineClickCloneSelection();
              queueMicrotask(applyLineClickCloneSelection);
            }
          }
          break;
        }

        case ACTIONS_TEXT: {
          // Use clone template properties if available, otherwise use defaults
          const defaultWidth = cloneTemplate?.width || 225;
          const defaultHeight = cloneTemplate?.height || 100;
          const textX = x - defaultWidth / 2;
          const textY = y - defaultHeight / 2;

          const restrictedProps = applyDrawingRestrictions("text", {
            x: textX,
            y: textY,
            width: defaultWidth,
            height: defaultHeight,
          });

          const baseTexts = (isOffCanvas ? offCanvasTexts : texts) || [];
          let updatedTexts = Array.isArray(baseTexts) ? [...baseTexts] : [];
          let finalizedText = null;
          activeSetTexts((prev) => {
            const prevTexts = Array.isArray(prev) ? prev : [];
            const textNumber = prevTexts.length + 1;

            // Create a clean template without id to prevent any ID conflicts
            const { id: templateId, ...cleanCloneTemplate } =
              cloneTemplate || {};

            // Use clone template properties if available
            const baseProps = cloneTemplate
              ? {
                ...cleanCloneTemplate,
                // Override position and size with click position and default size
                x: restrictedProps.x,
                y: restrictedProps.y,
                width: restrictedProps.width,
                height: restrictedProps.height,
              }
              : {
                part: "",
                task: "",
                note: "",
                unit: "",
                qty: 0,
                textStyles: {},
                selectedContent: null,
                showBackground: true,
                showContentInput: false,
                content: null,
                fontSize: 14,
              };

            // When cloning, generate a new name based on array length instead of using template name
            const newName = `Text Box TB${textNumber}`;

            const newText = {
              id,
              name: newName,
              ...restrictedProps,
              ...baseProps,
              isNew: false,
              createdOnCanvas: true, // So save/leave includes this text even though id is marker-*
            };

            updatedTexts = [...prevTexts, newText];
            finalizedText = newText;

            return updatedTexts;
          });

          // Clear all previous selections first
          setSelectedObject(null);
          setSelectedObjects([]);
          setEditTextBoxContent(false);

          // Clear transformer
          if (transformerRef?.current) {
            transformerRef.current.nodes([]);
          }

          // Set text-specific selection states and open text box
          const updatedTextsSafe = Array.isArray(updatedTexts) ? updatedTexts : [];
          if (!finalizedText) {
            const fallbackNumber = updatedTextsSafe.length + 1;
            finalizedText = {
              id,
              name: `Text Box TB${fallbackNumber}`,
              ...restrictedProps,
              part: "",
              task: "",
              note: "",
              unit: "",
              qty: 0,
              textStyles: {},
              selectedContent: null,
              showBackground: true,
              showContentInput: false,
              content: null,
              fontSize: 14,
              isNew: false,
              createdOnCanvas: true,
            };
          }
          if (finalizedText) {
            const selectedTextShape = {
              ...finalizedText,
              type: "Text",
              selectedContent: null,
            };
            const applyTextClickSelection = () => {
              flushSync(() => {
                if (typeof setSelectedText === "function") {
                  setSelectedText(finalizedText);
                }
                if (typeof setSelectedTextId === "function") {
                  setSelectedTextId(finalizedText.id);
                }
                if (typeof setShowTextBox === "function") {
                  setShowTextBox(true);
                }
                if (typeof setSelectedObjects === "function") {
                  setSelectedObjects([selectedTextShape]);
                }
                if (typeof setSelectedObject === "function") {
                  setSelectedObject(selectedTextShape);
                }
              });
            };
            applyTextClickSelection();
            queueMicrotask(applyTextClickSelection);
            requestAnimationFrame(() => {
              requestAnimationFrame(applyTextClickSelection);
            });
            if (pendingSelection) {
              pendingSelection.current = {
                elementId: finalizedText.id,
                elementType: "Text",
                elementArray: null,
              };
            }
          }

          // Use sequential delay; for off-canvas pass null for main texts and use currentOffCanvasTextsOverride
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            if (isOffCanvas) {
              saveToHistory(
                isCloneMode ? "add_text_clone" : "add_text",
                null, null, null, null, null,
                finalizedText,
                finalizedText?.id,
                null, null, null, null, null, null, null,
                updatedTextsSafe
              );
            } else {
              saveToHistory(
                isCloneMode ? "add_text_clone" : "add_text",
                null,
                null,
                null,
                null,
                updatedTextsSafe,
                finalizedText,
                finalizedText?.id
              );
            }

            // For locked tools, immediately deselect after saving to history
            // BUT: Don't deselect in clone mode - keep the original selected so user can continue cloning
            if (
              lockedAction === ACTIONS_TEXT &&
              setSelectedObject &&
              !isCloneMode
            ) {
              setSelectedObject(null);
            }
          }, saveOrder * 10);
          break;
        }

        case ACTIONS_IMAGE: {
          // Pure click - finalize the preview image that was created in onPointerDown
          const imageIdToFinalize = currentImageId?.current;


          if (isDrawingImage && imageIdToFinalize) {

            // Finalize the preview image: remove isNew flag, set opacity to 1
            let updatedImages;
            activeSetImages((prev) => {
              const imageExists = prev.some(
                (img) => img.id === imageIdToFinalize
              );

              if (!imageExists) {
                return prev;
              }

              // Finalize the image: remove isNew flag, set opacity to 1
              // CRITICAL: Use previewImageDataRef so final dimensions are from drag end (fixes tiny image on redo)
              const refDataSwitch = previewImageDataRef?.current;
              const finalized = prev.map((img) => {
                if (img.id === imageIdToFinalize) {
                  return clampMainCanvasImageClone({
                    ...img,
                    ...(refDataSwitch ? { x: refDataSwitch.x, y: refDataSwitch.y, width: refDataSwitch.width, height: refDataSwitch.height } : {}),
                    isNew: false,
                    opacity: 1, // Set full opacity when finalizing
                  });
                }
                return img;
              });

              // CRITICAL FIX: Immediately trigger selection after state update
              // This ensures selection happens regardless of pendingSelection timing
              const finalizedImage = finalized.find(img => img.id === imageIdToFinalize);
              if (finalizedImage && pendingSelection) {
                // Use requestAnimationFrame to ensure state has been updated
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {

                    // Set pendingSelection immediately
                    pendingSelection.current = {
                      elementId: imageIdToFinalize,
                      elementType: "Image",
                      elementArray: null,
                    };

                    // CRITICAL FIX: For off-canvas images, also trigger selection directly as fallback
                    if (isOffCanvas && setSelectedObject) {

                      // Find the image in the updated offCanvasImages array
                      const updatedOffCanvasImages = activeSetImages === setOffCanvasImages ? finalized : null;
                      if (updatedOffCanvasImages) {
                        const imageToSelect = updatedOffCanvasImages.find(img => img.id === imageIdToFinalize);
                        if (imageToSelect) {
                          setSelectedObject({
                            ...imageToSelect,
                            type: "Image",
                          });
                        }
                      }
                    }
                  });
                });
              }

              // Set up pending selection for the finalized image (backup mechanism)
              if (pendingSelection) {
                // Use a longer timeout to ensure state is updated before useEffect processes the selection
                setTimeout(() => {
                  // Don't pass elementArray - let useEffect search through all arrays
                  pendingSelection.current = {
                    elementId: imageIdToFinalize,
                    elementType: "Image",
                    elementArray: null,
                  };
                }, 50); // Increased delay to ensure state update
              }

              updatedImages = finalized;
              return finalized;
            });

            // Clear drawing state
            setIsDrawingImage(false);
            setImageStartPoint(null);
            currentImageId.current = null;

            // Reset action to SELECT mode (unless action is locked)
            if (lockedAction !== ACTIONS_IMAGE) {
              setAction(ACTIONS_SELECT);
            }

            // Save to history - use finalized array (updatedImages) so dimensions are from refData, not stale state
            const isOffCanvasAddImage = activeSetImages === setOffCanvasImages;
            const saveOrder = ++historySaveCounter;
            setTimeout(() => {
              if (updatedImages) {
                const toSave = isOffCanvasAddImage
                  ? updatedImages.map((img) => ({ ...img, isOnTopLayer: false }))
                  : updatedImages;
                saveToHistory(
                  isCloneMode ? "add_image_clone" : "add_image",
                  isOffCanvasAddImage ? null : toSave,
                  null, null, null, null, null, null, null, null, null, null, null, null, null,
                  null,
                  isOffCanvasAddImage ? { currentOffCanvasImagesOverride: toSave } : null
                );
              }

              // For locked tools, immediately deselect after saving to history
              // BUT: Don't deselect in clone mode - keep the original selected so user can continue cloning
              if (
                lockedAction === ACTIONS_IMAGE &&
                setSelectedObject &&
                !isCloneMode
              ) {
                setSelectedObject(null);
              }
            }, saveOrder * 10);
          } else {
          }
          break;
        }

        default:
          break;
      }

      // Don't process the drag logic if we created a default shape
      return;
    }

    // Handle click-to-create for images BEFORE drag completion logic
    // Check for click-to-create: action is IMAGE (or lockedAction is IMAGE), it was a click, we're drawing, and we have pendingImageData
    // Skip if clone mode with template (already handled above)
    const isImageAction = action === ACTIONS_IMAGE || lockedAction === ACTIONS_IMAGE;
    const skipCloneModeImage = isCloneMode && cloneTemplate && lockedAction === ACTIONS_IMAGE;
    const hasActiveImagePreviewToken = Boolean(
      currentImageId?.current || previewImageDataRef?.current,
    );
    const isImageClick =
      isImageAction &&
      wasClick &&
      isDrawingImage &&
      pendingImageData &&
      currentImageId.current &&
      imageStartPoint &&
      hasActiveImagePreviewToken &&
      !skipCloneModeImage;

    if (isImageClick) {
      const imageId = currentImageId.current;
      const startPos = imageStartPoint;
      const imgData = pendingImageData;

      setIsDrawingImage(false);

      // Get default dimensions - use natural image size if available, otherwise use default 100x100
      // But cap it at a reasonable size (e.g., 400x400) to prevent huge images
      const maxDefaultSize = 400;
      let defaultWidth = 100;
      let defaultHeight = 100;

      if (imgData.loadedImage && imgData.loadedImage.naturalWidth > 0 && imgData.loadedImage.naturalHeight > 0) {
        const naturalWidth = imgData.loadedImage.naturalWidth;
        const naturalHeight = imgData.loadedImage.naturalHeight;

        // CRITICAL: Use the same scaling logic as processCompressedImage
        // Scale to fit within maxDefaultSize while maintaining aspect ratio
        if (naturalWidth > maxDefaultSize || naturalHeight > maxDefaultSize) {
          const scaleFactor = Math.min(
            maxDefaultSize / naturalWidth,
            maxDefaultSize / naturalHeight
          );
          defaultWidth = naturalWidth * scaleFactor;
          defaultHeight = naturalHeight * scaleFactor;
        } else {
          // Use natural dimensions if they're within the limit
          defaultWidth = naturalWidth;
          defaultHeight = naturalHeight;
        }
      } else if (imgData.originalWidth && imgData.originalHeight) {
        // Use the same scaling logic
        if (imgData.originalWidth > maxDefaultSize || imgData.originalHeight > maxDefaultSize) {
          const scaleFactor = Math.min(
            maxDefaultSize / imgData.originalWidth,
            maxDefaultSize / imgData.originalHeight
          );
          defaultWidth = imgData.originalWidth * scaleFactor;
          defaultHeight = imgData.originalHeight * scaleFactor;
        } else {
          defaultWidth = imgData.originalWidth;
          defaultHeight = imgData.originalHeight;
        }
      }

      // CRITICAL: Ensure minimum dimensions to prevent invisible images
      if (defaultWidth < 10) defaultWidth = 10;
      if (defaultHeight < 10) defaultHeight = 10;

      // Use center anchor so rotation stays in place (same as preset images from CanvasHeightEditor)
      const imgX = startPos.x;
      const imgY = startPos.y;

      const restrictedProps = applyDrawingRestrictions("image", {
        x: imgX,
        y: imgY,
        width: defaultWidth,
        height: defaultHeight,
      });

      const centered = clampImageCenterInsideStage(
        restrictedProps.x + restrictedProps.width / 2,
        restrictedProps.y + restrictedProps.height / 2,
        restrictedProps.width,
        restrictedProps.height,
      );
      const centerX = centered.x;
      const centerY = centered.y;

      // Downscale large images before storing so canvas stays snappy (never store 30MB+ in state)
      let finalImage = imgData.loadedImage;
      if (!finalImage || !(finalImage instanceof window.Image) || finalImage.naturalWidth === 0) {
        finalImage = new window.Image();
        finalImage.crossOrigin = "anonymous";
        finalImage.src = imgData.imageSrc;
      }
      let displayImage = finalImage;
      let displaySrc = imgData.imageSrc;
      if (finalImage && finalImage.naturalWidth > 0) {
        const resolved = await getDisplayImageForState(finalImage, imgData.imageSrc);
        displayImage = resolved.image;
        displaySrc = resolved.src;
      }

      activeSetImages((prev) => {
        // Remove the temporary image with width: 0, height: 0
        const filteredPrev = prev.filter((img) => img.id !== imageId);

        const imageNumber = filteredPrev.length + 1;

        // Use downscaled display image/src so canvas never holds full-res (avoids slowness)
        const imageForState = displayImage && displayImage.naturalWidth > 0 ? displayImage : finalImage;
        const srcForState = displaySrc || imgData.imageSrc;

        // Calculate original dimensions for aspect ratio
        const originalWidth = imageForState.naturalWidth || imgData.originalWidth || restrictedProps.width;
        const originalHeight = imageForState.naturalHeight || imgData.originalHeight || restrictedProps.height;
        const originalAspectRatio = originalWidth / originalHeight;

        // Persist data URL when source is blob so redo can load the image (cap size for history)
        const needsDataUrlClick = (!imgData.imageDataUrl || (imgData.imageSrc && imgData.imageSrc.startsWith("blob:"))) && imageForState && imageForState.naturalWidth;
        const dataUrlClick = needsDataUrlClick ? imageElementToDataUrl(imageForState, DISPLAY_MAX_DIM) : null;
        const imageDataUrlForHistory = dataUrlClick || imgData.imageDataUrl || srcForState;
        const originalImageDataUrlForHistory = dataUrlClick || imgData.imageDataUrl || srcForState;

        const newImage = clampMainCanvasImageClone({
          id: imageId,
          image: imageForState,
          src: srcForState,
          x: centerX,
          y: centerY,
          width: restrictedProps.width,
          height: restrictedProps.height,
          maintainAspectRatio: true,
          compress: imgData.compress !== false, // Default to true unless explicitly false
          imageNumber,
          name: `Picture ${imageNumber}`,
          isNew: false,
          opacity: 1,
          useTopLeftAnchor: false,
          originalImage: imgData.originalImage || finalImage,
          originalImageDataUrl: originalImageDataUrlForHistory,
          imageDataUrl: imageDataUrlForHistory,
          originalBlob: imgData.originalBlob,
          originalWidth: originalWidth,
          originalHeight: originalHeight,
          originalAspectRatio: originalAspectRatio,
          isOnTopLayer: false,
          createdOnCanvas: true, // Mark as user-created element
        });

        const updatedImages = [...filteredPrev, newImage];

        // Set up pending selection for the newly created image
        // CRITICAL: Use the correct array reference based on which canvas we're creating the image in
        if (pendingSelection) {
          // Use a small timeout to ensure state is updated before useEffect processes the selection
          setTimeout(() => {
            // Don't pass elementArray - let useEffect search through all arrays
            pendingSelection.current = {
              elementId: imageId,
              elementType: "Image",
              elementArray: null,
            };
          }, 50); // Increased delay to ensure state update

          // Also try to set selection directly as a fallback
          // Use a small timeout to ensure state has been updated
          setTimeout(() => {
            const newlyCreatedImage = updatedImages.find(img => img.id === imageId);

            if (newlyCreatedImage) {
              setSelectedObject({
                ...newlyCreatedImage,
                type: "Image",
              });
              setSelectedObjects([{
                ...newlyCreatedImage,
                type: "Image",
              }]);
            }
          }, 100);
        }

        // Save to history
        // CRITICAL: Use updatedImages from closure so we save correct dimensions (300x198 etc).
        // Strip image/originalImage/compressedImage so JSON.stringify keeps width/height and redo gets same size.
        const isOffCanvasAddImageClick = activeSetImages === setOffCanvasImages;
        const saveOrder = ++historySaveCounter;
        const imagesToSave = updatedImages;
        setTimeout(() => {
          const stripImageRefs = (img) => {
            const { image, originalImage, compressedImage, ...rest } = img;
            const w = Number(img.width ?? rest.width) || 100;
            const h = Number(img.height ?? rest.height) || 100;
            const ow = Number(img.originalWidth ?? rest.originalWidth) || w;
            const oh = Number(img.originalHeight ?? rest.originalHeight) || h;
            return { ...rest, width: w, height: h, originalWidth: ow, originalHeight: oh };
          };
          const toSave = isOffCanvasAddImageClick
            ? imagesToSave.map((img) => ({ ...stripImageRefs(img), isOnTopLayer: false }))
            : imagesToSave.map(stripImageRefs);
          saveToHistory(
            isCloneMode ? "add_image_clone" : "add_image",
            isOffCanvasAddImageClick ? null : toSave,
            null, null, null, null, null, null, null, null, null, null, null, null, null,
            null,
            isOffCanvasAddImageClick ? { currentOffCanvasImagesOverride: toSave } : null
          );
        }, saveOrder * 10);

        return updatedImages;
      });

      setImageStartPoint(null);
      currentImageId.current = null;
      setPendingImageData(null);

      // Reset action to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_IMAGE) {
        setAction(ACTIONS_SELECT);
      }

      return; // Exit early, image created via click
    }

    // Handle click-to-create fallback for images that don't match isImageClick conditions
    // This catches clicks that have drawing state but might be missing some conditions
    if (
      wasClick &&
      isDrawingImage &&
      pendingImageData &&
      !isCloneMode &&
      currentImageId?.current &&
      hasActiveImagePreviewToken
    ) {

      setIsDrawingImage(false);
      setImageStartPoint(null);

      // Remove any preview image that was created
      if (currentImageId.current) {
        activeSetImages((prev) => prev.filter((img) => img.id !== currentImageId.current));
        currentImageId.current = null;
      }

      // Create image with natural dimensions
      const imgData = pendingImageData;
      const imageId = generateMarkerId();

      // Get natural dimensions with size limits
      const maxDefaultSize = 400;
      let defaultWidth = 100;
      let defaultHeight = 100;

      if (imgData.loadedImage && imgData.loadedImage.naturalWidth > 0 && imgData.loadedImage.naturalHeight > 0) {
        const naturalWidth = imgData.loadedImage.naturalWidth;
        const naturalHeight = imgData.loadedImage.naturalHeight;

        if (naturalWidth > maxDefaultSize || naturalHeight > maxDefaultSize) {
          const scaleFactor = Math.min(
            maxDefaultSize / naturalWidth,
            maxDefaultSize / naturalHeight
          );
          defaultWidth = naturalWidth * scaleFactor;
          defaultHeight = naturalHeight * scaleFactor;
        } else {
          defaultWidth = naturalWidth;
          defaultHeight = naturalHeight;
        }
      }

      // Get click position
      const stage = stageRef.current;
      const currentPos = stage?.getPointerPosition();
      if (!currentPos) {
        setPendingImageData(null);
        if (lockedAction !== ACTIONS_IMAGE) {
          setAction(ACTIONS_SELECT);
        }
        return;
      }

      // Apply drawing restrictions
      const restrictedProps = applyDrawingRestrictions("image", {
        x: currentPos.x,
        y: currentPos.y,
        width: defaultWidth,
        height: defaultHeight,
      });
      const fittedClickSize = fitImageSizeToOffCanvasStrip(
        restrictedProps.width,
        restrictedProps.height,
      );

      // Downscale large images before storing so canvas stays snappy
      const { image: displayImageFallback, src: displaySrcFallback } = await getDisplayImageForState(
        imgData.loadedImage,
        imgData.imageSrc
      );
      const imageForStateFallback = displayImageFallback && displayImageFallback.naturalWidth > 0 ? displayImageFallback : imgData.loadedImage;
      const srcForStateFallback = displaySrcFallback || imgData.imageSrc;

      // Create the image
      activeSetImages((prev) => {
        const imageNumber = prev.length + 1;
        const needsDataUrlFallback = (!imgData.imageDataUrl || (imgData.imageSrc && imgData.imageSrc.startsWith("blob:"))) && imageForStateFallback && imageForStateFallback.naturalWidth;
        const dataUrlFallback = needsDataUrlFallback ? imageElementToDataUrl(imageForStateFallback, DISPLAY_MAX_DIM) : null;
        const imageDataUrlFallback = dataUrlFallback || imgData.imageDataUrl || srcForStateFallback;
        const originalImageDataUrlFallback = dataUrlFallback || imgData.imageDataUrl || srcForStateFallback;

        const centeredClick = clampImageCenterInsideStage(
          restrictedProps.x + fittedClickSize.width / 2,
          restrictedProps.y + fittedClickSize.height / 2,
          fittedClickSize.width,
          fittedClickSize.height,
        );
        const centerXClick = centeredClick.x;
        const centerYClick = centeredClick.y;
        const updatedImages = [
          ...prev,
          clampMainCanvasImageClone({
            id: imageId,
            image: imageForStateFallback || null,
            src: srcForStateFallback,
            x: centerXClick,
            y: centerYClick,
            width: fittedClickSize.width,
            height: fittedClickSize.height,
            maintainAspectRatio: true,
            compress: imgData.compress !== false,
            imageNumber,
            name: `Picture ${imageNumber}`,
            isNew: false,
            opacity: 1,
            useTopLeftAnchor: false,
            isOnTopLayer: false,
            originalImage: imgData.originalImage || imgData.loadedImage,
            originalImageDataUrl: originalImageDataUrlFallback,
            imageDataUrl: imageDataUrlFallback,
            originalBlob: imgData.originalBlob,
            originalWidth: defaultWidth,
            originalHeight: defaultHeight,
            originalAspectRatio: defaultWidth / defaultHeight,
          }),
        ];

        // Set up pending selection
        if (pendingSelection) {
          // Don't pass elementArray - let useEffect search through all arrays
          pendingSelection.current = {
            elementId: imageId,
            elementType: "Image",
            elementArray: null,
          };
        }

        return updatedImages;
      });

      // Clear pending data and reset action
      setPendingImageData(null);
      if (lockedAction !== ACTIONS_IMAGE) {
        setAction(ACTIONS_SELECT);
      }

      return;
    }

    // Handle image placement (regular image upload, not clone mode)
    // CRITICAL: Only handle drag-to-create, not click-to-create (which is handled above)
    if (
      isDrawingImage &&
      pendingImageData &&
      !wasClick &&
      currentImageId?.current &&
      hasActiveImagePreviewToken
    ) {
      setIsDrawingImage(false);
      const startPoint = imageStartPoint;
      setImageStartPoint(null);

      if (!startPoint) return;

      const stage = stageRef.current;
      const currentPos = stage?.getPointerPosition();
      if (!currentPos) {
        setPendingImageData(null);
        if (lockedAction !== ACTIONS_IMAGE) {
          setAction(ACTIONS_SELECT);
        }
        return;
      }

      // CRITICAL FIX: Read the preview image's exact position and dimensions from ref
      // The ref is updated synchronously in onPointerMove, ensuring perfect alignment
      const previewImageId = currentImageId.current;
      let finalX, finalY, finalWidth, finalHeight;

      // CRITICAL: Read from ref first (most reliable - stored synchronously during onPointerMove)
      // The ref is updated on EVERY onPointerMove call, so it always has the latest data
      let previewImageData = previewImageDataRef?.current || null;

      // If not in ref, try to read from state as fallback
      if (!previewImageData) {
        // Use a closure variable to capture preview image data from state
        let capturedData = null;
        activeSetImages((prev) => {
          const previewImage = prev.find(
            (img) => img.isNew && img.id === previewImageId
          );

          if (previewImage) {
            // Capture the preview image's exact position and dimensions
            capturedData = {
              x: previewImage.x,
              y: previewImage.y,
              width: previewImage.width,
              height: previewImage.height,
            };
          }

          // Remove preview image
          return prev.filter((img) => img.id !== previewImageId);
        });
        previewImageData = capturedData;
      } else {
        // Remove preview image from state
        activeSetImages((prev) => prev.filter((img) => img.id !== previewImageId));
      }

      // Clear the ref
      if (previewImageDataRef) {
        previewImageDataRef.current = null;
      }
      currentImageId.current = null;

      // Use the preview image's exact data if available, otherwise fallback to calculation
      if (previewImageData) {
        // If preview has 0 dimensions (user never dragged), don't create the image
        if (previewImageData.width === 0 || previewImageData.height === 0) {
          setPendingImageData(null);
          if (lockedAction !== ACTIONS_IMAGE) {
            setAction(ACTIONS_SELECT);
          }
          return;
        }
        finalX = previewImageData.x;
        finalY = previewImageData.y;
        finalWidth = previewImageData.width;
        finalHeight = previewImageData.height;
      } else {
        // Fallback: calculate position if preview image not found
        const stageWidth = stage.width();
        const stageHeight = stage.height();
        const activeStrokeWidth = Number(cloneTemplate?.strokeWidth ?? 2);
        const strokeHalf = Math.max(0, activeStrokeWidth / 2);
        const clampedX = Math.max(
          strokeHalf,
          Math.min(currentPos.x, stageWidth - strokeHalf)
        );
        const clampedY = Math.max(
          strokeHalf,
          Math.min(currentPos.y, stageHeight - strokeHalf)
        );

        const startX = startPoint.x;
        const startY = startPoint.y;

        const distance = Math.hypot(clampedX - startX, clampedY - startY);
        const wasClick = distance < POINTER_CLICK_MOVE_THRESHOLD_PX;

        if (wasClick) {
          // If user clicked without dragging, don't create the image
          // Clear pending image data and reset action
          setPendingImageData(null);
          if (lockedAction !== ACTIONS_IMAGE) {
            setAction(ACTIONS_SELECT);
          }
          return;
        } else {
          const rawWidth = clampedX - startX;
          const rawHeight = clampedY - startY;
          const absWidth = Math.abs(rawWidth);
          const absHeight = Math.abs(rawHeight);
          const aspectRatio =
            pendingImageData.naturalWidth / pendingImageData.naturalHeight;

          if (absWidth < 1 && absHeight < 1) {
            finalWidth = 10;
            finalHeight = 10 / aspectRatio;
          } else if (absWidth < 1) {
            finalHeight = Math.max(10, absHeight);
            finalWidth = finalHeight * aspectRatio;
          } else if (absHeight < 1) {
            finalWidth = Math.max(10, absWidth);
            finalHeight = finalWidth / aspectRatio;
          } else {
            const lastConstraint = imageConstraintRef?.current || null;
            const widthRatio = absWidth / absHeight;
            const aspectRatioThreshold = aspectRatio;

            let useWidthConstraint = false;

            if (lastConstraint === "width") {
              useWidthConstraint = widthRatio >= aspectRatioThreshold * 0.7;
            } else if (lastConstraint === "height") {
              useWidthConstraint = widthRatio >= aspectRatioThreshold * 1.3;
            } else {
              useWidthConstraint = widthRatio >= aspectRatioThreshold;
            }

            if (useWidthConstraint) {
              finalWidth = absWidth;
              finalHeight = finalWidth / aspectRatio;
            } else {
              finalHeight = absHeight;
              finalWidth = finalHeight * aspectRatio;
            }

            if (finalWidth < 10) {
              finalWidth = 10;
              finalHeight = 10 / aspectRatio;
            }
            if (finalHeight < 10) {
              finalHeight = 10;
              finalWidth = 10 * aspectRatio;
            }
          }

          finalX = clampedX < startX ? clampedX : startX;
          finalY = clampedY < startY ? clampedY : startY;
          // Convert top-left to center so rotation stays in place (same as preset images)
          finalX = finalX + finalWidth / 2;
          finalY = finalY + finalHeight / 2;
        }
      }
      // When previewImageData was used, finalX/finalY are already center (set from ref)

      // Don't create image if dimensions are 0 (user never dragged)
      const fittedFinalSize = fitImageSizeToOffCanvasStrip(
        finalWidth,
        finalHeight,
      );
      finalWidth = fittedFinalSize.width;
      finalHeight = fittedFinalSize.height;
      if (finalWidth === 0 || finalHeight === 0) {
        setPendingImageData(null);
        if (lockedAction !== ACTIONS_IMAGE) {
          setAction(ACTIONS_SELECT);
        }
        return;
      }

      // Add the final image directly to the correct canvas using activeSetImages
      // This replaces the addImage call which was hardcoded to main canvas

      // Downscale large images before storing so canvas stays snappy (never store 30MB+ in state)
      const { image: displayImageDrag, src: displaySrcDrag } = await getDisplayImageForState(
        pendingImageData.loadedImage,
        pendingImageData.imageSrc
      );
      const imageForStateDrag = displayImageDrag && displayImageDrag.naturalWidth > 0 ? displayImageDrag : pendingImageData.loadedImage;
      const srcForStateDrag = displaySrcDrag || pendingImageData.imageSrc;

      // Create the final image object directly
      const finalImageId = generateMarkerId?.() || `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      activeSetImages((prev) => {
        const imageNumber = prev.length + 1;
        const updatedImages = [
          ...prev,
          clampMainCanvasImageClone({
            id: finalImageId,
            src: srcForStateDrag,
            image: imageForStateDrag || null,
            x: finalX,
            y: finalY,
            width: finalWidth,
            height: finalHeight,
            maintainAspectRatio: true,
            compress: true, // Default to true for new images
            imageNumber,
            name: `Image ${imageNumber}`,
            isNew: false, // Not a preview anymore
            opacity: 1, // Fully visible
            useTopLeftAnchor: false, // Center anchor so rotation stays in place (same as preset images)
            isOnTopLayer: false, // New images should start on base layer
            createdOnCanvas: true, // Mark as user-created element
          })
        ];

        // Set up pending selection for the newly created image
        if (pendingSelection) {
          // Use a small timeout to ensure state is updated before useEffect processes the selection
          setTimeout(() => {
            // Don't pass elementArray - let useEffect search through all arrays
            pendingSelection.current = {
              elementId: finalImageId,
              elementType: "Image",
              elementArray: null,
            };
          }, 50); // Increased delay to ensure state update
        }

        return updatedImages;
      });

      // Clear pending image data and reset action
      setPendingImageData(null);
      if (lockedAction !== ACTIONS_IMAGE) {
        setAction(ACTIONS_SELECT);
      }

      return;
    }

    // Handle click-to-create for rectangles BEFORE drag completion logic
    // Check for click-to-create: action is RECTANGLE (or lockedAction is RECTANGLE), it was a click, we're drawing, and we have the necessary data
    // Skip if clone mode with template (already handled above)
    // This should work for both locked and unlocked tools
    const isRectangleAction = action === ACTIONS_RECTANGLE || lockedAction === ACTIONS_RECTANGLE;
    const skipCloneMode = isCloneMode && cloneTemplate; // Skip if clone mode handler already handled it
    const isRectangleClick =
      isRectangleAction &&
      wasClick &&
      rectDrawCommitted &&
      currentRectId.current &&
      rectStartCommitted &&
      !skipCloneMode;

    if (isRectangleClick) {
      const rectangleId = currentRectId.current;
      const rectStartPos = rectStartCommitted;

      setIsDrawingRect(false);
      const { width: defaultWidth, height: defaultHeight } =
        presetRectangleWidthHeight(
          rectangleDefaultPreset,
          50,
          50,
          stageRef?.current?.width?.() ?? getMainCanvasWidthPx(),
        );
      const rectangleStrokeWidth = Number(rectangleDefaultPreset?.strokeWidth ?? 2);
      const centeredRectX = rectStartPos.x - defaultWidth / 2;
      const centeredRectY = rectStartPos.y - defaultHeight / 2;
      const boundedRectPos = clampRectTopLeftInsideStage(
        centeredRectX,
        centeredRectY,
        defaultWidth,
        defaultHeight,
        rectangleStrokeWidth
      );

      const restrictedProps = applyDrawingRestrictions("rectangle", {
        x: boundedRectPos.x,
        y: boundedRectPos.y,
        width: defaultWidth,
        height: defaultHeight,
      });

      let clickToCreateRect = null;
      activeSetRectangles((prev) => {
        // Remove the temporary rectangle with width: 0, height: 0
        const filteredPrev = prev.filter((rect) => rect.id !== rectangleId);
        // Generate unique name by checking existing names
        const existingNames = new Set(filteredPrev.map((r) => r.name));
        const baseName = cloneTemplate?.name || "Rectangle 1";
        const newName = generateUniqueNameForClone(baseName, existingNames);

        const baseProps = cloneTemplate
          ? (() => {
            const { id: templateId, name: templateName, ...cleanCloneTemplate } = cloneTemplate;
            return {
              ...cleanCloneTemplate,
              x: restrictedProps.x,
              y: restrictedProps.y,
              width: restrictedProps.width,
              height: restrictedProps.height,
            };
          })()
          : {
            fill: "#FFFFFF",
            stroke: "#FF0000",
            fillColor: "#FFFFFF",
            strokeColor: "#FF0000",
            maintainAspectRatio: false,
            strokeWidth: 2,
            ...pickStyleFields(rectangleDefaultPreset, [
              "fill",
              "fillColor",
              "fillOpacity",
              "stroke",
              "strokeColor",
              "strokeOpacity",
              "strokeWidth",
              "fontSize",
              "textColor",
              "fontStyle",
              "textDecoration",
              "align",
              "verticalAlign",
              "text",
              "maintainAspectRatio",
            ]),
          };

        const newRect = clampMainCanvasRectClone({
          id: rectangleId,
          name: newName,
          ...restrictedProps,
          _preserveSizeOnClamp: true,
          ...baseProps,
          fillOpacity:
            cloneTemplate?.fillOpacity ?? baseProps.fillOpacity ?? 1,
          strokeOpacity:
            cloneTemplate?.strokeOpacity ?? baseProps.strokeOpacity ?? 1,
          opacity: cloneTemplate?.opacity ?? baseProps.opacity ?? 1,
          isNew: false,
          isHighlighted: false,
        });
        console.log("[RECT-CLICK-FINALIZE]", {
          id: rectangleId,
          defaultWidth,
          defaultHeight,
          restrictedWidth: restrictedProps?.width,
          restrictedHeight: restrictedProps?.height,
          finalWidth: newRect?.width,
          finalHeight: newRect?.height,
        });

        // CRITICAL: Verify the rectangle has proper dimensions before saving

        const updatedRectangles = [...filteredPrev, newRect];

        // CRITICAL: Verify the rectangle in updatedRectangles has correct dimensions
        const savedRect = updatedRectangles.find((r) => r.id === rectangleId);
        if (savedRect && (savedRect.width !== newRect.width || savedRect.height !== newRect.height)) {
          console.error("❌ [RECTANGLE-CLICK] Rectangle dimensions mismatch!", {
            rectangleId: rectangleId,
            newRectWidth: newRect.width,
            newRectHeight: newRect.height,
            savedRectWidth: savedRect.width,
            savedRectHeight: savedRect.height,
          });
        }

        // Set up pending selection for the newly created rectangle
        if (pendingSelection) {
          // Don't pass elementArray - let useEffect search through all arrays
          pendingSelection.current = {
            elementId: rectangleId,
            elementType: "Rect",
            elementArray: null,
          };
        }

        // CRITICAL: Save to history - handle off-canvas vs main canvas
        // CRITICAL: Capture updatedRectangles in a const for the setTimeout closure to ensure correct dimensions
        const capturedUpdatedRectangles = updatedRectangles;
        const saveOrder = ++historySaveCounter;
        setTimeout(() => {
          if (isOffCanvas) {
            // For off-canvas rectangles, ensure main canvas is explicitly empty
            const finalMainCanvasRects = [];
            const rectangleToSave = capturedUpdatedRectangles.find((r) => r.id === rectangleId);
            saveToHistory(
              isCloneMode ? "add_rectangle_clone" : "add_rectangle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              finalMainCanvasRects, // currentRectanglesOverride - explicitly empty for off-canvas rectangles
              capturedUpdatedRectangles // currentOffCanvasRectanglesOverride - contains the new off-canvas rectangle with proper dimensions
            );
          } else {
            saveToHistory(
              isCloneMode ? "add_rectangle_clone" : "add_rectangle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              capturedUpdatedRectangles, // currentRectanglesOverride - pass main canvas rectangles here
              null // currentOffCanvasRectanglesOverride - null for main canvas
            );
          }
        }, saveOrder * 10);

        clickToCreateRect = newRect;
        return updatedRectangles;
      });

      setNewRectStart(null);
      currentRectId.current = null;

      if (clickToCreateRect) {
        const sel = { ...clickToCreateRect, type: "Rect" };
        if (typeof setSelectedObject === "function") setSelectedObject(sel);
        if (typeof setSelectedObjects === "function") setSelectedObjects([sel]);
      }

      // Switch to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_RECTANGLE) {
        setAction(ACTIONS_SELECT);
      }
      consumeDefaultStylePreset("rectangleDefaultStylePreset");

      return; // Exit early, rectangle created
    }

    if (rectDrawCommitted) {
      const rectStartSnapshot = rectStartCommitted;
      setIsDrawingRect(false);
      setNewRectStart(null);
      const rectId = currentRectId.current;

      const coerceMicroDragRect = (rect) => {
        if (!rectId || rect.id !== rectId || !rectStartSnapshot) return rect;
        const rw = Number(rect.width) || 0;
        const rh = Number(rect.height) || 0;
        if (Math.max(rw, rh) > RECT_MICRO_DRAG_COERCE_MAX_SIDE_PX) return rect;

        let defaultWidth;
        let defaultHeight;
        if (isCloneMode && cloneTemplate) {
          const tw = Number(cloneTemplate.width);
          const th = Number(cloneTemplate.height);
          defaultWidth = Math.max(RECT_CLICK_MIN_W, Number.isFinite(tw) && tw > 0 ? tw : 50);
          defaultHeight = Math.max(RECT_CLICK_MIN_H, Number.isFinite(th) && th > 0 ? th : 50);
        } else {
          const wh = presetRectangleWidthHeight(
            rectangleDefaultPreset,
            50,
            50,
            stageRef?.current?.width?.() ?? getMainCanvasWidthPx(),
          );
          defaultWidth = wh.width;
          defaultHeight = wh.height;
        }
        const rectangleStrokeWidth = Number(
          (isCloneMode && cloneTemplate?.strokeWidth) ??
            rectangleDefaultPreset?.strokeWidth ??
            2,
        );
        const centeredRectX = rectStartSnapshot.x - defaultWidth / 2;
        const centeredRectY = rectStartSnapshot.y - defaultHeight / 2;
        const boundedRectPos = clampRectTopLeftInsideStage(
          centeredRectX,
          centeredRectY,
          defaultWidth,
          defaultHeight,
          rectangleStrokeWidth,
        );
        const restrictedProps = applyDrawingRestrictions("rectangle", {
          x: boundedRectPos.x,
          y: boundedRectPos.y,
          width: defaultWidth,
          height: defaultHeight,
        });
        return { ...rect, ...restrictedProps };
      };

      let updatedRectangles;
      activeSetRectangles((prev) => {
        const afterCoerce = prev.map(coerceMicroDragRect);

        const filteredRects = afterCoerce
          .filter((rect) => rect.width > 2 && rect.height > 2)
          .map((rect) =>
            rect.isNew
              ? clampMainCanvasRectClone({
                ...rect,
                isNew: false,
                _preserveSizeOnClamp: true,
                fill: rect.fill || rect.fillColor || fillColor,
                fillColor: rect.fillColor || rect.fill || fillColor,
                fillOpacity:
                  rect.fillOpacity !== undefined ? rect.fillOpacity : 1,
                strokeOpacity:
                  rect.strokeOpacity !== undefined ? rect.strokeOpacity : 1,
                opacity: rect.opacity !== undefined ? rect.opacity : 1,
              })
              : rect
          );
        if (rectId) {
          const finalized = filteredRects.find((r) => r.id === rectId);
          if (finalized) {
            console.log("[RECT-DRAGEND-FINALIZE]", {
              id: rectId,
              finalWidth: finalized.width,
              finalHeight: finalized.height,
            });
          }
        }

        updatedRectangles = filteredRects;

        if (rectId) {
          pendingSelection.current = {
            elementId: rectId,
            elementType: "Rect",
            elementArray: null,
          };
        }

        return updatedRectangles;
      });

      if (
        isCloneMode &&
        rectId &&
        updatedRectangles &&
        typeof setSelectedObject === "function"
      ) {
        const r = updatedRectangles.find((x) => x.id === rectId);
        if (r) {
          const sel = { ...r, type: "Rect" };
          const applyDragCloneSelection = () => {
            setSelectedObject(sel);
            if (typeof setSelectedObjects === "function") {
              setSelectedObjects([sel]);
            }
          };
          applyDragCloneSelection();
          queueMicrotask(applyDragCloneSelection);
        }
      }

      // Now that drawing is complete, switch to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_RECTANGLE) {
        setAction(ACTIONS_SELECT);
      }

      // Use Promise-based queue to ensure each saveToHistory executes after the previous one
      // This is critical for drawing actions where multiple elements are created quickly
      const rafStartTime = Date.now();
      const rectangleId = rectId;
      const saveOrder = ++historySaveCounter;

      // Wait for previous saves to complete, then save this one
      // Use a longer delay to ensure each save completes before the next one starts
      lastHistorySavePromise = lastHistorySavePromise.then(() => {
        return new Promise((resolve) => {
          setTimeout(() => {
            const delayTime = Date.now();
            // DEBUG: Log rectangle creation for drawing
            // Pass the updated rectangles array directly to saveToHistory
            // This ensures each element creation creates a separate history entry
            saveToHistory(
              isCloneMode ? "add_rectangle_clone" : "add_rectangle",
              null,
              null,
              null,
              null,
              null,
              null,
              null,
              updatedRectangles
            );

            // Wait longer to ensure the save completes (flushSync + setHistory update)
            setTimeout(resolve, 150);
          }, saveOrder * 50); // Increased delay to ensure sequential execution
        });
      });
      consumeDefaultStylePreset("rectangleDefaultStylePreset");
      currentRectId.current = null;
    } else if (action === ACTIONS_CIRCLE) {
      const circleId = currentCircleId.current;

      // Check if this was a click (not a drag) - if so, create a default-sized circle
      if (wasClick && circleId && newCircleStart) {
        setIsDrawingCircle(false);
        const upStage = stageRef?.current;
        const upH = upStage?.height?.() ?? getCanvasStageHeightPx();
        const upMaxR = getMainCanvasMaxRadiusPx(undefined, upH);
        const { defaultRadius, extras: circleEllipseExtras } =
          circleClickDimensionsFromPreset(circleDefaultPreset, 25, upMaxR);
        const circleStrokeWidth = Number(circleDefaultPreset?.strokeWidth ?? 2);
        const boundedCircleCenter = clampCircleCenterInsideStage(
          newCircleStart.x,
          newCircleStart.y,
          defaultRadius,
          circleStrokeWidth
        );

        const restrictedProps = applyDrawingRestrictions("circle", {
          x: boundedCircleCenter.x,
          y: boundedCircleCenter.y,
          radius: defaultRadius,
        });

        let finalizedClickCircle = null;
        activeSetCircles((prev) => {
          // Remove the temporary circle with radius 0
          const filteredPrev = prev.filter((circle) => circle.id !== circleId);
          // Generate unique name by checking existing names
          const existingNames = new Set(filteredPrev.map((c) => c.name));
          const baseName = cloneTemplate?.name || "Circle 1";
          const circleNumber = filteredPrev.length + 1; // Keep for logging, but use unique name generator
          const newName = generateUniqueNameForClone(baseName, existingNames);

          const baseProps = {
            fill: "#FFFFFF",
            stroke: "#FF0000",
            fillColor: "#FFFFFF",
            maintainAspectRatio: true,
            strokeColor: "#FF0000",
            strokeWidth: 2,
            ...pickStyleFields(circleDefaultPreset, [
              "fill",
              "fillColor",
              "fillOpacity",
              "stroke",
              "strokeColor",
              "strokeOpacity",
              "strokeWidth",
              "fontSize",
              "textColor",
              "fontStyle",
              "textDecoration",
              "align",
              "verticalAlign",
              "text",
              "maintainAspectRatio",
            ]),
          };

          const newCircle = clampMainCanvasCircleClone({
            id: circleId,
            name: newName,
            ...restrictedProps,
            ...baseProps,
            ...circleEllipseExtras,
            fillOpacity: baseProps.fillOpacity ?? 1,
            strokeOpacity: baseProps.strokeOpacity ?? 1,
            opacity: baseProps.opacity ?? 1,
            isNew: false,
            isHighlighted: false,
          });

          const updatedCircles = [...filteredPrev, newCircle];
          finalizedClickCircle = newCircle;

          // Set up pending selection for the newly created circle
          if (pendingSelection) {
            // Don't pass elementArray - let useEffect search through all arrays
            pendingSelection.current = {
              elementId: circleId,
              elementType: "Circle",
              elementArray: null,
            };
          }

          // CRITICAL: Save to history - handle off-canvas vs main canvas (matching rectangle pattern)
          // CRITICAL: Capture updatedCircles in a const for the setTimeout closure to ensure correct dimensions
          const capturedUpdatedCircles = updatedCircles;
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            if (isOffCanvas) {
              // Pass null for currentCirclesOverride so main canvas circles are preserved (same fix as off-canvas rectangle).
              saveToHistory(
                isCloneMode ? "add_circle_clone" : "add_circle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null, // currentCirclesOverride - null so main canvas circles are preserved
                capturedUpdatedCircles // currentOffCanvasCirclesOverride - contains the new off-canvas circle with proper dimensions
              );
            } else {
              saveToHistory(
                isCloneMode ? "add_circle_clone" : "add_circle",
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                null,
                capturedUpdatedCircles, // currentCirclesOverride - pass main canvas circles here
                null // currentOffCanvasCirclesOverride - null for main canvas
              );
            }
          }, saveOrder * 10);

          return updatedCircles;
        });

        setNewCircleStart(null);
        currentCircleId.current = null;

        // Switch to SELECT mode (unless action is locked)
        if (lockedAction !== ACTIONS_CIRCLE) {
          setAction(ACTIONS_SELECT);
        }
        consumeDefaultStylePreset("circleDefaultStylePreset");

        if (finalizedClickCircle) {
          bumpShapeDrawSelectionGuard();
          const sel = { ...finalizedClickCircle, type: "Circle" };
          const applyCircleClickSelection = () => {
            flushSync(() => {
              if (typeof setSelectedObjects === "function") {
                setSelectedObjects([sel]);
              }
              if (typeof setSelectedObject === "function") {
                setSelectedObject(sel);
              }
            });
          };
          applyCircleClickSelection();
          queueMicrotask(applyCircleClickSelection);
        }

        return; // Exit early, circle created
      }

      // Drag case - finalize the drawn circle
      // CRITICAL: Match click-to-create path - replace the drawn circle with a fresh object (explicit x, y, radius)
      // so state shape is identical and editor/updater work the same for both draw and click.
      setIsDrawingCircle(false);
      setNewCircleStart(null);

      let updatedCircles;
      let finalizedDragCircle = null;
      activeSetCircles((prev) => {
        const withRadius = prev.filter((circle) => circle.radius > 5);
        const drawnCircle = withRadius.find((c) => c.id === circleId && c.isNew);
        if (!drawnCircle) {
          updatedCircles = withRadius.map((c) =>
            c.isNew
              ? clampMainCanvasCircleClone({
                ...c,
                isNew: false,
                fill: c.fill || c.fillColor || fillColor,
                fillColor: c.fillColor || c.fill || fillColor,
                fillOpacity:
                  c.fillOpacity !== undefined ? c.fillOpacity : 1,
                strokeOpacity:
                  c.strokeOpacity !== undefined ? c.strokeOpacity : 1,
                opacity: c.opacity !== undefined ? c.opacity : 1,
              })
              : c
          );
          if (circleId && pendingSelection) {
            pendingSelection.current = { elementId: circleId, elementType: "Circle", elementArray: null };
          }
          finalizedDragCircle = updatedCircles.find((c) => c.id === circleId) || null;
          return updatedCircles;
        }

        // Build finalized circle with explicit numeric x, y, radius (same shape as click-to-create)

        const existingNames = new Set(withRadius.filter((c) => c.id !== circleId).map((c) => c.name));
        const newName = generateUniqueNameForClone("Circle 1", existingNames);
        const x = Number(drawnCircle.x);
        const y = Number(drawnCircle.y);
        const radius = Number(drawnCircle.radius);

        const finalizedCircle = clampMainCanvasCircleClone({
          ...pickStyleFields(drawnCircle, [
            "fontSize",
            "textColor",
            "fontStyle",
            "textDecoration",
            "align",
            "verticalAlign",
            "text",
            "rotation",
            "radiusX",
            "radiusY",
            "isDashed",
          ]),
          id: circleId,
          name: newName,
          x,
          y,
          radius,
          fill: drawnCircle.fill || drawnCircle.fillColor || fillColor,
          fillColor: drawnCircle.fillColor || drawnCircle.fill || fillColor,
          stroke:
            drawnCircle.stroke || drawnCircle.strokeColor || "#FF0000",
          strokeColor:
            drawnCircle.strokeColor || drawnCircle.stroke || "#FF0000",
          strokeWidth: drawnCircle.strokeWidth ?? 2,
          maintainAspectRatio: drawnCircle.maintainAspectRatio ?? true,
          fillOpacity:
            drawnCircle.fillOpacity !== undefined ? drawnCircle.fillOpacity : 1,
          strokeOpacity:
            drawnCircle.strokeOpacity !== undefined
              ? drawnCircle.strokeOpacity
              : 1,
          opacity: drawnCircle.opacity !== undefined ? drawnCircle.opacity : 1,
          isNew: false,
          isHighlighted: false,
        });

        updatedCircles = withRadius.filter((c) => c.id !== circleId).concat([finalizedCircle]);
        finalizedDragCircle = finalizedCircle;

        if (pendingSelection) {
          pendingSelection.current = { elementId: circleId, elementType: "Circle", elementArray: null };
        }

        return updatedCircles;
      });

      // CRITICAL FIX: Save to history after finalizing circle from drag
      // This was missing, causing undo button to be disabled after creating circles by dragging
      // Rectangles have this save call, circles need it too
      // CRITICAL: Handle off-canvas vs main canvas (matching rectangle pattern)
      const capturedUpdatedCircles = updatedCircles;
      const saveOrder = ++historySaveCounter;
      setTimeout(() => {
        if (isOffCanvas) {
          // Pass null for currentCirclesOverride so main canvas circles are preserved (same fix as off-canvas rectangle).
          saveToHistory(
            isCloneMode ? "add_circle_clone" : "add_circle",
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null, // currentCirclesOverride - null so main canvas circles are preserved
            capturedUpdatedCircles // currentOffCanvasCirclesOverride - contains the finalized off-canvas circle
          );
        } else {
          saveToHistory(
            isCloneMode ? "add_circle_clone" : "add_circle",
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            capturedUpdatedCircles, // currentCirclesOverride - pass main canvas circles here
            null // currentOffCanvasCirclesOverride - null for main canvas
          );
        }
      }, saveOrder * 10);

      // Clear the current circle ID after saving
      consumeDefaultStylePreset("circleDefaultStylePreset");
      currentCircleId.current = null;

      // Now that drawing is complete, switch to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_CIRCLE) {
        setAction(ACTIONS_SELECT);
      }

      if (finalizedDragCircle) {
        bumpShapeDrawSelectionGuard();
        const sel = { ...finalizedDragCircle, type: "Circle" };
        const applyCircleDragSelection = () => {
          flushSync(() => {
            if (typeof setSelectedObjects === "function") {
              setSelectedObjects([sel]);
            }
            if (typeof setSelectedObject === "function") {
              setSelectedObject(sel);
            }
          });
        };
        applyCircleDragSelection();
        queueMicrotask(applyCircleDragSelection);
      }
    } else if (isDrawingText) {
      setIsDrawingText(false);
      const textId = currentTextId.current;
      const textStartPoint = newTextStart;
      setNewTextStart(null);

      // Check if this was a click (no significant movement) vs a drag
      const stage = stageRef.current;
      let currentPos = stage?.getPointerPosition?.();
      const evtClientX =
        e?.evt?.clientX ??
        e?.evt?.touches?.[0]?.clientX ??
        e?.evt?.changedTouches?.[0]?.clientX ??
        null;
      const evtClientY =
        e?.evt?.clientY ??
        e?.evt?.touches?.[0]?.clientY ??
        e?.evt?.changedTouches?.[0]?.clientY ??
        null;
      if (
        (!currentPos ||
          !Number.isFinite(Number(currentPos.x)) ||
          !Number.isFinite(Number(currentPos.y))) &&
        stage &&
        evtClientX !== null &&
        evtClientY !== null
      ) {
        const stageRect = stage.container?.()?.getBoundingClientRect?.();
        if (stageRect) {
          const stageWidth = Number(stage.width?.()) || 0;
          const stageHeight = Number(stage.height?.()) || 0;
          const domW = Math.max(1, Number(stageRect.width) || 1);
          const domH = Math.max(1, Number(stageRect.height) || 1);
          const sx = stageWidth > 0 ? stageWidth / domW : 1;
          const sy = stageHeight > 0 ? stageHeight / domH : 1;
          currentPos = {
            x: (evtClientX - stageRect.left) * sx,
            y: (evtClientY - stageRect.top) * sy,
          };
        }
      }
      const initialPos = initialPointerPosition?.current;

      let wasClick = false;
      if (initialPos && textStartPoint && currentPos) {
        const distance = Math.hypot(
          currentPos.x - initialPos.x,
          currentPos.y - initialPos.y,
        );
        wasClick = distance < POINTER_CLICK_MOVE_THRESHOLD_PX;
      }

      // Finalize text - ensure minimum width and mark as completed
      let updatedTexts;
      let finalizedText;
      flushSync(() => activeSetTexts((prev) => {
        // First, find the text we're finalizing by ID (check both isNew and regular)
        const textToFinalize = prev.find((text) => text.id === textId);

        if (!textToFinalize) {
          console.error("Text element not found for finalization", {
            textId,
            prevTexts: prev,
            prevTextIds: prev.map((t) => ({
              id: t.id,
              isNew: t.isNew,
              width: t.width,
            })),
          });
          finalizedText = null;
          updatedTexts = prev;
          return prev;
        }

        // Compute draw-end width from pointer delta first.
        // Relying only on text state can be stale when pointer-up lands before the last
        // onPointerMove state batch, which causes a false fallback to default width (175).
        const pointerDrawnWidth =
          textStartPoint && currentPos
            ? Math.max(0, Math.abs(Number(currentPos.x) - Number(textStartPoint.x)))
            : NaN;
        const rawDrawnWidth = Number.isFinite(pointerDrawnWidth)
          ? pointerDrawnWidth
          : Math.max(0, Number(textToFinalize.width) || 0);
        const wasClickByWidthOnly = rawDrawnWidth < POINTER_CLICK_MOVE_THRESHOLD_PX;
        const treatAsClick = wasClick || wasClickByWidthOnly;
        let finalizedWidth = treatAsClick
          ? 175
          : Math.max(10, rawDrawnWidth || 10);

        // CRITICAL: Don't re-center the text on click - we've already positioned it correctly
        // The textStartPoint already accounts for the 10px padding offset, so the content
        // will appear at the click position. Re-centering would move it incorrectly.
        // Just use the position we already set (which accounts for padding)
        let finalizedX =
          textStartPoint && currentPos
            ? Math.min(Number(textStartPoint.x) || 0, Number(currentPos.x) || 0)
            : textToFinalize.x;
        let finalizedY = textToFinalize.y;
        const getMinTextHeightPx = (t) => {
          const fs = t?.fontSize;
          if (fs === 11) return 30;
          if (fs === 14 || fs === "14") return 35;
          return 43;
        };
        const isTextBoxEffectivelyEmpty = (t) => {
          const hasAny =
            Boolean(t?.content) ||
            Boolean(t?.part) ||
            Boolean(t?.task) ||
            Boolean(t?.note) ||
            (t?.partsByType && Object.keys(t.partsByType).length > 0);
          return !hasAny;
        };
        const minTextHeight = getMinTextHeightPx(textToFinalize);
        const rawHeight = Number(textToFinalize.height);
        // When empty, do not force the default 100px height at draw-end. That default
        // causes large, unnecessary upward clamps near the bottom edge.
        const textHeight = isTextBoxEffectivelyEmpty(textToFinalize)
          ? minTextHeight
          : Math.max(
              minTextHeight,
              Number.isFinite(rawHeight) ? rawHeight : minTextHeight,
            );

        if (isOffCanvas && stageWidth > 0 && stageHeight > 0) {
          const { mainZoneStart, mainZoneEnd } =
            getOffCanvasMainColumnBounds(stageWidth);
          const maxStripWidth = Math.max(
            10,
            mainZoneStart - stageClampBufferPx * 2,
          );
          finalizedWidth = Math.max(10, Math.min(finalizedWidth, maxStripWidth));
          // Force draw-end placement to left off-canvas strip only.
          // Generic seam-avoid helper can legally choose either strip, which allowed
          // right-strip placement when stage geometry changed (e.g. reduced height).
          const minLeftStripX = stageClampBufferPx;
          const maxLeftStripX = Math.max(
            minLeftStripX,
            mainZoneStart - finalizedWidth - stageClampBufferPx,
          );
          finalizedX = Math.max(
            minLeftStripX,
            Math.min(maxLeftStripX, Number(finalizedX) || 0),
          );
          finalizedY = Math.max(
            stageClampBufferPx,
            Math.min(
              Number(finalizedY) || 0,
              Math.max(stageClampBufferPx, stageHeight - textHeight - stageClampBufferPx),
            ),
          );
          console.log("[DRAW-END-CLAMP][OFFCANVAS][TEXT]", {
            id: textToFinalize?.id,
            input: {
              x: textToFinalize?.x,
              y: textToFinalize?.y,
              width: textToFinalize?.width,
              height: textToFinalize?.height,
            },
            bounds: { stageWidth, stageHeight, mainZoneStart, mainZoneEnd, maxStripWidth },
            output: { x: finalizedX, y: finalizedY, width: finalizedWidth, height: textHeight },
          });
        } else if (stageWidth > 0 && stageHeight > 0) {
          // Main-stage draw-end: shrink oversized text width before position clamping.
          // Without this, very large widths only shift x/y and still overflow stage bounds.
          const maxMainWidth = Math.max(
            1,
            stageWidth - stageClampBufferPx * 2,
          );
          finalizedWidth = Math.max(1, Math.min(finalizedWidth, maxMainWidth));
          const mainClamped = clampRectTopLeftInsideStage(
            Number(finalizedX) || 0,
            Number(finalizedY) || 0,
            finalizedWidth,
            textHeight,
            stageClampBufferPx,
          );
          finalizedX = mainClamped.x;
          finalizedY = mainClamped.y;
        }


        // DEBUG: Log finalization

        // Update all texts: finalize the one we're working on, filter out others with width < 10
        const filteredTexts = prev
          .filter((text) => {
            // Keep the text we're finalizing even if width < 10 (we'll fix it)
            if (text.id === textId) return true;
            // Filter out other texts with width < 10
            return (text.width || 0) >= 10;
          })
          .map((text) =>
            text.id === textId
              ? {
                ...text,
                isNew: false,
                width: finalizedWidth,
                height: textHeight,
                x: finalizedX,
                y: finalizedY,
              }
              : text
          );

        finalizedText = filteredTexts.find((text) => text.id === textId);

        if (!finalizedText) {
          console.error("Failed to find finalized text after processing", {
            textId,
            filteredTexts,
            textToFinalize,
          });
          finalizedText = textToFinalize
            ? { ...textToFinalize, isNew: false, width: finalizedWidth }
            : null;
        }

        // Clear selectedContent from all texts (matching old addText behavior)
        updatedTexts = filteredTexts.map((t) => ({
          ...t,
          selectedContent: null,
        }));

        return updatedTexts;
      }));

      // Clear all previous selections first
      setSelectedObject(null);
      setSelectedObjects([]);

      // Clear editing states
      setEditTextBoxContent(false);

      // Clear transformer
      if (transformerRef?.current) {
        transformerRef.current.nodes([]);
      }

      // Set text-specific selection states (matching old addText behavior)
      // Need to set both selectedText (for TextBoxWithButtons) and selectedObject (for transformer)
      if (!finalizedText || !finalizedText.id) {
        console.error("Failed to finalize text element", {
          finalizedText,
          textId,
          currentTextId: currentTextId.current,
          textsArray: texts?.map((t) => ({
            id: t.id,
            isNew: t.isNew,
            width: t.width,
          })),
        });
        // Try to get the text from the appropriate array as fallback
        // Check both main texts and off-canvas texts arrays
        const fallbackText = texts?.find((t) => t.id === textId) ||
          offCanvasTexts?.find((t) => t.id === textId);
        if (fallbackText) {
          finalizedText = {
            ...fallbackText,
            isNew: false,
            width: Math.max(10, fallbackText.width || 10),
          };
          // Update the appropriate texts array with the finalized text
          activeSetTexts((prev) =>
            prev.map((t) => (t.id === textId ? finalizedText : t))
          );
        } else {
          console.error("Text element not found in texts or offCanvasTexts arrays", {
            textId,
            texts,
          });
          return;
        }
      }

      const resolveFinalizedTextForSelection = () => {
        if (finalizedText?.id) return finalizedText;
        const targetId = textId || currentTextId.current;
        if (!targetId) return null;
        return (
          updatedTexts?.find((t) => t.id === targetId) ||
          texts?.find((t) => t.id === targetId) ||
          offCanvasTexts?.find((t) => t.id === targetId) ||
          null
        );
      };

      // Apply clamped selection immediately so UI reflects draw-end clamp
      // without requiring deselect/reselect. Keep a microtask retry for
      // transformer attach timing after DOM updates.
      const textForSelection = resolveFinalizedTextForSelection();
      if (textForSelection?.id) {
        const selectedTextShape = {
          ...textForSelection,
          type: "Text",
          selectedContent: null,
        };
        const applyTextDrawSelection = () => {
          flushSync(() => {
            if (typeof setSelectedText === "function") {
              setSelectedText(textForSelection);
            }
            if (typeof setSelectedTextId === "function") {
              setSelectedTextId(textForSelection.id);
            }
            if (typeof setShowTextBox === "function") {
              setShowTextBox(true);
            }
            if (typeof setSelectedObjects === "function") {
              setSelectedObjects([selectedTextShape]);
            }
            if (typeof setSelectedObject === "function") {
              setSelectedObject(selectedTextShape);
            }
          });
        };
        applyTextDrawSelection();
        queueMicrotask(applyTextDrawSelection);
        requestAnimationFrame(() => {
          requestAnimationFrame(applyTextDrawSelection);
        });
        if (pendingSelection) {
          pendingSelection.current = {
            elementId: textForSelection.id,
            elementType: "Text",
            elementArray: null,
          };
        }
      }

      // Now that drawing is complete, switch to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_TEXT) {
        setAction(ACTIONS_SELECT);
      }

      // Save to history after finalizing text; for off-canvas use currentOffCanvasTextsOverride
      const saveOrder = ++historySaveCounter;
      setTimeout(() => {
        const textForHistory = resolveFinalizedTextForSelection();
        const textIdForHistory = textForHistory?.id || textId || finalizedText?.id;
        if (isOffCanvas) {
          saveToHistory(
            isCloneMode ? "add_text_clone" : "add_text",
            null, null, null, null, null,
            textForHistory,
            textIdForHistory,
            null, null, null, null, null, null, null,
            updatedTexts
          );
        } else {
          saveToHistory(
            isCloneMode ? "add_text_clone" : "add_text",
            null,
            null,
            null,
            null,
            updatedTexts,
            textForHistory,
            textIdForHistory
          );
        }
      }, saveOrder * 50);
    } else if (action === ACTIONS_ARROW && isDrawingArrow) {
      // Finalize arrow creation
      setIsDrawingArrow(false);
      setArrowStartPoint(null);
      const arrowId = currentArrowId.current;
      currentShapeId.current = null;

      // Remove arrows with minimal length and mark others as completed
      let updatedArrows;
      setArrows((prev) => {
        const filteredArrows = prev
          .filter((arrow) => {
            const points = arrow.points || [];
            if (points.length < 4) return false;

            // Calculate total path length for complex arrows
            let totalLength = 0;
            for (let i = 0; i < points.length - 2; i += 2) {
              const x1 = points[i];
              const y1 = points[i + 1];
              const x2 = points[i + 2];
              const y2 = points[i + 3];
              const segmentLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
              totalLength += segmentLength;
            }
            return totalLength > 10; // Minimum arrow length
          })
          .map((arrow) => {
            if (arrow.isNew) {
              // Calculate final width and height from points
              const [x1, y1, x2, y2] = arrow.points;
              const width = Math.abs(x2 - x1);
              const height = Math.abs(y2 - y1);

              // Ensure draw-end clamp behaves like lines:
              // - keep the arrow/points inside stage bounds
              // - avoid the off-canvas "main overlay" column
              // - honor updated stage height
              const clamped = clampMainCanvasPolylineClone({
                ...arrow,
                isNew: false,
                width,
                height,
                strokeColor: arrow.strokeColor || fillColor || "black",
                stroke: arrow.stroke || fillColor || "black",
              });

              return {
                ...arrow,
                ...clamped,
                isNew: false,
                width,
                height,
                // Ensure fill color is properly assigned if it's not already set
                fillColor: arrow.fillColor || fillColor,
              };
            }
            return arrow;
          });

        updatedArrows = filteredArrows;

        // Set up pending selection for the newly created arrow
        if (arrowId) {
          // Don't pass elementArray - let useEffect search through all arrays
          pendingSelection.current = {
            elementId: arrowId,
            elementType: "Arrow",
            elementArray: null,
          };
        }

        return updatedArrows;
      });

      // Now that drawing is complete, switch to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_ARROW) {
        setAction(ACTIONS_SELECT);
      }

      // Use sequential delay to ensure each saveToHistory executes after the previous one
      // This is critical for drawing actions where multiple elements are created quickly
      const saveOrder = ++historySaveCounter;
      setTimeout(() => {
        saveToHistory(isCloneMode ? "add_arrow_clone" : "add_arrow", null, updatedArrows);
      }, saveOrder * 10);
      consumeDefaultStylePreset("unifiedLineArrowDefaultStylePreset");
    } else if (action === ACTIONS_DOUBLE_SIDED_ARROW && isDrawingDoubleArrow) {
      // Finalize double-sided arrow creation
      setIsDrawingDoubleArrow(false);
      setDoubleArrowStartPoint(null);
      const arrowId = currentDoubleArrowId.current;
      currentShapeId.current = null;

      // Remove double-sided arrows with minimal length and mark others as completed
      let updatedDoubleSidedArrows;
      setDoubleSidedArrows((prev) => {
        const filteredArrows = prev
          .filter((arrow) => {
            const points = arrow.points || [];
            if (points.length < 4) return false;

            // Calculate total path length for complex arrows
            let totalLength = 0;
            for (let i = 0; i < points.length - 2; i += 2) {
              const x1 = points[i];
              const y1 = points[i + 1];
              const x2 = points[i + 2];
              const y2 = points[i + 3];
              const segmentLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
              totalLength += segmentLength;
            }
            return totalLength > 10; // Minimum arrow length
          })
          .map((arrow) => {
            if (arrow.isNew) {
              // Calculate final width and height from points
              const [x1, y1, x2, y2] = arrow.points;
              const width = Math.abs(x2 - x1);
              const height = Math.abs(y2 - y1);

              // Ensure draw-end clamp behaves like lines (points inside + off-canvas avoidance)
              const clamped = clampMainCanvasPolylineClone({
                ...arrow,
                isNew: false,
                width,
                height,
                strokeColor: arrow.strokeColor || fillColor || "black",
                stroke: arrow.stroke || fillColor || "black",
              });

              return {
                ...arrow,
                ...clamped,
                isNew: false,
                width,
                height,
                // Ensure fill color is properly assigned if it's not already set
                fillColor: arrow.fillColor || fillColor,
              };
            }
            return arrow;
          });

        updatedDoubleSidedArrows = filteredArrows;

        // Set up pending selection for the newly created double-sided arrow
        if (arrowId) {
          // Don't pass elementArray - let useEffect search through all arrays
          pendingSelection.current = {
            elementId: arrowId,
            elementType: "DoubleSidedArrow",
            elementArray: null,
          };
        }

        return updatedDoubleSidedArrows;
      });

      // Now that drawing is complete, switch to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_DOUBLE_SIDED_ARROW) {
        setAction(ACTIONS_SELECT);
      }

      // Use sequential delay to ensure each saveToHistory executes after the previous one
      // This is critical for drawing actions where multiple elements are created quickly
      const saveOrder = ++historySaveCounter;
      setTimeout(() => {
        saveToHistory(
          isCloneMode ? "add_double_sided_arrow_clone" : "add_double_sided_arrow",
          null,
          null,
          updatedDoubleSidedArrows
        );
      }, saveOrder * 10);
      consumeDefaultStylePreset("unifiedLineArrowDefaultStylePreset");
    } else if (action === ACTIONS_LINE && isDrawingLine) {
      const lineId = currentLineId.current;
      const stage = stageRef.current;

      // Check if this was a click (not a drag) - if so, create a default-sized line
      if (wasClick && lineId && lineStartPoint) {
        setIsDrawingLine(false);

        const unifiedDefaults = pickStyleFields(unifiedLineArrowDefaultPreset, [
          "stroke",
          "strokeColor",
          "fill",
          "fillColor",
          "strokeWidth",
          "isDashed",
          "dash",
          "opacity",
          "hasStartArrow",
          "hasEndArrow",
          "points",
        ]);
        const resolvedHasStartArrow =
          unifiedDefaults.hasStartArrow !== undefined
            ? Boolean(unifiedDefaults.hasStartArrow)
            : false;
        const resolvedHasEndArrow =
          unifiedDefaults.hasEndArrow !== undefined
            ? Boolean(unifiedDefaults.hasEndArrow)
            : true;
        if (debugUnifiedPresetCreate) {
          console.log("[UnifiedPresetDebug][pointerUp][line-finalize-click]", {
            unifiedLineArrowDefaultPreset,
            unifiedDefaults,
            resolvedHasStartArrow,
            resolvedHasEndArrow,
            isCloneMode,
            hasCloneTemplate: Boolean(cloneTemplate),
          });
        }
        const presetPointsRaw =
          Array.isArray(unifiedDefaults.points) &&
          unifiedDefaults.points.length >= 4
            ? unifiedDefaults.points
            : null;
        let presetPoints = normalizeRelativePolylinePoints(presetPointsRaw, 0, 0);
        // Non-clone click-to-create should also reject stage-absolute preset points.
        // If preset points still look absolute-like after normalization, rebase to
        // first point so the new element anchors at click position.
        if (Array.isArray(presetPoints) && presetPoints.length >= 4) {
          const numericPts = presetPoints.map((n) => Number(n) || 0);
          const maxAbs = Math.max(...numericPts.map((n) => Math.abs(n)));
          const minAbs = Math.min(...numericPts.map((n) => Math.abs(n)));
          const hasNearOriginCoord = numericPts.some((n) => Math.abs(n) <= 8);
          const looksAbsoluteLike =
            maxAbs > 220 && minAbs > 16 && !hasNearOriginCoord;
          if (looksAbsoluteLike) {
            const sx = numericPts[0] || 0;
            const sy = numericPts[1] || 0;
            presetPoints = numericPts.map((coord, idx) =>
              idx % 2 === 0 ? coord - sx : coord - sy,
            );
            console.log("[line][click-create][preset-rebased-to-relative]", {
              originalFirstPoint: { x: sx, y: sy },
              rebasedPoints: presetPoints,
            });
          }
        }

        // Single click - create line with default length (fallback 50)
        const defaultLength = presetPoints
          ? Math.sqrt(
            Math.pow(presetPoints[presetPoints.length - 2] - presetPoints[0], 2) +
              Math.pow(presetPoints[presetPoints.length - 1] - presetPoints[1], 2)
          ) || 50
          : 50;
        const lineStrokeWidth = Number(unifiedDefaults.strokeWidth ?? 2);
        const strokeHalf = Math.max(0, lineStrokeWidth) / 2;
        const boundedDefaultLength = Math.max(
          1,
          Math.min(defaultLength, Math.max(1, stageWidth - strokeHalf * 2))
        );
        const clickCreatePoints = presetPoints || [0, 0, boundedDefaultLength, 0];
        let minRelX = Infinity;
        let minRelY = Infinity;
        let maxRelX = -Infinity;
        let maxRelY = -Infinity;
        for (let i = 0; i < clickCreatePoints.length; i += 2) {
          const px = Number(clickCreatePoints[i]) || 0;
          const py = Number(clickCreatePoints[i + 1]) || 0;
          minRelX = Math.min(minRelX, px);
          maxRelX = Math.max(maxRelX, px);
          minRelY = Math.min(minRelY, py);
          maxRelY = Math.max(maxRelY, py);
        }
        const clickShapeWidth = Math.max(0, maxRelX - minRelX);
        const clickShapeHeight = Math.max(0, maxRelY - minRelY);

        // Clamp by the rendered click-shape footprint (works for vertical presets too).
        const boundedLineAbsTopLeft = clampRectTopLeftInsideStage(
          lineStartPoint.x + minRelX,
          lineStartPoint.y + minRelY,
          clickShapeWidth,
          clickShapeHeight,
          lineStrokeWidth
        );
        const boundedLinePos = {
          x: boundedLineAbsTopLeft.x - minRelX,
          y: boundedLineAbsTopLeft.y - minRelY,
        };

        const restrictedProps = applyDrawingRestrictions("line", {
          x: boundedLinePos.x,
          y: boundedLinePos.y,
          width: clickShapeWidth,
          height: clickShapeHeight,
        });

        activeSetLines((prev) => {
          // Remove the temporary line with zero length
          const filteredPrev = prev.filter((line) => line.id !== lineId);
          // Generate unique name by checking existing names
          // CRITICAL: Use the actual lines array (passed to function) to ensure we see all existing names
          const currentLinesArray = lines || [];
          const existingNames = new Set([
            ...currentLinesArray.map((l) => l.name),
            ...filteredPrev.map((l) => l.name), // Also include filteredPrev in case of pending updates
          ]);
          const baseName = cloneTemplate?.name || "Stroke 1";
          const newName = generateUniqueNameForClone(baseName, existingNames);

          const baseProps = {
            fillColor,
            fill: fillColor,
            strokeColor: fillColor || "black",
            strokeWidth: 2,
            stroke: fillColor || "black",
            ...unifiedDefaults,
            hasStartArrow: resolvedHasStartArrow,
            hasEndArrow: resolvedHasEndArrow,
            // Keep legacy properties for backward compatibility
            pointerAtBeginning: resolvedHasStartArrow,
            pointerAtEnd: resolvedHasEndArrow,
          };

          const newLine = clampMainCanvasPolylineClone({
            id: lineId,
            name: newName,
            ...restrictedProps,
            ...baseProps,
            // Create horizontal line by default: points are relative to line.x and line.y
            points: clickCreatePoints,
            width: clickShapeWidth,
            height: clickShapeHeight,
            isNew: false,
            startDot: {
              radius: 4,
              fill: fillColor || "black",
              stroke: fillColor || "black",
              strokeWidth: 1,
            },
            endDot: {
              radius: 4,
              fill: fillColor || "black",
              stroke: fillColor || "black",
              strokeWidth: 1,
            },
          });

          const updatedLines = [...filteredPrev, newLine];

          // Set up pending selection for the newly created line
          if (pendingSelection) {
            // Don't pass elementArray - let useEffect search through all arrays
            pendingSelection.current = {
              elementId: lineId,
              elementType: "Line",
              elementArray: null,
            };
          }

          // Save to history - for off-canvas use currentOffCanvasLinesOverride
          const saveOrder = ++historySaveCounter;
          setTimeout(() => {
            if (isOffCanvas) {
              saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, null, null, null, null, null, null, null, null, null, null, updatedLines);
            } else {
              saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, updatedLines);
            }
          }, saveOrder * 10);

          return updatedLines;
        });

        setLineStartPoint(null);
        currentLineId.current = null;
        currentShapeId.current = null;

        // Switch to SELECT mode (unless action is locked)
        if (lockedAction !== ACTIONS_LINE) {
          setAction(ACTIONS_SELECT);
        }
        consumeDefaultStylePreset("unifiedLineArrowDefaultStylePreset");

        return; // Exit early, line created
      }

      // Drag case - finalize the drawn line
      setIsDrawingLine(false);
      const { x, y } = stage.getPointerPosition();

      // Remove lines with minimal length and mark others as completed
      let updatedLines;
      activeSetLines((prev) => {
        const filteredLines = prev
          .filter((line) => {
            const [x1, y1, x2, y2] = line.points;
            // Since points are now relative, calculate length from relative coordinates
            const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
            return length > 10; // Minimum line length
          })
          .map((line) => {
            if (line.isNew) {
              // Calculate final width and height from points
              const [x1, y1, x2, y2] = line.points;
              const width = Math.abs(x2 - x1);
              const height = Math.abs(y2 - y1);

              return clampMainCanvasPolylineClone({
                ...line,
                isNew: false,
                width: width,
                height: height,
                // Ensure stroke color is properly assigned
                strokeColor: line.strokeColor || fillColor || "black",
                stroke: line.stroke || fillColor || "black",
                pointerAtBeginning: line.pointerAtBeginning || false,
                pointerAtEnd: line.pointerAtEnd || false,
                startDot: {
                  radius: 4,
                  fill: line.strokeColor || fillColor || "black",
                  stroke: line.strokeColor || fillColor || "black",
                  strokeWidth: 1,
                },
                endDot: {
                  radius: 4,
                  fill: line.strokeColor || fillColor || "black",
                  stroke: line.strokeColor || fillColor || "black",
                  strokeWidth: 1,
                },
              });
            }
            return line;
          });

        updatedLines = filteredLines;

        // Set up pending selection for the newly created line
        if (lineId && pendingSelection) {
          // Don't pass elementArray - let useEffect search through all arrays
          pendingSelection.current = {
            elementId: lineId,
            elementType: "Line",
            elementArray: null,
          };
        }

        return updatedLines;
      });

      setLineStartPoint(null);
      currentShapeId.current = null;
      // Switch to SELECT mode (unless action is locked)
      if (lockedAction !== ACTIONS_LINE) {
        setAction(ACTIONS_SELECT);
      }

      // Use sequential delay to ensure each saveToHistory executes after the previous one
      // For off-canvas pass null for main lines and use currentOffCanvasLinesOverride
      const saveOrder = ++historySaveCounter;
      setTimeout(() => {
        if (isOffCanvas) {
          saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, null, null, null, null, null, null, null, null, null, null, updatedLines);
        } else {
          saveToHistory(isCloneMode ? "add_line_clone" : "add_line", null, null, null, updatedLines);
        }
      }, saveOrder * 10);
      consumeDefaultStylePreset("unifiedLineArrowDefaultStylePreset");
    } else if (action === ACTIONS_SCRIBBLE) {
      // Finalize scribble creation
      currentShapeId.current = null;
      setAction(ACTIONS_SELECT);

      saveToHistory("add_scribble");
    }
  };
};
